/**
 * i18n Translation Messages
 * All translatable strings for the admin interface
 * Migrated from Alpine.js i18n-messages.js
 */

export type Language = "zh" | "en";

export interface PushNotificationsMessages {
  // Page
  pageTitle: string;

  // Stats
  stats: {
    totalTokens: string;
    activeTokens: string;
    pushSent: string;
    successRate: string;
    platformBreakdown: string;
    ios: string;
    android: string;
  };

  // Send Notification Form
  sendForm: {
    title: string;
    description: string;
    notificationTitle: string;
    notificationTitlePlaceholder: string;
    body: string;
    bodyPlaceholder: string;
    target: string;
    targetAll: string;
    targetAllWarning: string;
    targetTest: string;
    targetTestDesc: string;
    targetSpecificUsers: string;
    targetSpecificUsersDesc: string;
    userIds: string;
    userIdsPlaceholder: string;
    userIdsHint: string;
    sound: string;
    soundPlaceholder: string;
    soundHint: string;
    soundDefault?: string;
    soundNone?: string;
    badgeCount: string;
    badgeCountPlaceholder: string;
    badgeCountHint: string;
    badgeCountHintFull?: string;
    customData: string;
    customDataPlaceholder: string;
    customDataHint: string;
    dataTemplates?: string;
    dataTemplateLabels?: Record<string, string>;
    clearButton: string;
    sendButton: string;
    sending: string;
    successMessage: string;
    sent: string;
    failed: string;
    errors: string;
  };

  // Device Tokens
  tokens: {
    tabTitle: string;
    title: string;
    allPlatforms: string;
    refresh: string;
    loading: string;
    noTokens: string;
    user: string;
    registered: string;
    active: string;
    inactive: string;
    page: string;
    of: string;
    total: string;
    previous: string;
    next: string;
  };

  // Notification Logs
  logs: {
    tabTitle: string;
    title: string;
    allStatuses: string;
    statusSent: string;
    statusFailed: string;
    refresh: string;
    loading: string;
    noLogs: string;
    error: string;
    page: string;
    of: string;
    total: string;
    previous: string;
    next: string;
    // Detail view fields
    user?: string;
    userId?: string;
    displayName?: string;
    platform?: string;
    deviceInfo?: string;
    sentAt?: string;
    pushToken?: string;
    fullTitle?: string;
    fullBody?: string;
    dataPayload?: string;
    actionNavigation?: string;
    actionType?: string;
    actionFortune?: string;
    // Grouped view fields
    recipients?: string;
    batches?: string;
    totalRecipients?: string;
    recipientList?: string;
  };

  // Time formatting
  time: {
    justNow: string;
    minutesAgo: string;
    hoursAgo: string;
    daysAgo: string;
  };

  // Validation errors
  validation: {
    titleRequired: string;
    bodyRequired: string;
    userIdsRequired: string;
    invalidJson: string;
  };

  // User Selector
  userSelector?: {
    searchPlaceholder: string;
    selectedCount: string;
    selectAll: string;
    deselectAll: string;
    clearAll: string;
    noUsers: string;
    devices: string;
    showingCount: string;
  };

  // Content Assist (AI-powered content generation)
  contentAssist?: {
    sectionTitle: string;
    inputModeLabel: string;
    agentMode: string;
    llmMode: string;
    manualMode: string;
    selectAgent: string;
    loading: string;
    noAgents: string;
    descriptionLabel: string;
    descriptionPlaceholder: string;
    generateButton: string;
    generating: string;
    selectAgentFirst: string;
    generateFailed: string;
    // LLM mode specific
    selectLLMModel: string;
    noModels: string;
    llmModeDescription: string;
    temperature: string;
    precise: string;
    balanced: string;
    creative: string;
    selectModelFirst: string;
  };
}

export interface MonitoringMessages {
  // Page
  pageTitle: string;
  pageDescription: string;
  lastUpdated: string;

  // Tabs
  tabs: {
    services: string;
    cache: string;
    storage: string;
    queue: string;
  };

  // Queue
  queue?: {
    title: string;
    status: string;
    running: string;
    stopped: string;
    queueSize: string;
    processed: string;
    success: string;
    failed: string;
    merged: string;
    savedCalls: string;
    mergeRate: string;
    costSaving: string;
    deadLetter: string;
    deadLetterDesc: string;
    retryAll: string;
    clearAll: string;
    retry: string;
    noTasks: string;
    taskId: string;
    userId: string;
    memoryType: string;
    error: string;
    failedAt: string;
    retryCount: string;
    confirmClear: string;
    confirmRetryAll: string;
    clearSuccess: string;
    retrySuccess: string;
    resetStats: string;
    confirmReset: string;
    resetSuccess: string;
  };

  // System Task Queue
  systemTaskQueue?: {
    title: string;
    queueSize: string;
    deadLetterSize: string;
    maxRetries: string;
    retryDelay: string;
    jobTimeout: string;
    deadLetter: string;
    deadLetterDesc: string;
    taskType: string;
    scheduledBy: string;
    scheduleId: string;
    config: string;
    lastError: string;
    finalError: string;
    failedAt: string;
    retryCount: string;
    noTasks: string;
    confirmClear: string;
    confirmRetryAll: string;
    clearSuccess: string;
    retrySuccess: string;
  };

  // Service Health
  serviceHealth: {
    title: string;
    services: string;
    healthy: string;
    unhealthy: string;
    error: string;
    unknown: string;
    allOk: string;
    issues: string;
    overallStatus: string;
    latency: string;
    openService: string;
  };

  // Cache Performance
  cache: {
    title: string;
    hitRate: string;
    hits: string;
    misses: string;
    avgLatency: string;
    totalRequests: string;
    successfulRetrievals: string;
    fetchFailures: string;
    avgResponseTime: string;
    unavailable: string;
  };

  // Charts
  charts: {
    cacheHitRate: string;
    hitRateLabel: string;
    avgLatency: string;
    total: string;
    cacheLatency: string;
    backend: string;
    requestDistribution: string;
    noData: string;
  };

  // Cache Details
  cacheDetails: {
    title: string;
    totalRequests: string;
    cachedKeys: string;
    cacheSize: string;
    errors: string;
    redisConnection: string;
    teiBackend: string;
    connected: string;
    disconnected: string;
    healthy: string;
    unhealthy: string;
    clear: string;
    clearConfirm: string;
    clearSuccess: string;
    clearError: string;
  };

  // Refresh Control
  refresh: {
    autoRefresh: string;
    interval: string;
    manual: string;
    refreshButton: string;
    refreshing: string;
  };

  // Errors
  errors: {
    loadFailed: string;
    tryAgain: string;
  };
}

export interface ClassificationAnalyticsMessages {
  // Page
  pageTitle: string;
  pageDescription: string;
  lastUpdated: string;

  // Tabs
  tabs: {
    skillExecution: string;
    classification: string;
  };

  // Time Range
  timeRange: {
    label: string;
    last24h: string;
    last7d: string;
    last30d: string;
  };

  // Skill Execution
  skillExecution: {
    title: string;
    stats: {
      totalExecutions: string;
      activeSkills: string;
      autoMatchRate: string;
      ruleMatchRate: string;
    };
    charts: {
      matchLayer: string;
      matchSource: string;
      topSkills: string;
    };
    recentExecutions: string;
    noData: string;
    skillId: string;
    query: string;
    matchSource: string;
    matchedRule: string;
    confidence: string;
    time: string;
  };

  // Classification
  classification: {
    title: string;
    stats: {
      totalClassifications: string;
      quickResponseRate: string;
      avgQueryLength: string;
      avgLatency: string;
      // Skill metrics
      ruleMatchRate: string;
      avgRuleMatchTime: string;
      avgLlmMatchTime: string;
      avgPromptInjection: string;
    };
    charts: {
      responseType: string;
      avgConfidence: string;
      methodPerformance: string;
      count: string;
      latency: string;
      // Skill charts
      skillMatchLayer: string;
      topMatchedSkills: string;
    };
    recentClassifications: string;
    noData: string;
    responseType: string;
    method: string;
    latencyMs: string;
  };

  // Common
  export: {
    button: string;
    csv: string;
    json: string;
  };
  refresh: string;
  loading: string;
  error: string;
  quick: string;
  deep: string;
}

export interface UsersMessages {
  pageTitle: string;
  pageDescription: string;
  refresh: string;
  loading: string;
  error: string;

  stats: {
    totalUsers: string;
    admins: string;
    betaTesters: string;
    roleDistribution: string;
  };

  filters: {
    searchPlaceholder: string;
    allUsers: string;
    betaTesters: string;
    nonBeta: string;
  };

  batch: {
    selected: string;
    enableBeta: string;
    disableBeta: string;
    clearSelection: string;
  };

  table: {
    user: string;
    contact: string;
    role: string;
    betaTester: string;
    created: string;
    actions: string;
  };

  roles: {
    free: string;
    vip: string;
    premium: string;
    admin: string;
  };

  actions: {
    makeBeta: string;
    removeBeta: string;
    makeAdmin: string;
    revokeAdmin: string;
    resources: string;
    viewResources: string;
  };

  status: {
    beta: string;
    notBeta: string;
  };

  pagination: {
    showing: string;
    page: string;
  };
}

export interface SystemSettingsMessages {
  // Page
  pageTitle: string;
  pageDescription: string;

  // Tabs
  tabs: {
    mcpServers: string;
    mem0Config: string;
    firstMessage: string;
    artifactTypes: string;
    toolEnhancement: string;
    toolCombo: string;
    promptTemplates: string;
    outputSchemas: string;
    exportImport: string;
    auditLogs: string;
  };

  // Audit
  audit: {
    viewLogsFor: string;
    mcpServersLog: string;
    mem0ConfigLog: string;
    firstMessageLog: string;
    artifactTypesLog: string;
    toolEnhancementLog: string;
    toolComboLog: string;
    promptTemplatesLog: string;
    outputSchemasLog: string;
  };

  // MCP Servers Panel
  mcp?: {
    title: string;
    description: string;
    warningBanner: string;
    editJson: string;
    reloadManager: string;
    addServer: string;
    noServers: string;
    addFirstServer: string;
    aboutMcp: string;
    aboutMcpItems: string[];
    version: string;
    lastUpdatedBy: string;
    jsonEditorTitle: string;
    jsonEditorHint: string;
    cancel: string;
    saving: string;
    saveConfiguration: string;
    loading: string;
    loadFailed: string;
    saveFailed: string;
    deleteFailed: string;
    reloadSuccess: string;
    reloadFailed: string;
    jsonSaveSuccess: string;
    jsonMustHaveMcpServers: string;
  };

  // Mem0 Config Panel
  mem0?: {
    title: string;
    description: string;
    warningBanner: string;
    helpTitle: string;
    helpDescription: string;
    helpItems: string[];
    documentation: string;
    reloadFromDatabase: string;
    saving: string;
    saveSystemDefaults: string;
    userMemoryPrompt: string;
    agentMemoryPrompt: string;
    memoryUpdatePrompt: string;
    userMemoryCategories: string;
    agentMemoryCategories: string;
    resetToHardcoded: string;
    promptMinMax: string;
    promptUpdateMinMax: string;
    categoriesHint: string;
    loading: string;
    loadFailed: string;
    saveSuccess: string;
    saveFailed: string;
    resetSuccess: string;
    resetFailed: string;
    noDefaultPrompt: string;
    noDefaultCategories: string;
    version: string;
    lastUpdatedBy: string;
    validation: {
      userPromptMin: string;
      agentPromptMin: string;
      updatePromptMin: string;
      userPromptMax: string;
      agentPromptMax: string;
      updatePromptMax: string;
    };
  };

  // First Message Panel
  firstMessage?: {
    title: string;
    description: string;
    warningBanner: string;
    reloadFromDatabase: string;
    saving: string;
    saveSystemDefault: string;
    instructionTitle: string;
    instructionPlaceholder: string;
    instructionLength: string;
    instructionMinMax: string;
    howItWorksTitle: string;
    howItWorksItems: string[];
    loading: string;
    loadFailed: string;
    saveSuccess: string;
    saveFailed: string;
    version: string;
    lastUpdatedBy: string;
    validation: {
      minLength: string;
      maxLength: string;
    };
  };

  // Artifact Types Panel
  artifactTypes?: {
    title: string;
    description: string;
    addType: string;
    noTypes: string;
    addFirstType: string;
    editType: string;
    addTypeTitle: string;
    typeId: string;
    typeIdHint: string;
    icon: string;
    iconOptions: {
      package: string;
      sparkles: string;
      audio: string;
      document: string;
      image: string;
      calendar: string;
    };
    nameEnglish: string;
    nameChinese: string;
    descriptionLabel: string;
    descriptionHint: string;
    contentTypes: string;
    contentTypesHint: string;
    rotationPeriod: string;
    rotationOptions: {
      none: string;
      daily: string;
      weekly: string;
      monthly: string;
    };
    maxStored: string;
    maxStoredHint: string;
    cancel: string;
    saving: string;
    save: string;
    confirmDelete: string;
    loading: string;
    loadFailed: string;
    saveFailed: string;
    deleteFailed: string;
    version: string;
    lastUpdatedBy: string;
    // Documentation sections
    aboutTitle: string;
    aboutItems: string[];
    apiAccessTitle: string;
    workflowTitle: string;
    fieldRefTitle: string;
    adminOpsTitle: string;
  };

  // Audit Log Viewer
  auditLog?: {
    loading: string;
    loadFailed: string;
    noLogs: string;
    noLogsHint: string;
    showing: string;
    version: string;
    reason: string;
    ip: string;
  };

  // Export/Import Panel
  exportImport?: {
    exportTitle: string;
    exportDescription: string;
    exporting: string;
    exportAll: string;
    exportSuccess: string;
    exportFailed: string;
    importTitle: string;
    importDescription: string;
    selectFile: string;
    importMode: string;
    importModeOptions: {
      createOnly: string;
      overwrite: string;
      merge: string;
    };
    importModeHints: {
      createOnly: string;
      overwrite: string;
      merge: string;
    };
    skipErrors: string;
    skipErrorsHint: string;
    importPreview: string;
    foundConfigs: string;
    importResults: string;
    imported: string;
    skipped: string;
    errors: string;
    importing: string;
    importConfigurations: string;
    aboutTitle: string;
    aboutItems: string[];
    noFileSelected: string;
    invalidJson: string;
    invalidFormat: string;
    importCompleted: string;
    importFailed: string;
  };

  // Tool Enhancement Panel
  toolEnhancement?: {
    title: string;
    description: string;
    loading: string;
    loadFailed: string;
    saveSuccess: string;
    saveFailed: string;
    save: string;
    saving: string;
    reload: string;
    searchPlaceholder: string;
    noToolSelected: string;
    configured: string;
    notConfigured: string;
    displayNameCn: string;
    displayNameEn: string;
    descriptionCn: string;
    descriptionEn: string;
    icon: string;
    displayOrder: string;
    enabled: string;
    clearConfig: string;
    toolInfo: string;
    technicalName: string;
    originalDescription: string;
    allCategories: string;
    orphanConfigs: string;
    orphanConfigsDesc: string;
    cleanupOrphans: string;
    orphanWarning: string;
    category: string;
    categoryOverride: string;
    categoryOverrideHint: string;
    originalCategory: string;
    useDefaultCategory: string;
    toolKey: string;
    // Category translations
    categories: Record<string, string>;
    // AI Generation
    aiGenerate: string;
    aiGenerateDesc: string;
    aiGenerateButton: string;
    aiGenerating: string;
    aiSelectModel: string;
    aiSelectModelPlaceholder: string;
    aiExplanation: string;
    aiNoDescription: string;
    // Reload Enhancer
    reloadEnhancer: string;
    reloadEnhancerDesc: string;
    reloadEnhancerSuccess: string;
    reloadEnhancerFailed: string;
    // Tool Testing
    testTool: string;
    testToolDesc: string;
    testParameters: string;
    testParametersPlaceholder: string;
    runTest: string;
    running: string;
    testResult: string;
    testSuccess: string;
    testFailed: string;
    executionTime: string;
    errorType: string;
    closeModal: string;
    noParameters: string;
    invalidJson: string;
    requiredFields: string;
    optionalFields: string;
    paramType: string;
    paramDefault: string;
    requiresUserContext: string;
    requiresUserContextDesc: string;
    formMode: string;
    jsonMode: string;
    useDefaultValue: string;
  };

  // Prompt Templates Panel
  promptTemplates?: {
    title: string;
    description: string;
    addTemplate: string;
    noTemplates: string;
    addFirstTemplate: string;
    editTemplate: string;
    addTemplateTitle: string;
    templateKey: string;
    templateKeyHint: string;
    nameEnglish: string;
    nameChinese: string;
    descriptionEn: string;
    descriptionZh: string;
    systemPromptZh: string;
    systemPromptEn: string;
    displayOrder: string;
    category: string;
    categoryOptions: {
      image: string;
      push: string;
      workflow: string;
    };
    isDefault: string;
    resetToDefault: string;
    resetSuccess: string;
    cancel: string;
    saving: string;
    save: string;
    confirmDelete: string;
    cannotDeleteDefault: string;
    loading: string;
    loadFailed: string;
    saveFailed: string;
    deleteFailed: string;
    version: string;
    lastUpdatedBy: string;
    searchPlaceholder: string;
    allCategories: string;
    // Preview
    previewPrompt: string;
    chinese: string;
    english: string;
  };

  // Output Schemas Panel
  outputSchemas?: {
    title: string;
    description: string;
    addSchema: string;
    noSchemas: string;
    noSchemasMatch: string;
    addFirstSchema: string;
    editSchema: string;
    searchPlaceholder: string;
    scopeAll: string;
    scopeGlobal: string;
    scopeAgent: string;
    statusAll: string;
    statusEnabled: string;
    statusDisabled: string;
    schemaKey: string;
    schemaKeyHint: string;
    nameEnglish: string;
    nameChinese: string;
    descriptionEn: string;
    descriptionZh: string;
    scope: string;
    agentType: string;
    jsonSchema: string;
    exampleOutput: string;
    formatInstruction: string;
    formatInstructionHint: string;
    displayOrder: string;
    enabled: string;
    loading: string;
    loadFailed: string;
    saveFailed: string;
    deleteFailed: string;
    toggleFailed: string;
    totalSchemas: string;
    showing: string;
    aboutTitle: string;
    aboutItem1: string;
    aboutItem2: string;
    aboutItem3: string;
    aboutItem4: string;
  };
}

export interface EvalScenariosMessages {
  // Page
  pageTitle: string;
  pageDescription: string;

  // Actions
  createScenario: string;
  promoteChat: string;
  refresh: string;
  runAll: string;
  runSingle: string;
  running: string;
  edit: string;
  delete: string;
  duplicate: string;
  duplicateSuccess: string;
  expandDetails: string;
  collapseDetails: string;
  removeTag: string;

  // Filters
  filters: string;
  searchPlaceholder: string;
  allAgentTypes: string;
  allCategories: string;
  allSources: string;
  allStatus: string;
  sourceManual: string;
  sourceChatPromoted: string;
  statusActive: string;
  statusInactive: string;

  // Stats
  totalScenarios: string;
  active: string;
  manual: string;
  chatPromoted: string;
  neverRun: string;

  // Form Steps
  step1Title: string;
  step1Description: string;
  step2Title: string;
  step2Description: string;
  step3Title: string;
  step3Description: string;
  stepOf: string;
  next: string;
  previous: string;

  // Form Fields - Step 1
  scenarioName: string;
  scenarioNamePlaceholder: string;
  scenarioNameHint: string;
  description: string;
  descriptionPlaceholder: string;
  targetAgent: string;
  targetAgentHint: string;
  category: string;
  categoryPlaceholder: string;
  priority: string;
  priorityHint: string;
  tags: string;
  addTagPlaceholder: string;

  // Form Fields - Step 2
  testConversation: string;
  conversationHint: string;
  addTurn: string;
  userRole: string;
  assistantRole: string;
  userMessagePlaceholder: string;
  assistantMessagePlaceholder: string;
  expectedResponse: string;
  expectedResponsePlaceholder: string;
  expectedResponseHint: string;
  expectedResponseDetailTitle: string;
  expectedResponseDetail1: string;
  expectedResponseDetail2: string;
  expectedResponseDetail3: string;
  expectedResponseExample: string;

  // Form Fields - Step 3
  llmJudgeModel: string;
  llmJudgeModelHint: string;
  llmJudgeModelWarning: string;
  quickAddMetrics: string;
  evaluationCriteria: string;
  customMetric: string;
  metricNamePlaceholder: string;
  threshold: string;
  noMetricsSelected: string;
  minOverallScore: string;
  minOverallScoreHint: string;
  activeCheckbox: string;

  // Metrics
  metrics: {
    hallucination: string;
    hallucinationDesc: string;
    hallucinationHelp: string;
    relevancy: string;
    relevancyDesc: string;
    relevancyHelp: string;
    faithfulness: string;
    faithfulnessDesc: string;
    faithfulnessHelp: string;
    answer_relevancy: string;
    answer_relevancyDesc: string;
    answer_relevancyHelp: string;
    contextual_relevancy: string;
    contextual_relevancyDesc: string;
    contextual_relevancyHelp: string;
    bias: string;
    biasDesc: string;
    biasHelp: string;
    toxicity: string;
    toxicityDesc: string;
    toxicityHelp: string;
    astro_accuracy: string;
    astro_accuracyDesc: string;
    astro_accuracyHelp: string;
    interpretation_quality: string;
    interpretation_qualityDesc: string;
    interpretation_qualityHelp: string;
    helpfulness: string;
    helpfulnessDesc: string;
    helpfulnessHelp: string;
  };
  metricsGuide: string;
  metricsGuideContent: string;
  lowerIsBetter: string;
  higherIsBetter: string;

  // Dialogs
  createDialogTitle: string;
  editDialogTitle: string;
  createButton: string;
  saveChanges: string;

  // Promote Chat Dialog
  promoteChatTitle: string;
  promoteChatDescription: string;
  chatTopicId: string;
  chatTopicIdPlaceholder: string;
  chatTopicIdHint: string;
  promoteButton: string;

  // Run Evaluation Dialog
  runEvaluationTitle: string;
  runningEvaluation: string;
  runningHint?: string;
  runningHintResult?: string;
  evaluationStarted?: string;
  evaluationStartedHint?: string;
  checkRunsPanel?: string;
  evaluationComplete: string;
  overallScore: string;
  passed: string;
  failed: string;
  response: string;
  responseTime: string;
  metricResults: string;
  reason: string;
  scenario: string;
  agentType: string;
  judgerModel: string;
  criteriaLabel: string;
  batchSummary: string;
  total: string;
  close: string;
  runEvaluationButton: string;
  batchInfoPrefix: string;
  batchInfoSuffix: string;

  // Results
  lastResult: string;
  noResults: string;

  // Confirmations
  deleteConfirm: string;

  // Success/Error messages
  createSuccess: string;
  updateSuccess: string;
  deleteSuccess: string;
  promoteSuccess: string;
  runSuccess: string;
  error: string;

  // History Panel
  historyPanel?: {
    title: string;
    scoreTrend: string;
    recentRuns: string;
    noHistory: string;
    checkRunsPanelHint: string;
    expandDetails: string;
    collapseDetails: string;
    agentResponse: string;
    responseTime: string;
    executedAt: string;
    executedBy: string;
    metrics: string;
    resultsList: string;
    running: string;
    failed: string;
  };

  // Result Detail Dialog
  resultDetail?: {
    title: string;
    viewDetails: string;
    overallScore: string;
    metricsTitle: string;
    judgmentReason: string;
    executedAt: string;
    model: string;
    responseTime: string;
    executedBy: string;
    runningHint: string;
  };

  // Runs Panel
  runsPanel?: {
    title: string;
    runId: string;
    totalScenarios: string;
    passed: string;
    failed: string;
    skipped: string;
    averageScore: string;
    duration: string;
    viewDetails: string;
    noRuns: string;
    noResults: string;
    autoRefreshing: string;
    statusRunning: string;
    statusCompleted: string;
    statusFailed: string;
  };

  // Results Panel
  resultsPanel?: {
    title: string;
    loading: string;
    noResults: string;
    noResultsHint: string;
    averageScore: string;
    totalRuns: string;
    scenarioName: string;
    description: string;
    testInput: string;
    noInput: string;
    history: string;
    detailedMetrics: string;
    agentResponse: string;
    evaluationNotes: string;
  };

  // Common texts
  optional: string;
  noDescription: string;
  createFirstScenario: string;
  input: string;

  // Time formatting
  time?: {
    justNow: string;
    minutesAgo: string;
    hoursAgo: string;
    daysAgo: string;
  };

  // Metric Definitions
  metricDefinitions?: {
    pageTitle: string;
    pageDescription: string;
    createMetric: string;
    builtinMetrics: string;
    customMetrics: string;
    viewPrompt: string;
    editMetric: string;
    deleteMetric: string;
    metricName: string;
    displayName: string;
    metricType: string;
    defaultThreshold: string;
    thresholdDirection: string;
    higherBetter: string;
    lowerBetter: string;
    applicableAgents: string;
    allAgents: string;
    systemMetric: string;
    customGeval: string;
    criteria: string;
    criteriaPlaceholder: string;
    criteriaHint: string;
    evaluationSteps: string;
    evaluationStepsHint: string;
    addStep: string;
    removeStep: string;
    noMetrics: string;
    builtinPromptNote: string;
    promptViewer: {
      title: string;
      type: string;
      source: string;
      threshold: string;
      prompt: string;
      readOnlyNote: string;
    };
    editor: {
      createTitle: string;
      editTitle: string;
      nameHint: string;
      displayNameHint: string;
      descriptionHint: string;
      // Evaluation Steps
      evaluationStepsTitle: string;
      optional: string;
      stepsVsCriteriaTitle: string;
      stepsExplanation1: string;
      stepsExplanation2: string;
      stepsExplanation3: string;
      exampleTitle: string;
      exampleStep1: string;
      exampleStep2: string;
      exampleStep3: string;
      exampleStep4: string;
      yourSteps: string;
      noStepsYet: string;
      stepPlaceholder: string;
      addStep: string;
      removeStep: string;
      // AI Generation
      aiAssistantTitle: string;
      aiAssistantDescription: string;
      aiDescriptionPlaceholder: string;
      generating: string;
      generateMetric: string;
      minChars: string;
      chars: string;
      aiExplanation: string;
      aiDescriptionTooShort: string;
      aiGenerationFailed: string;
      selectModel: string;
      selectModelPlaceholder: string;
      selectModelFirst: string;
    };
    deleteConfirm: string;
    deleteSystemError: string;
    createSuccess: string;
    updateSuccess: string;
    deleteSuccess: string;
  };

  // Test Suites
  testSuites?: {
    // Selection
    selectForSuite: string;
    exitSelect: string;
    createSuite: string;
    selectAll: string;
    deselectAll: string;
    selected: string;

    // Dialog
    createTitle: string;
    editTitle: string;
    name: string;
    namePlaceholder: string;
    description: string;
    descriptionPlaceholder: string;
    tags: string;
    tagsPlaceholder: string;
    priority: string;
    scenarioCount: string;
    scenarios: string;
    cancel: string;
    create: string;
    save: string;

    // Run
    selectSuite: string;
    runAllActive: string;
    runSuite: string;
    runSuiteInfo: string;
  };
}

export interface NavigationMessages {
  appTitle: string;
  agentConfigs: string;
  systemSettings: string;
  promptTemplates: string;
  skills: string;
  monitoring: string;
  reports: string;
  classificationAnalytics: string;
  messageFeedback: string;
  users: string;
  pushTest: string;
  evaluation: string;
  playground: string;
  imageWorkflows: string;
  workflowSchedules: string;
  annotation: string;
  logout: string;
  expandSidebar: string;
  collapseSidebar: string;
}

export interface EvaluationMessages {
  pageTitle: string;
  pageDescription: string;
  tabs: {
    scenarios: string;
    metrics: string;
    suites: string;
  };
}

export interface HooksMessages {
  // Pipeline
  pipelineTitle: string;
  loading: string;
  errorLoading: string;
  noData: string;
  enabledCount: string;
  showHidden: string;
  hideHidden: string;

  // Stages
  stages: {
    system: string;
    user: string;
    final: string;
  };
  hooksCount: string;

  // Config Panel
  selectHook: string;
  description: string;
  noDescription: string;
  stage: string;
  category: string;
  scope: string;
  sortOrder: string;
  dragToReorder: string;
  configInAgentConfig: string;

  // Hook Definitions (i18n for display_name and description)
  hookDefinitions: {
    [key: string]: {
      displayName: string;
      description: string;
      /** 依赖的前置条件（如其他 hooks 或配置） */
      requires?: string;
      /** 额外说明或注意事项 */
      note?: string;
    };
  };
}

export interface AgentConfigMessages {
  // List Page
  listTitle: string;
  listDescription: string;
  createAgent: string;
  searchPlaceholder: string;
  showInactive: string;
  noAgentsMatch: string;
  noAgentsMatchHint: string;
  noAgentsYet: string;
  noAgentsYetHint: string;
  showingAgents: string;
  activeCount: string;
  inactive: string;
  version: string;
  updated: string;

  // Detail Page
  loadingConfig: string;
  backToList: string;
  agentNotFound: string;
  copy: string;
  unsavedChanges: string;
  saving: string;
  saveSuccess: string;
  saveFailed: string;
  active: string;
  updatedViaAdmin: string;

  // Tabs
  tabSettings: string;
  tabHooks: string;
  tabTools: string;
  tabPrompt: string;
  tabSchemas: string;
  tabSkills: string;
  tabJson: string;
  tabVersions: string;
  // New tabs
  tabBasic: string;
  tabMemory: string;
  tabKnowledge: string;
  tabAstrology: string;
  tabEmotion: string;
  tabClassifier: string;
  tabTeam: string;
  tabAdvanced: string;

  // Actions (for JsonField browseTools button)
  actions?: {
    browseTools?: string;
  };

  // Output Format Section
  outputFormat?: {
    title: string;
    createSchema: string;
    manageSchemas: string;
    noSchemasWarning: string;
    createFirstSchema: string;
    noDefault: string;
    defaultSchemaHelp: string;
    availableSchemas: string;
    apiHint: string;
  };

  // Create/Copy Agent Dialog
  createAgentDialog: {
    copyAgent: string;
    createNewAgent: string;
    agentType: string;
    agentTypePlaceholder: string;
    agentTypeHint: string;
    name: string;
    namePlaceholder: string;
    description: string;
    descriptionPlaceholder: string;
    copyingFrom: string;
    creating: string;
    agentTypeRequired: string;
    nameRequired: string;
    agentTypeInvalid: string;
  };

  // Version History
  versionHistory: {
    title: string;
    loading: string;
    current: string;
    restore: string;
    noHistory: string;
    createdBy: string;
    versionConfig: string;
    confirmRestore: string;
    restoreReason: string;
    editing: string;
    clickToShowJson: string;
    viewConfig: string;
  };

  // JSON Preview
  jsonPreview: {
    title: string;
    copy: string;
    copied: string;
    download: string;
  };

  // Tools Browser Modal
  toolsBrowser?: {
    customTools: string;
    builtinToolkits: string;
    customToolsDesc: string;
    builtinToolkitsDesc: string;
    filterByCategory: string;
    allCategories: string;
    toolsSelected: string;
    loadingTools: string;
    availableTools: string;
    selectedTools: string;
    previewConfig: string;
    cancel: string;
    apply: string;
    noBuiltinToolkits?: string;
    noCustomTools?: string;
    noToolsSelected?: string;
    toolsCount?: string;
    builtinToolsBrowser?: string;
    customToolsBrowser?: string;
    format?: string;
    noItemsConfigured?: string;
    toolkits?: string;
    enabledTools?: string;
    servers?: string;
    modules?: string;
    allToolsEnabled?: string;
    toolNotFound?: string;
    serverNotFound?: string;
    willMigrateOnSave?: string;
    toolNotFoundRemove?: string;
    invalidToolsWarning?: string;
    removeInvalidToolsFirst?: string;
  };

  // MCP Tools Browser Modal
  mcpBrowser?: {
    title?: string;
    refresh?: string;
    systemServers?: string;
    userServers?: string;
    serversSelected?: string;
    toolsSelected?: string;
    invalidItemsWarning?: string;
    removeInvalidItemsFirst?: string;
    loadingServers?: string;
    availableServers?: string;
    selectedItems?: string;
    servers?: string;
    individualTools?: string;
    noServersSelected?: string;
    noServersAvailable?: string;
    connected?: string;
    disconnected?: string;
    failedToConnect?: string;
    previewConfig?: string;
    cancel?: string;
    apply?: string;
    toolsCount?: string;
  };

  // Prompt Template Preview
  promptPreview?: {
    title: string;
    buttonLabel: string;
    copy: string;
    copied: string;
    loading: string;
    refresh: string;
    generatePreview: string;
    usingTemplate: string;
    default: string;
    enabledSections: string;
    useRealData: string;
    livePreview: string;
    userId: string;
    enterUserId: string;
    useCurrent: string;
    usingCurrentUser: string;
    sampleMessage: string;
    enterSampleMessage: string;
    realDataHint: string;
    sampleDataHint: string;
    previewFailed: string;
    template: string;
    systemChars: string;
    userChars: string;
    user: string;
    hooksExecuted: string;
    systemStage: string;
    userStage: string;
    noHooksExecuted: string;
    dataSources: string;
    dataSourceHint: string;
    systemPrompt: string;
    enhancedUserMessage: string;
    contextItems: string;
    emptyStateTitle: string;
    emptyStateDesc: string;
  };

  // Output Schemas Tab
  outputSchemas?: {
    title: string;
    add: string;
    noSchemas: string;
    noSchemasHint: string;
    default: string;
    properties: string;
    noSelection: string;
    noSelectionHint: string;
  };

  // Skills Tab
  skills?: {
    title: string;
    add: string;
    noSkills: string;
    noSkillsHint: string;
    noSelection: string;
    noSelectionHint: string;
    enabled: string;
    disabled: string;
    priority: string;
    exclusionGroup: string;
    disabledWarning: string;
  };

  // Schema Editor
  schemaEditor?: {
    aiGenerate: string;
    aiGenerateDesc: string;
    selectModel: string;
    selectModelPlaceholder: string;
    inputExample: string;
    generating: string;
    generate: string;
    aiExplanation: string;
    key: string;
    keyValidHint: string;
    keyInvalidHint: string;
    name: string;
    namePlaceholder: string;
    description: string;
    descriptionPlaceholder: string;
    jsonSchema: string;
    validJson: string;
    jsonSchemaHint: string;
    exampleOutput: string;
    exampleOutputHint: string;
    formatInstruction: string;
    formatInstructionHint: string;
    defaultSchema: string;
    setAsDefault: string;
    deleteSchema: string;
    confirmDelete: string;
    alertExampleRequired: string;
    alertModelRequired: string;
    alertGenerateFailed: string;
  };

  // Template Selector
  templateSelector?: {
    title: string;
    description: string;
    useDefault: string;
    default: string;
    recommended: string;
    systemDefault: string;
    sections: string;
    sectionsCount: string;
    updated: string;
    noTemplates: string;
    loadingTemplates: string;
    previewTemplate: string;
  };
}

export interface ImageWorkflowsMessages {
  executionPanel: {
    title: string;
    execution: string;
    progress: string;
    nodes: string;
    failed: string;
    duration: string;
    error: string;
    clearAndClose: string;
    nodeDetail: string;
  };
  executionHistory: {
    title: string;
    tab: string;
    executionLabel: string;
    nodes: string;
    saveFirst: string;
    noHistory: string;
    runFirst: string;
  };
  nodeDetail: {
    selectNode: string;
    noStepData: string;
    status: string;
    duration: string;
    order: string;
    startedAt: string;
    completedAt: string;
  };
  stepResult: {
    error: string;
    variables: string;
    noVariables: string;
    mode: string;
    generatedPrompt: string;
    showLess: string;
    showMore: string;
    noPrompt: string;
    model: string;
    output: string;
    imageLoadError: string;
    noImage: string;
    storage: string;
    saved: string;
    temporary: string;
    outputPending: string;
    noOutput: string;
  };
  outputNode?: {
    noArtifact: string;
  };
}

// Resources module messages
export interface ResourcesMessages {
  // Page title and tabs
  pageTitle: string;
  tabs: {
    artifacts: string;
    library: string;
    temp: string;
  };

  // Resource types
  types: {
    artifact: string;
    library: string;
    temp: string;
  };

  // Stats
  stats: {
    totalFiles: string;
    totalSize: string;
    uniqueUsers: string;
    byType: string;
    bySubtype: string;
    files: string;
    users: string;
    subtypes: string;
    categories: string;
    avgPerUser: string;
    largestUsers: string;
    autoCleanup: string;
  };

  // Summary
  summary: {
    totalFiles: string;
    totalSize: string;
  };

  // Modal
  modal: {
    title: string;
    summary: string;
    noResources: string;
    loading: string;
    error: string;
    deleteWarning: string;
  };

  // Status
  status: {
    expired: string;
    current: string;
  };

  // Actions
  actions: {
    viewResources: string;
    delete: string;
    open: string;
    download: string;
    confirmDelete: string;
    cancel: string;
    browseTools: string;
  };

  // Groups
  groups?: {
    uncategorized: string;
    expandAll: string;
    collapseAll: string;
  };

  // Delete all
  deleteAll: {
    type: string;
    all: string;
    confirmType: string;
    confirmAll: string;
  };

  // Deleting state
  deleting: string;

  // Table headers
  table: {
    userId: string;
    totalFiles: string;
    totalSize: string;
  };

  // Cleanup operations
  cleanup: {
    title: string;
    tempTitle: string;
    tempDesc: string;
    tempButton: string;
    artifactsTitle: string;
    artifactsDesc: string;
    artifactsButton: string;
    daysOld: string;
    days: string;
    confirm: string;
    cleaning: string;
    error: string;
    successTemp: string;
    successArtifacts: string;
  };

  // Timestamps
  lastUpdated: string;

  // Errors
  errors: {
    loadFailed: string;
  };
}

export interface I18nMessages {
  common: {
    appTitle: string;
    save: string;
    saving: string;
    cancel: string;
    delete: string;
    reset: string;
    loading: string;
    success: string;
    error: string;
    warning: string;
    required: string;
    previous: string;
    next: string;
    refresh: string;
    close: string;
    description: string;
    retry: string;
    clear: string;
    admin: string;
  };
  actions: {
    saveChanges: string;
    resetToDefault: string;
    resetToSystemDefault: string;
    browseTools: string;
  };
  navigation: NavigationMessages;
  sections: Record<string, string>;
  fields: Record<string, FieldTranslation>;
  pushNotifications: PushNotificationsMessages;
  evalScenarios: EvalScenariosMessages;
  evaluation: EvaluationMessages;
  monitoring: MonitoringMessages;
  classificationAnalytics: ClassificationAnalyticsMessages;
  users: UsersMessages;
  systemSettings: SystemSettingsMessages;
  agentConfig: AgentConfigMessages;
  hooks: HooksMessages;
  imageWorkflows: ImageWorkflowsMessages;
  resources: ResourcesMessages;
  // Shared components
  userSelector?: UserSelectorMessages;
  workflowTarget?: WorkflowTargetMessages;
}

// User Selector component messages
export interface UserSelectorMessages {
  searchPlaceholder: string;
  selectedCount: string;
  selectAll: string;
  deselectAll: string;
  clearAll: string;
  noUsers: string;
  devices: string;
  showingCount: string;
}

// Workflow Target Selector component messages
export interface WorkflowTargetMessages {
  currentUser: string;
  currentUserDesc: string;
  specificUsers: string;
  specificUsersDesc: string;
  willRunAs: string;
}

export interface FieldTranslation {
  label: string;
  help?: string;
  placeholder?: string;
  invalidMessage?: string;
  options?: Record<string, string>;
}

const messages: Record<Language, I18nMessages> = {
  // ========== Chinese (Simplified) ==========
  zh: {
    common: {
      appTitle: "Agent 管理",
      save: "保存",
      saving: "保存中...",
      cancel: "取消",
      delete: "删除",
      reset: "重置",
      loading: "加载中...",
      success: "成功",
      error: "错误",
      warning: "警告",
      required: "必填",
      previous: "上一页",
      next: "下一页",
      refresh: "刷新",
      close: "关闭",
      description: "描述",
      retry: "重试",
      clear: "清除",
      admin: "管理员",
    },
    actions: {
      saveChanges: "保存更改",
      resetToDefault: "重置为默认值",
      resetToSystemDefault: "重置为系统默认",
      browseTools: "浏览工具",
    },
    navigation: {
      appTitle: "Dora 管理",
      agentConfigs: "Agent 配置",
      systemSettings: "系统设置",
      promptTemplates: "Prompt 模板",
      skills: "技能",
      monitoring: "服务监控",
      reports: "运营报表",
      classificationAnalytics: "分类分析",
      messageFeedback: "消息反馈",
      users: "用户管理",
      pushTest: "推送测试",
      evaluation: "评测中心",
      playground: "测试广场",
      imageWorkflows: "图像工作流",
      workflowSchedules: "定时任务",
      annotation: "数据标注",
      logout: "退出登录",
      expandSidebar: "展开侧边栏",
      collapseSidebar: "收起侧边栏",
    },
    sections: {
      basic: "基本信息",
      models: "模型配置",
      llm_parameters: "LLM 采样参数",
      output_format: "输出格式",
      features: "功能开关",
      message_processing: "消息处理",
      advanced: "高级设置",
      memory: "记忆配置",
      memory_sampling: "记忆模型采样",
      memory_prompts: "记忆自定义提示词",
      memory_user: "用户记忆",
      memory_agent: "助手记忆",
      memory_query: "记忆查询设置",
      memory_ebbinghaus: "记忆衰减 (艾宾浩斯)",
      session_summary: "会话摘要",
      custom_tools: "自定义工具",
      builtin_tools: "内置工具",
      mcp_tools: "MCP 服务器",
      team: "团队配置",
      prompts: "系统提示词",
      first_message: "首条消息扩展",
      response_classifier: "响应分类扩展",
      response_classifier_prompts: "响应类型指令",
    },
    fields: {
      // Basic Information
      agent_type: {
        label: "Agent 类型",
        help: "Agent 的唯一标识符",
      },
      name: {
        label: "名称",
        help: "Agent 的显示名称",
        placeholder: "请输入 Agent 名称",
      },
      description: {
        label: "描述",
        help: "Agent 的简要说明",
        placeholder: "请输入简要描述",
      },

      // Model Configuration
      chat_model_id: {
        label: "对话模型",
        help: "用于对话生成的 LLM 模型",
        invalidMessage: '警告: 模型 ID "{value}" 不在可用列表中',
      },
      memory_model_id: {
        label: "记忆模型",
        help: "用于记忆提取和管理的模型",
        invalidMessage: '警告: 模型 ID "{value}" 不在可用列表中',
      },
      embedding_model_id: {
        label: "向量模型",
        help: "用于文本向量化的嵌入模型",
        invalidMessage: '警告: 模型 ID "{value}" 不在可用列表中',
      },

      // LLM Sampling Parameters
      temperature: {
        label: "温度",
        help: "采样温度 (0=确定性, 数值越高越随机). 范围: 0.0-2.0",
      },
      top_p: {
        label: "Top P",
        help: "核采样概率 (0.0-1.0). 数值越低使用的token越少",
      },
      max_tokens: {
        label: "最大Token数",
        help: "生成的最大token数 (留空为不限). 范围: 1-128000",
        placeholder: "留空为不限",
      },
      presence_penalty: {
        label: "存在惩罚",
        help: "Token存在惩罚 (-2.0到2.0). 正值鼓励新主题",
      },
      frequency_penalty: {
        label: "频率惩罚",
        help: "Token频率惩罚 (-2.0到2.0). 正值减少重复",
      },

      // Features
      output_format: {
        label: "输出格式",
        help: "响应输出格式：默认（纯文本）、Markdown 或 JSON",
        options: {
          default: "默认",
          markdown: "Markdown",
          json: "JSON",
        },
      },
      debug_mode: {
        label: "调试模式",
        help: "启用详细日志输出",
      },
      storage_enabled: {
        label: "启用存储",
        help: "启用会话存储功能",
      },
      knowledge_enabled: {
        label: "知识库",
        help: "启用知识库检索",
      },
      enable_birth_chart_context: {
        label: "出生星盘上下文",
        help: "自动注入用户出生星盘数据（XML格式）到Agent上下文。需要用户已添加星盘。",
      },
      enable_web_search: {
        label: "网页搜索",
        help: "启用网页搜索功能",
      },

      // Message Processing
      add_datetime_to_instructions: {
        label: "添加日期时间",
        help: "在系统提示词中添加当前日期和时间",
      },
      add_history_to_context: {
        label: "添加历史记录",
        help: "在Agent上下文中包含对话历史 (Agno: add_history_to_context)",
      },
      num_history_runs: {
        label: "最大历史轮数",
        help: "包含的最大对话轮数 (0-50). 防止上下文溢出 (Agno: num_history_runs)",
      },
      read_chat_history: {
        label: "读取聊天历史",
        help: "允许Agent通过工具查询完整聊天历史 (Agno: read_chat_history)",
      },

      // Memory Configuration
      "memory.enabled": {
        label: "启用记忆",
        help: "启用长期记忆存储",
      },
      "memory.provider": {
        label: "记忆提供者",
        help: "选择记忆存储后端",
        options: {
          MEM0: "MEM0",
          POSTGRES: "PostgreSQL",
          NONE: "无",
        },
        invalidMessage:
          '警告: 提供者 "{value}" 无效。应为: MEM0, POSTGRES 或 NONE',
      },
      "memory.model_id": {
        label: "记忆模型",
        help: "用于记忆提取和操作的模型",
        invalidMessage: '警告: 模型ID "{value}" 不在可用记忆模型列表中',
      },
      "memory.enable_agentic_memory": {
        label: "Agentic 记忆",
        help: "启用Agentic记忆管理 - 为Agent添加记忆控制工具。MEM0: 添加搜索/添加/更新/删除记忆工具。PostgreSQL: 添加记忆搜索/保存工具。启用后，Agent可以主动管理自己的记忆。",
      },
      "memory.operation_mode": {
        label: "Mem0 操作模式",
        help: "选择记忆操作模式",
        options: {
          SYNC: "同步 (默认 - 等待确认)",
          FIRE_FORGET: "触发即忘 (快速 - 无需等待)",
          BATCH: "批处理 (队列批量处理)",
        },
      },
      "memory.loading_strategy": {
        label: "记忆加载策略",
        help: "选择记忆加载策略",
        options: {
          balanced: "平衡 (默认 - 3偏好, 3事实, 2计划, 2最近)",
          preference_first: "偏好优先 (5偏好, 2关键事实, 1最近)",
          context_heavy: "上下文重 (2关键偏好, 3关键, 5情景)",
          minimal: "最小化 (仅3关键, 2情景)",
          time_aware: "时间感知 (3计划, 2偏好, 3最近事实)",
        },
      },

      // Memory Sampling
      "memory.model_sampling.temperature": {
        label: "记忆温度",
        help: "记忆模型温度 (更低=更真实). 默认: 0.3 (比聊天更确定性)",
      },
      "memory.model_sampling.top_p": {
        label: "记忆 Top P",
        help: "记忆模型核采样 (默认: 0.9 精确度)",
      },
      "memory.model_sampling.max_tokens": {
        label: "记忆最大Token数",
        help: "记忆操作的最大token数 (默认: 2000)",
        placeholder: "2000",
      },

      // Memory Custom Prompts
      "memory.prompts.enable_custom_prompts": {
        label: "启用自定义提示词",
        help: "为此Agent启用Mem0自定义提示词功能",
      },
      "memory.prompts.user_memory_extraction_mode": {
        label: "用户记忆提取模式",
        help: "选择使用系统默认或自定义提示词",
        options: {
          system: "使用系统默认",
          custom: "使用自定义提示词",
        },
      },
      "memory.prompts.user_memory_extraction_prompt": {
        label: "用户记忆提取提示词",
        help: '从用户消息中提取记忆的自定义提示词。点击"重置为系统默认"加载默认值。',
        placeholder: "输入自定义用户记忆提取提示词 (50-4000字符)...",
      },
      "memory.prompts.agent_memory_extraction_mode": {
        label: "Agent记忆提取模式",
        help: "选择使用系统默认或自定义提示词",
        options: {
          system: "使用系统默认",
          custom: "使用自定义提示词",
        },
      },
      "memory.prompts.agent_memory_extraction_prompt": {
        label: "Agent记忆提取提示词",
        help: '从Agent消息中提取记忆的自定义提示词。点击"重置为系统默认"加载默认值。',
        placeholder: "输入自定义Agent记忆提取提示词 (50-4000字符)...",
      },
      "memory.prompts.memory_update_mode": {
        label: "记忆更新模式",
        help: "选择使用系统默认或自定义提示词",
        options: {
          system: "使用系统默认",
          custom: "使用自定义提示词",
        },
      },
      "memory.prompts.memory_update_prompt": {
        label: "记忆更新提示词",
        help: '用于更新和合并记忆的自定义提示词。点击"重置为系统默认"加载默认值。',
        placeholder: "输入自定义记忆更新提示词 (50-4000字符)...",
      },

      // Memory Categories
      "memory.categories.enable_user_custom_categories": {
        label: "启用用户自定义类别",
        help: "启用用户记忆的自定义类别",
      },
      "memory.categories.user_categories_mode": {
        label: "用户类别模式",
        help: "选择使用系统默认或自定义类别",
        options: {
          system: "使用系统默认",
          custom: "使用自定义类别",
        },
      },
      "memory.categories.user_custom_categories": {
        label: "用户自定义类别",
        help: '以JSON格式定义用户记忆类别。每个类别必须包含: description (字符串), examples (数组), temporal (布尔值)。点击"重置为系统默认"加载默认类别。',
        placeholder:
          '{"preference": {"description": "用户偏好", "examples": ["喜欢披萨"], "temporal": false}}',
      },
      "memory.categories.enable_agent_custom_categories": {
        label: "启用Agent自定义类别",
        help: "启用Agent记忆的自定义类别",
      },
      "memory.categories.agent_categories_mode": {
        label: "Agent类别模式",
        help: "选择使用系统默认或自定义类别",
        options: {
          system: "使用系统默认",
          custom: "使用自定义类别",
        },
      },
      "memory.categories.agent_custom_categories": {
        label: "Agent自定义类别",
        help: '以JSON格式定义Agent记忆类别。每个类别必须包含: description (字符串), examples (数组), temporal (布尔值)。点击"重置为系统默认"加载默认类别。',
        placeholder:
          '{"recommendation": {"description": "建议记录", "examples": ["建议每天锻炼"], "temporal": false}}',
      },

      // Memory Query Settings
      "memory.query.rerank_enabled": {
        label: "启用重排序",
        help: "启用记忆查询结果的重排序功能",
      },
      "memory.query.top_k": {
        label: "Top K",
        help: "返回的最大记忆条数",
      },
      "memory.query.score_threshold": {
        label: "分数阈值",
        help: "记忆相关性分数的最低阈值 (0.0-1.0)",
      },
      "memory.query.facts_limit": {
        label: "事实查询限制",
        help: "检索的最大永久用户事实数量 (1-100)",
      },
      "memory.query.events_limit": {
        label: "事件查询限制",
        help: "检索的最大时序事件记忆数量 (1-50)",
      },
      "memory.query.events_threshold": {
        label: "事件搜索阈值",
        help: "事件搜索的相似度阈值 (0.0-1.0)。较低的值返回更多结果。",
      },
      "memory.query.agent_memories_limit": {
        label: "Agent记忆限制",
        help: "检索的最大Agent专属记忆数量 (1-20)",
      },
      "memory.query.agent_memories_threshold": {
        label: "Agent记忆阈值",
        help: "Agent记忆搜索的相似度阈值 (0.0-1.0)。较低的值返回更多结果。",
      },
      "memory.query.query_max_length": {
        label: "查询最大长度",
        help: "记忆搜索查询的最大总长度（字符数）(100-1000)",
      },
      "memory.query.user_query_length": {
        label: "用户查询长度",
        help: "搜索查询中当前用户问题的最大长度 (50-500字符)",
      },
      "memory.query.assistant_context_length": {
        label: "助手上下文长度",
        help: "搜索查询中最后助手回复的最大长度 (0-500字符)。设为0可禁用助手上下文。",
      },

      // Memory Decay (Ebbinghaus)
      "memory.ebbinghaus.enabled": {
        label: "启用记忆衰减",
        help: "启用艾宾浩斯遗忘曲线机制。时序记忆会根据访问频率随时间衰减。",
      },
      "memory.ebbinghaus.decay_factor": {
        label: "衰减系数",
        help: "控制衰减速度 (0.1=快速遗忘, 1.0=缓慢遗忘)。数值越高，记忆衰减越慢。",
      },
      "memory.ebbinghaus.min_retention": {
        label: "最小保持率",
        help: "记忆保持率下限 (0.0-1.0)。记忆永远不会衰减到此值以下。默认0.1表示记忆至少保留10%相关性。",
      },
      "memory.ebbinghaus.initial_strength": {
        label: "初始强度",
        help: "新记忆的初始强度 (0.5-2.0)。数值越高，新记忆越能抵抗衰减。",
      },
      "memory.ebbinghaus.reinforcement_boost": {
        label: "强化提升",
        help: "记忆被访问时的强度增加值 (0.1-1.0)。数值越高，每次访问对记忆的强化效果越大。",
      },
      "memory.ebbinghaus.max_strength": {
        label: "最大强度",
        help: "记忆强度上限 (1.0-5.0)。限制记忆通过强化能达到的最大强度。",
      },

      // Session Summary Configuration
      "session_summary.enabled": {
        label: "启用会话摘要",
        help: "为长对话自动生成摘要。摘要会压缩早期对话内容，注入到系统提示中，让 Agent 保持对整个对话的感知。",
      },
      "session_summary.model_id": {
        label: "摘要生成模型",
        help: "用于生成摘要的模型。建议使用快速、经济的模型（如 gpt-5-mini），因为摘要生成是后台任务。",
      },
      "session_summary.summary_frequency": {
        label: "生成时机",
        help: "选择何时生成摘要：\n• 按需生成：仅通过 API 手动触发\n• N条消息后：达到阈值后自动生成（推荐）\n• 会话结束时：会话结束时生成",
        options: {
          on_request: "按需生成",
          after_n_messages: "N条消息后（推荐）",
          end_of_session: "会话结束时",
        },
      },
      "session_summary.message_threshold": {
        label: "首次生成阈值",
        help: "对话达到多少条消息后生成第一次摘要。\n\n💡 推荐配置：设置为与「历史运行数」相同的值。\n\n例如：历史运行数=5，阈值=5\n• 消息1-5：由历史记录覆盖（无需摘要）\n• 消息6+：摘要覆盖[1, N-5]，历史覆盖[N-4, N]\n\n这样确保信息无缝衔接，不会遗漏。",
      },
      "session_summary.regenerate_interval": {
        label: "再生成间隔",
        help: "首次生成后，每隔多少条新消息重新生成摘要。\n• 0：永不重新生成（只用第一次的摘要）\n• N > 0：每N条新消息后更新摘要\n\n💡 推荐配置：设置为与「历史运行数」相同的值，实现滚动覆盖。",
      },
      "session_summary.inject_into_prompt": {
        label: "注入系统提示",
        help: "是否将摘要作为上下文信息注入到系统提示中。启用后，Agent 在回复时能感知到早期对话的要点。",
      },
      "session_summary.custom_prompt": {
        label: "自定义摘要提示词",
        help: "自定义摘要生成的提示词模板。留空则使用 Agno 框架的默认提示词。\n\n可用变量：{messages}（对话内容）",
      },

      // Advanced Settings
      session_timeout_minutes: {
        label: "会话超时 (分钟)",
        help: "会话自动超时时间",
      },
      max_cached_resources: {
        label: "最大缓存资源数",
        help: "缓存的最大资源数量",
      },
      resource_ttl_minutes: {
        label: "资源TTL (分钟)",
        help: "资源缓存的有效时间",
      },

      // Tools
      custom_tools: {
        label: "自定义工具",
        help: "自定义工具配置 - 可以是模块名（字符串）、模块列表（数组）或详细配置（对象）",
        placeholder:
          '{"modules": ["tools.astrology.astro_tools"], "enabled_tools": ["calculate_natal_chart"]}',
      },
      builtin_tools: {
        label: "内置工具",
        help: "启用的内置工具列表",
      },
      mcp_servers: {
        label: "MCP 服务器",
        help: "MCP服务器配置 - 可以是服务器ID（字符串）、ID列表（数组）或详细配置（对象）",
        placeholder:
          '{"servers": ["astro-tool"], "enabled_tools": ["mcp__astro_tool__get_birth_chart"]}',
      },

      // System Prompts
      instructions: {
        label: "系统指令",
        help: "定义Agent的角色、行为和能力",
        placeholder: "请输入Agent的系统指令...",
      },
      additional_context: {
        label: "额外上下文",
        help: "额外的上下文、示例或约束",
        placeholder: "请输入额外的上下文或指引...",
      },
      enable_first_message_instruction: {
        label: "启用首条消息指令",
        help: "为新话题/会话的首条消息启用特殊指令",
      },
      first_message_mode: {
        label: "指令模式",
        help: "选择使用系统默认或自定义指令",
        options: {
          system: "使用系统默认",
          custom: "使用自定义指令",
        },
      },
      first_message_custom_instruction: {
        label: "自定义首条消息指令",
        help: '首条消息的自定义指令。点击"重置为系统默认"加载默认值。',
        placeholder: "请输入自定义指令...",
      },

      // Response Classifier
      "response_classifier.enabled": {
        label: "启用响应分类器",
        help: "对用户消息进行分类以确定响应类型（快速响应 vs 深度响应）。根据消息复杂度启用动态提示词选择。",
      },
      "response_classifier.model_id": {
        label: "兜底分类模型",
        help: "当规则匹配置信度低于阈值时，使用此模型进行 LLM 分类。建议使用快速、低成本的模型。",
      },
      "response_classifier.rule_confidence_threshold": {
        label: "规则置信度阈值",
        help: "规则分类的置信度阈值。低于此值时使用LLM分类。范围: 0.0-1.0，默认: 0.8",
      },
      "response_classifier.quick_response_max_length": {
        label: "快速响应最大长度",
        help: "快速响应分类的最大消息长度（字符数）。更长的消息倾向于深度响应。",
      },
      "response_classifier.llm_timeout": {
        label: "LLM 超时 (秒)",
        help: "LLM分类的超时时间（秒）。超时后默认为深度响应。",
      },

      // Response Type Instructions
      "response_classifier.quick_response_instructions": {
        label: "快速响应指令",
        help: "用于快速响应的指令（问候、确认、闲聊）。如果为空，将使用默认指令。",
        placeholder:
          "你是一个友好的助手。请简洁、自然地回复用户的问候或简单问题。保持轻松愉快的对话氛围。",
      },
      "response_classifier.deep_response_instructions": {
        label: "深度响应指令",
        help: "用于深度响应的指令（分析、咨询、情感支持）。如果为空，将使用默认指令。",
        placeholder:
          "你是一位专业的顾问。请认真分析用户的问题，提供深入、专业的见解和建议。考虑多个角度，给出有价值的分析。",
      },

      // ========== Hook Configuration Fields ==========

      // Memory Query Settings (hooks.user_facts, hooks.memory_context - unified)
      "hooks.user_facts.facts_limit": {
        label: "事实查询限制",
        help: "检索的最大永久用户事实数量 (1-100)",
      },
      "hooks.memory_context.events_limit": {
        label: "事件查询限制",
        help: "检索的最大时序事件记忆数量 (1-50)",
      },
      "hooks.memory_context.events_threshold": {
        label: "事件搜索阈值",
        help: "事件搜索的相似度阈值 (0.0-1.0)。较低的值返回更多结果。",
      },
      "hooks.memory_context.agent_memories_limit": {
        label: "Agent记忆限制",
        help: "检索的最大Agent专属记忆数量 (1-20)",
      },
      "hooks.memory_context.agent_memories_threshold": {
        label: "Agent记忆阈值",
        help: "Agent记忆搜索的相似度阈值 (0.0-1.0)。较低的值返回更多结果。",
      },
      "hooks.memory_context.query_max_length": {
        label: "查询最大长度",
        help: "记忆搜索查询的最大总长度（字符数）(100-1000)",
      },
      "hooks.memory_context.user_query_length": {
        label: "用户查询长度",
        help: "搜索查询中当前用户问题的最大长度 (50-500字符)",
      },
      "hooks.memory_context.assistant_context_length": {
        label: "助手上下文长度",
        help: "搜索查询中最后助手回复的最大长度 (0-500字符)。设为0可禁用助手上下文。",
      },

      // Memory Decay / Ebbinghaus (hooks.memory_reinforce)
      "hooks.memory_reinforce.enabled": {
        label: "启用记忆衰减",
        help: "启用艾宾浩斯遗忘曲线机制。时序记忆会根据访问频率随时间衰减。",
      },
      "hooks.memory_reinforce.decay_factor": {
        label: "衰减系数",
        help: "控制衰减速度 (1.0=快速遗忘, 30.0=缓慢遗忘)。数值越高，记忆衰减越慢。",
      },
      "hooks.memory_reinforce.min_retention": {
        label: "最小保持率",
        help: "记忆保持率下限 (0.0-0.5)。记忆永远不会衰减到此值以下。默认0.1表示记忆至少保留10%相关性。",
      },
      "hooks.memory_reinforce.initial_strength": {
        label: "初始强度",
        help: "新记忆的初始强度 (0.5-2.0)。数值越高，新记忆越能抵抗衰减。",
      },
      "hooks.memory_reinforce.reinforcement_boost": {
        label: "强化提升",
        help: "记忆被访问时的强度增加值 (0.1-1.0)。数值越高，每次访问对记忆的强化效果越大。",
      },
      "hooks.memory_reinforce.max_strength": {
        label: "最大强度",
        help: "记忆强度上限 (1.0-5.0)。限制记忆通过强化能达到的最大强度。",
      },

      // First Message Hook (hooks.first_message)
      "hooks.first_message.enabled": {
        label: "启用首条消息扩展",
        help: "为新会话的首条消息添加特殊指令",
      },
      "hooks.first_message.mode": {
        label: "首条消息模式",
        help: "选择使用系统默认或自定义指令",
        options: {
          system: "使用系统默认",
          custom: "使用自定义指令",
        },
      },
      "hooks.first_message.custom_instruction": {
        label: "自定义首条消息指令",
        help: "新会话首条消息的自定义指令",
        placeholder: "请输入自定义指令...",
      },

      // Response Classifier Hook (hooks.classifier)
      "hooks.classifier.enabled": {
        label: "启用响应分类器",
        help: "对用户消息进行分类以确定响应类型（快速响应 vs 深度响应）。根据消息复杂度启用动态提示词选择。",
      },
      "hooks.classifier.model_id": {
        label: "兜底分类模型",
        help: "当规则匹配置信度低于阈值时，使用此模型进行 LLM 分类。建议使用快速、低成本的模型。",
      },
      "hooks.classifier.rule_confidence_threshold": {
        label: "规则置信度阈值",
        help: "规则分类的置信度阈值。低于此值时使用LLM分类。范围: 0.0-1.0，默认: 0.8",
      },
      "hooks.classifier.quick_response_max_length": {
        label: "快速响应最大长度",
        help: "快速响应分类的最大消息长度（字符数）。更长的消息倾向于深度响应。",
      },
      "hooks.classifier.llm_timeout": {
        label: "LLM 超时 (秒)",
        help: "LLM分类的超时时间（秒）。超时后默认为深度响应。",
      },
      "hooks.classifier.quick_response_instructions": {
        label: "快速响应指令",
        help: "用于快速响应的指令（问候、确认、闲聊）。如果为空，将使用默认指令。",
        placeholder:
          "你是一个友好的助手。请简洁、自然地回复用户的问候或简单问题。保持轻松愉快的对话氛围。",
      },
      "hooks.classifier.deep_response_instructions": {
        label: "深度响应指令",
        help: "用于深度响应的指令（分析、咨询、情感支持）。如果为空，将使用默认指令。",
        placeholder:
          "你是一位专业的顾问。请认真分析用户的问题，提供深入、专业的见解和建议。考虑多个角度，给出有价值的分析。",
      },
    },
    pushNotifications: {
      // Page
      pageTitle: "推送通知测试",

      // Stats
      stats: {
        totalTokens: "总令牌数",
        activeTokens: "活跃令牌",
        pushSent: "已发送推送",
        successRate: "成功率",
        platformBreakdown: "平台分布",
        ios: "iOS",
        android: "Android",
      },

      // Send Notification Form
      sendForm: {
        title: "发送测试通知",
        description: "编写并发送测试推送通知",
        notificationTitle: "标题",
        notificationTitlePlaceholder: "通知标题",
        body: "内容",
        bodyPlaceholder: "通知消息内容",
        target: "发送目标",
        targetAll: "所有用户",
        targetAllWarning: "这将向所有已注册的用户发送通知。",
        targetTest: "测试用户",
        targetTestDesc: "发送到测试账号",
        targetSpecificUsers: "指定用户",
        targetSpecificUsersDesc: "发送到指定用户ID",
        userIds: "用户ID列表",
        userIdsPlaceholder: "输入用户ID，多个用逗号分隔",
        userIdsHint: "多个用户ID用逗号分隔",
        sound: "提示音",
        soundPlaceholder: "default",
        soundHint: "通知到达时播放的声音",
        soundDefault: "默认提示音",
        soundNone: "静音",
        badgeCount: "角标数字",
        badgeCountPlaceholder: "",
        badgeCountHint: "应用图标角标数字",
        badgeCountHintFull: "0 = 清除角标，留空 = 不修改",
        customData: "自定义数据 (JSON)",
        customDataPlaceholder: '{"type": "action", "action": "profile"}',
        customDataHint: "点击通知后触发的动作",
        dataTemplates: "快捷模板",
        dataTemplateLabels: {
          navigation: "首页",
          profile: "个人资料",
          feedback: "反馈",
          birthChart: "星盘",
          fortune: "运势",
        },
        clearButton: "清除",
        sendButton: "发送通知",
        sending: "发送中...",
        successMessage: "通知发送成功！",
        sent: "已发送",
        failed: "失败",
        errors: "错误",
      },

      // Device Tokens
      tokens: {
        tabTitle: "设备令牌",
        title: "已注册设备",
        allPlatforms: "所有平台",
        refresh: "刷新",
        loading: "加载中...",
        noTokens: "暂无注册的令牌",
        user: "用户",
        registered: "注册时间",
        active: "活跃",
        inactive: "未激活",
        page: "第",
        of: "页，共",
        total: "条",
        previous: "上一页",
        next: "下一页",
      },

      // Notification Logs
      logs: {
        tabTitle: "推送历史",
        title: "最近推送",
        allStatuses: "所有状态",
        statusSent: "已发送",
        statusFailed: "失败",
        refresh: "刷新",
        loading: "加载中...",
        noLogs: "暂无推送历史",
        error: "错误",
        page: "第",
        of: "页，共",
        total: "条",
        previous: "上一页",
        next: "下一页",
        // Detail view fields
        user: "用户",
        userId: "用户 ID",
        displayName: "用户名称",
        platform: "平台",
        deviceInfo: "设备信息",
        sentAt: "发送时间",
        pushToken: "推送令牌",
        fullTitle: "完整标题",
        fullBody: "完整内容",
        dataPayload: "自定义数据",
        actionNavigation: "导航",
        actionType: "动作",
        actionFortune: "运势",
        // Grouped view fields
        recipients: "个接收者",
        batches: "批次",
        totalRecipients: "接收者数量",
        recipientList: "接收者列表",
      },

      // Time formatting
      time: {
        justNow: "刚刚",
        minutesAgo: "{n}分钟前",
        hoursAgo: "{n}小时前",
        daysAgo: "{n}天前",
      },

      // Validation errors
      validation: {
        titleRequired: "标题为必填项",
        bodyRequired: "内容为必填项",
        userIdsRequired: "至少需要一个用户ID",
        invalidJson: "JSON格式无效",
      },

      // User Selector
      userSelector: {
        searchPlaceholder: "搜索用户名或ID...",
        selectedCount: "已选择 {count} 个用户",
        selectAll: "全选",
        deselectAll: "取消全选",
        clearAll: "清除",
        noUsers: "未找到用户",
        devices: "个设备",
        showingCount: "显示 {from}-{to} 条，共 {total} 条",
      },

      // Content Assist (AI-powered content generation)
      contentAssist: {
        sectionTitle: "推送内容",
        inputModeLabel: "内容输入方式",
        agentMode: "Agent",
        llmMode: "LLM",
        manualMode: "手动",
        selectAgent: "选择 Agent",
        loading: "加载中...",
        noAgents: "无可用 Agent",
        descriptionLabel: "描述推送内容",
        descriptionPlaceholder: "例如：提醒用户查看今日运势",
        generateButton: "生成内容",
        generating: "生成中...",
        selectAgentFirst: "请先选择一个 Agent",
        generateFailed: "生成内容失败",
        // LLM mode specific
        selectLLMModel: "选择 LLM 模型",
        noModels: "无可用模型",
        llmModeDescription: "使用原始 LLM API 生成提示词，完全控制系统提示",
        temperature: "温度 (Temperature)",
        precise: "精确 (0)",
        balanced: "平衡 (1)",
        creative: "创意 (2)",
        selectModelFirst: "请先选择一个 LLM 模型",
      },
    },
    evalScenarios: {
      // Page
      pageTitle: "评测场景",
      pageDescription: "管理测试场景并运行 Agent 评测以确保质量",

      // Actions
      createScenario: "创建场景",
      promoteChat: "提升对话",
      refresh: "刷新",
      runAll: "全部运行",
      runSingle: "运行测试",
      running: "运行中...",
      edit: "编辑",
      delete: "删除",
      duplicate: "复制",
      duplicateSuccess: "场景复制成功",
      expandDetails: "展开详情",
      collapseDetails: "收起详情",
      removeTag: "删除标签",

      // Filters
      filters: "筛选",
      searchPlaceholder: "搜索场景...",
      allAgentTypes: "所有 Agent 类型",
      allCategories: "所有分类",
      allSources: "所有来源",
      allStatus: "所有状态",
      sourceManual: "手动创建",
      sourceChatPromoted: "对话提升",
      statusActive: "已启用",
      statusInactive: "已禁用",

      // Stats
      totalScenarios: "总场景数",
      active: "已启用",
      manual: "手动创建",
      chatPromoted: "对话提升",
      neverRun: "未运行",

      // Form Steps
      step1Title: "基本信息",
      step1Description: "设置场景名称、选择目标 Agent 和分类",
      step2Title: "测试对话",
      step2Description: "定义要测试的对话（用户消息和预期流程）",
      step3Title: "评测配置",
      step3Description: "选择 LLM 评判模型和评测指标",
      stepOf: "第 {current} 步，共 {total} 步",
      next: "下一步",
      previous: "上一步",

      // Form Fields - Step 1
      scenarioName: "场景名称",
      scenarioNamePlaceholder: "例如：sagittarius_career_advice",
      scenarioNameHint: "建议使用下划线分隔的小写字母（snake_case）",
      description: "描述",
      descriptionPlaceholder:
        "此场景测试什么？例如：测试 Agent 是否为射手座用户提供有帮助的职业建议",
      targetAgent: "目标 Agent",
      targetAgentHint: "选择要运行此测试的 Agent",
      category: "分类",
      categoryPlaceholder: "例如：占星、职业",
      priority: "优先级",
      priorityHint: "数值越高越优先执行",
      tags: "标签",
      addTagPlaceholder: "输入标签后按回车添加...",

      // Form Fields - Step 2
      testConversation: "测试对话",
      conversationHint:
        "定义测试对话。至少添加一条用户消息发送给 Agent。可以添加多轮对话来模拟多轮会话。",
      addTurn: "添加对话",
      userRole: "用户",
      assistantRole: "助手",
      userMessagePlaceholder: "要测试的用户消息...",
      assistantMessagePlaceholder: "预期的助手回复（用于上下文参考）...",
      expectedResponse: "期望响应",
      expectedResponsePlaceholder:
        "填写一个理想的 Assistant 回复示例，例如：\n\n好的，考虑到你喜欢安静的环境，推荐以下小众景点：\n1. 青城后山 - 人少清幽，门票60元\n2. 街子古镇 - 无门票，约2小时车程\n\n你倾向哪种类型？",
      expectedResponseHint: "填写理想的回复文本，而非评判规则",
      expectedResponseDetailTitle: "什么是期望响应？",
      expectedResponseDetail1:
        '✅ 应填写：一个具体的理想回复示例（"Agent 应该怎么回复"）',
      expectedResponseDetail2:
        '❌ 不要填写：评判规则描述（那是"评测标准 Criteria"的作用）',
      expectedResponseDetail3:
        "用途：用于 AnswerRelevancy、Correctness 等指标做语义相似度比较",
      expectedResponseExample:
        "如果主要用 GEval 自定义指标，此字段可留空，评判主要依靠 Criteria",

      // Form Fields - Step 3
      llmJudgeModel: "LLM 评判模型",
      llmJudgeModelHint: "推荐：gpt-4o-mini 性价比高，gpt-4o 准确度更高",
      llmJudgeModelWarning:
        "⚠️ 此模型可能不被 DeepEval 支持。建议使用 OpenAI GPT 系列模型（如 gpt-4o-mini, gpt-4o）以确保评测正常运行。",
      quickAddMetrics: "快速添加指标",
      evaluationCriteria: "评测标准",
      customMetric: "自定义指标",
      metricNamePlaceholder: "指标名称",
      threshold: "阈值",
      noMetricsSelected: '未选择指标。点击上方"快速添加"按钮或添加自定义指标。',
      minOverallScore: "最低通过分数",
      minOverallScoreHint: "启用指标的加权平均分超过此阈值则通过",
      activeCheckbox: "启用（场景将包含在批量评测中）",

      // Metrics
      metrics: {
        hallucination: "幻觉检测",
        hallucinationDesc: "检测事实性错误",
        hallucinationHelp:
          "检测 AI 是否编造了不存在的信息。分数越低越好 (0=无幻觉, 1=严重幻觉)。阈值 0.3 表示幻觉分数必须低于 30% 才算通过。",
        relevancy: "相关性",
        relevancyDesc: "检查响应是否相关",
        relevancyHelp:
          "评估回答与用户问题的相关程度。分数越高越好 (0=完全无关, 1=高度相关)。阈值 0.7 表示相关性必须达到 70% 以上。",
        faithfulness: "忠实度",
        faithfulnessDesc: "检查对源内容的忠实程度",
        faithfulnessHelp:
          '检查回答是否忠实于提供的上下文/参考内容，不添加额外信息。分数越高越好。需要提供"期望响应"作为参考。',
        answer_relevancy: "答案相关性",
        answer_relevancyDesc: "检查答案质量",
        answer_relevancyHelp:
          "综合评估答案的质量：是否直接回答问题、信息是否完整、表达是否清晰。分数越高越好。",
        contextual_relevancy: "上下文相关性",
        contextual_relevancyDesc: "检查上下文使用情况",
        contextual_relevancyHelp:
          "评估 AI 是否正确使用了对话历史和上下文信息。适用于多轮对话场景。",
        bias: "偏见检测",
        biasDesc: "检测偏见内容",
        biasHelp:
          "检测回答中是否包含性别、种族、年龄等方面的偏见。分数越低越好 (0=无偏见)。",
        toxicity: "毒性检测",
        toxicityDesc: "检测有害内容",
        toxicityHelp:
          "检测回答中是否包含攻击性、侮辱性或有害内容。分数越低越好 (0=安全)。阈值 0.1 表示毒性必须低于 10%。",
        astro_accuracy: "占星准确性",
        astro_accuracyDesc: "检查占星术语准确性",
        astro_accuracyHelp:
          "评估占星相关内容的专业准确性：星座识别、行星位置、宫位解读、相位分析等是否正确。",
        interpretation_quality: "解读质量",
        interpretation_qualityDesc: "评估解读的深度和见解",
        interpretation_qualityHelp:
          "评估占星解读的质量：深度和洞察力、个人相关性、专业性与易读性的平衡、是否提供可行建议。",
        helpfulness: "有用性",
        helpfulnessDesc: "评估回答的帮助程度",
        helpfulnessHelp:
          "评估回答对用户的实际帮助：是否解决了用户问题、信息是否实用可行、语气是否恰当友好。",
      },
      metricsGuide: "评测指南",
      metricsGuideContent:
        "指标分两类：1) 越低越好 (幻觉、偏见、毒性) - 阈值代表可接受的最大分数；2) 越高越好 (相关性、忠实度等) - 阈值代表最低要求分数。所有分数范围为 0-1。",
      lowerIsBetter: "↓ 越低越好",
      higherIsBetter: "↑ 越高越好",

      // Dialogs
      createDialogTitle: "创建评测场景",
      editDialogTitle: "编辑评测场景",
      createButton: "创建场景",
      saveChanges: "保存更改",

      // Promote Chat Dialog
      promoteChatTitle: "提升对话为测试场景",
      promoteChatDescription:
        "将真实的聊天对话转换为测试场景。对话中的消息将被提取并用作测试对话内容。",
      chatTopicId: "对话/主题 ID",
      chatTopicIdPlaceholder: "输入对话或主题 ID",
      chatTopicIdHint: "可在聊天历史或数据库中查找",
      promoteButton: "提升为场景",

      // Run Evaluation Dialog
      runEvaluationTitle: "运行评测",
      runningEvaluation: "正在评测...",
      runningHint: "您可以关闭此对话框，评测将在后台继续运行",
      runningHintResult: "完成后结果将显示在评测历史中",
      evaluationStarted: "评测已启动",
      evaluationStartedHint: "评测正在后台运行中，您可以关闭此对话框",
      checkRunsPanel: "请打开「运行记录」查看实时进度",
      evaluationComplete: "评测完成",
      overallScore: "综合得分",
      passed: "通过",
      failed: "未通过",
      response: "响应内容",
      responseTime: "响应时间",
      metricResults: "指标结果",
      reason: "原因",
      scenario: "场景",
      agentType: "Agent 类型",
      judgerModel: "评判模型",
      criteriaLabel: "评测标准",
      batchSummary: "批量评测概况",
      total: "总计",
      close: "关闭",
      runEvaluationButton: "运行评测",
      batchInfoPrefix: "这将为",
      batchInfoSuffix: "个已启用的场景运行评测。",

      // Results
      lastResult: "最近结果",
      noResults: "暂无结果",

      // Confirmations
      deleteConfirm: "确定要删除此场景吗？",

      // Success/Error messages
      createSuccess: "场景创建成功",
      updateSuccess: "场景更新成功",
      deleteSuccess: "场景删除成功",
      promoteSuccess: "对话提升成功",
      runSuccess: "评测完成",
      error: "操作失败",

      // History Panel
      historyPanel: {
        title: "评测历史",
        scoreTrend: "分数趋势",
        recentRuns: "最近 {n} 次",
        noHistory: "暂无评测记录",
        checkRunsPanelHint:
          "如果您刚启动评测，请点击页面顶部的「运行记录」按钮查看进度",
        expandDetails: "展开详情",
        collapseDetails: "收起详情",
        agentResponse: "Agent 响应",
        responseTime: "响应时间",
        executedAt: "执行时间",
        executedBy: "执行者",
        metrics: "评测指标",
        resultsList: "评测记录",
        running: "运行中...",
        failed: "执行失败",
      },

      // Result Detail Dialog
      resultDetail: {
        title: "评测结果详情",
        viewDetails: "查看详情",
        overallScore: "综合得分",
        metricsTitle: "评测指标详情",
        judgmentReason: "LLM 判断理由",
        executedAt: "执行时间",
        model: "模型",
        responseTime: "响应时间",
        executedBy: "执行者",
        runningHint: "评测正在进行中，请稍后刷新查看结果",
      },

      // Runs Panel
      runsPanel: {
        title: "评测运行记录",
        runId: "运行 ID",
        totalScenarios: "场景",
        passed: "通过",
        failed: "失败",
        skipped: "跳过",
        averageScore: "平均分",
        duration: "耗时",
        viewDetails: "查看详情",
        noRuns: "暂无运行记录",
        noResults: "暂无结果数据",
        autoRefreshing: "自动刷新中...",
        statusRunning: "运行中",
        statusCompleted: "已完成",
        statusFailed: "失败",
      },

      // Results Panel
      resultsPanel: {
        title: "评测结果",
        loading: "加载结果中...",
        noResults: "暂无结果",
        noResultsHint: "运行评测以查看结果",
        averageScore: "平均分数",
        totalRuns: "运行次数",
        scenarioName: "场景名称",
        description: "描述",
        testInput: "测试输入",
        noInput: "无输入",
        history: "评测历史",
        detailedMetrics: "详细指标",
        agentResponse: "Agent 响应",
        evaluationNotes: "评测备注",
      },

      // Common texts
      optional: "可选",
      noDescription: "暂无描述",
      createFirstScenario: "创建您的第一个场景开始使用",
      input: "输入",

      // Time formatting
      time: {
        justNow: "刚刚",
        minutesAgo: "{n} 分钟前",
        hoursAgo: "{n} 小时前",
        daysAgo: "{n} 天前",
      },

      // Metric Definitions
      metricDefinitions: {
        pageTitle: "评测指标管理",
        pageDescription:
          "管理评测指标定义，查看内置指标 Prompt，自定义 GEval 指标",
        createMetric: "新建自定义指标",
        builtinMetrics: "系统内置指标",
        customMetrics: "自定义指标 (GEval)",
        viewPrompt: "查看",
        editMetric: "编辑",
        deleteMetric: "删除",
        metricName: "指标名称",
        displayName: "显示名称",
        metricType: "指标类型",
        defaultThreshold: "默认阈值",
        thresholdDirection: "阈值方向",
        higherBetter: "越高越好 (≥)",
        lowerBetter: "越低越好 (≤)",
        applicableAgents: "适用 Agent",
        allAgents: "全部",
        systemMetric: "系统指标",
        customGeval: "自定义 GEval",
        criteria: "评测标准 (Criteria)",
        criteriaPlaceholder: "描述如何评估响应质量...",
        criteriaHint:
          "Criteria 会被传递给 LLM 模型，用于评判回复质量。写得越具体，评测结果越一致。建议使用要点列表格式。",
        evaluationSteps: "评测步骤（高级）",
        evaluationStepsHint:
          "如果需要更精确控制评测流程，可以定义具体的评测步骤。设置后会覆盖 Criteria。",
        addStep: "添加步骤",
        removeStep: "删除",
        noMetrics: "暂无自定义指标",
        builtinPromptNote:
          "这是 DeepEval 内置指标，Prompt 由框架维护，无法修改。如需自定义评测逻辑，请创建新的 GEval 自定义指标。",
        promptViewer: {
          title: "查看指标 Prompt",
          type: "类型",
          source: "来源",
          threshold: "默认阈值",
          prompt: "评测 Prompt",
          readOnlyNote: "此 Prompt 仅供查看，不可编辑",
        },
        editor: {
          createTitle: "新建自定义指标",
          editTitle: "编辑指标",
          nameHint: "唯一标识符，使用小写字母和下划线",
          displayNameHint: "在界面上显示的名称",
          descriptionHint: "描述此指标评估什么内容",
          // Evaluation Steps
          evaluationStepsTitle: "评测步骤 (高级选项)",
          optional: "可选",
          stepsVsCriteriaTitle: "评测步骤 vs 评测标准 (Criteria)",
          stepsExplanation1:
            "评测标准 (Criteria): 描述性的评测要求，适合大多数场景",
          stepsExplanation2:
            "评测步骤 (Steps): 逐步的评测指令，LLM 会按顺序执行每一步",
          stepsExplanation3: "如果设置了评测步骤，将覆盖评测标准",
          exampleTitle: "示例 - 占星准确性评测步骤:",
          exampleStep1: "检查星座和行星位置是否与用户星盘数据一致",
          exampleStep2: "验证相位解读是否符合占星学原则",
          exampleStep3: "评估术语使用的专业性和准确性",
          exampleStep4: "根据以上检查结果给出 1-5 分的综合评分",
          yourSteps: "您的评测步骤",
          noStepsYet: "暂无评测步骤，点击下方按钮添加",
          stepPlaceholder: "描述这一步要检查或评估的内容...",
          addStep: "添加步骤",
          removeStep: "删除",
          // AI Generation
          aiAssistantTitle: "AI 智能设计",
          aiAssistantDescription:
            "描述您想要评测的内容，AI 会根据 DeepEval 最佳实践自动生成指标配置",
          aiDescriptionPlaceholder:
            "例如：评测回复中占星解读的准确性，包括星座位置、行星相位、宫位解释等是否符合专业标准...",
          generating: "生成中...",
          generateMetric: "生成指标配置",
          minChars: "至少 10 个字符",
          chars: "字符",
          aiExplanation: "AI 设计说明",
          aiDescriptionTooShort: "请提供更详细的描述（至少 10 个字符）",
          aiGenerationFailed: "AI 生成失败，请重试",
          selectModel: "选择模型",
          selectModelPlaceholder: "-- 请选择模型 --",
          selectModelFirst: "请先选择模型",
        },
        deleteConfirm: "确定要删除此指标吗？此操作不可撤销。",
        deleteSystemError: "系统内置指标不可删除",
        createSuccess: "指标创建成功",
        updateSuccess: "指标更新成功",
        deleteSuccess: "指标删除成功",
      },

      // Test Suites
      testSuites: {
        // Selection
        selectForSuite: "选择创建 Suite",
        exitSelect: "退出选择",
        createSuite: "创建 Suite",
        selectAll: "全选",
        deselectAll: "取消全选",
        selected: "已选",

        // Dialog
        createTitle: "创建 Test Suite",
        editTitle: "编辑 Test Suite",
        name: "名称",
        namePlaceholder: "输入 Suite 名称",
        description: "描述",
        descriptionPlaceholder: "输入描述（可选）",
        tags: "标签",
        tagsPlaceholder: "输入标签后按回车",
        priority: "优先级",
        scenarioCount: "包含场景数",
        scenarios: "个场景",
        cancel: "取消",
        create: "创建",
        save: "保存",

        // Run
        selectSuite: "选择 Test Suite",
        runAllActive: "运行所有激活的场景",
        runSuite: "运行 Test Suite",
        runSuiteInfo: "运行",
      },
    },
    evaluation: {
      pageTitle: "评测中心",
      pageDescription: "管理评测场景、指标定义和测试套件",
      tabs: {
        scenarios: "评测场景",
        metrics: "评测指标",
        suites: "Test Suite",
      },
    },
    monitoring: {
      // Page
      pageTitle: "服务监控",
      pageDescription: "实时健康状态和性能指标",
      lastUpdated: "最后更新",

      // Tabs
      tabs: {
        services: "服务健康",
        cache: "缓存",
        storage: "存储",
        queue: "提取队列",
      },

      // Queue
      queue: {
        title: "提取队列",
        status: "队列状态",
        running: "运行中",
        stopped: "已停止",
        queueSize: "队列大小",
        processed: "已处理",
        success: "成功",
        failed: "失败",
        merged: "已合并",
        savedCalls: "节省调用",
        mergeRate: "合并率",
        costSaving: "成本节省率",
        deadLetter: "死信队列",
        deadLetterDesc: "重试失败后的任务会进入死信队列，可手动重试或清理",
        retryAll: "全部重试",
        clearAll: "清空队列",
        retry: "重试",
        noTasks: "暂无失败任务",
        taskId: "任务ID",
        userId: "用户ID",
        memoryType: "类型",
        error: "错误",
        failedAt: "失败时间",
        retryCount: "重试次数",
        confirmClear: "确定要清空死信队列吗？此操作不可恢复。",
        confirmRetryAll: "确定要重试所有失败任务吗？",
        clearSuccess: "已清空 {count} 个任务",
        retrySuccess: "已重试 {count} 个任务",
        resetStats: "重置统计",
        confirmReset: "确定要重置队列统计数据吗？",
        resetSuccess: "统计数据已重置",
      },

      // System Task Queue
      systemTaskQueue: {
        title: "系统任务队列",
        queueSize: "队列大小",
        deadLetterSize: "死信数量",
        maxRetries: "最大重试次数",
        retryDelay: "重试延迟",
        jobTimeout: "任务超时",
        deadLetter: "系统任务死信队列",
        deadLetterDesc: "系统任务（如定时清理）失败后进入此队列",
        taskType: "任务类型",
        scheduledBy: "调度来源",
        scheduleId: "计划ID",
        config: "配置",
        lastError: "最后错误",
        finalError: "最终错误",
        failedAt: "失败时间",
        retryCount: "重试次数",
        noTasks: "暂无失败任务",
        confirmClear: "确定要清空系统任务死信队列吗？",
        confirmRetryAll: "确定要重试所有失败的系统任务吗？",
        clearSuccess: "已清空 {count} 个系统任务",
        retrySuccess: "已重试 {count} 个系统任务",
      },

      // Service Health
      serviceHealth: {
        title: "服务健康状态",
        services: "服务",
        healthy: "健康",
        unhealthy: "异常",
        error: "错误",
        unknown: "未知",
        allOk: "全部正常",
        issues: "存在问题",
        overallStatus: "整体状态",
        latency: "延迟",
        openService: "打开服务",
      },

      // Cache Performance
      cache: {
        title: "缓存性能",
        hitRate: "命中率",
        hits: "命中",
        misses: "未命中",
        avgLatency: "平均延迟",
        totalRequests: "总请求数",
        successfulRetrievals: "成功缓存检索",
        fetchFailures: "缓存获取失败",
        avgResponseTime: "平均响应时间",
        unavailable: "缓存数据不可用",
      },

      // Charts
      charts: {
        cacheHitRate: "缓存命中率",
        hitRateLabel: "命中率",
        avgLatency: "平均延迟",
        total: "总计",
        cacheLatency: "缓存",
        backend: "后端",
        requestDistribution: "请求分布",
        noData: "暂无数据",
      },

      // Cache Details
      cacheDetails: {
        title: "缓存性能",
        totalRequests: "总请求数",
        cachedKeys: "缓存键数",
        cacheSize: "缓存大小",
        errors: "错误数",
        redisConnection: "Redis 连接",
        teiBackend: "TEI 后端",
        connected: "已连接",
        disconnected: "已断开",
        healthy: "健康",
        unhealthy: "异常",
        clear: "清除",
        clearConfirm: "清除所有缓存的嵌入向量？此操作不可撤销。",
        clearSuccess: "已清除 {count} 条缓存嵌入向量",
        clearError: "清除缓存失败",
      },

      // Refresh Control
      refresh: {
        autoRefresh: "自动刷新",
        interval: "刷新间隔",
        manual: "手动",
        refreshButton: "刷新",
        refreshing: "刷新中...",
      },

      // Errors
      errors: {
        loadFailed: "加载监控数据失败",
        tryAgain: "重试",
      },
    },

    // Classification Analytics
    classificationAnalytics: {
      pageTitle: "分类分析",
      pageDescription: "Skill 匹配和 Response Type 分类性能分析",
      lastUpdated: "最后更新",

      tabs: {
        skillExecution: "Skill 执行",
        classification: "Response 分类",
      },

      timeRange: {
        label: "时间范围",
        last24h: "最近 24 小时",
        last7d: "最近 7 天",
        last30d: "最近 30 天",
      },

      skillExecution: {
        title: "Skill 执行分析",
        stats: {
          totalExecutions: "总执行次数",
          activeSkills: "活跃 Skill 数",
          autoMatchRate: "自动匹配率",
          ruleMatchRate: "规则匹配率",
        },
        charts: {
          matchLayer: "匹配层分布",
          matchSource: "匹配来源分布",
          topSkills: "Top 10 Skills",
        },
        recentExecutions: "最近执行记录",
        noData: "暂无数据",
        skillId: "Skill ID",
        query: "用户查询",
        matchSource: "匹配来源",
        matchedRule: "匹配规则",
        confidence: "置信度",
        time: "时间",
      },

      classification: {
        title: "Response 分类分析",
        stats: {
          totalClassifications: "总分类次数",
          quickResponseRate: "Quick Response 占比",
          avgQueryLength: "平均查询长度",
          avgLatency: "平均延迟",
          // Skill metrics
          ruleMatchRate: "规则匹配率",
          avgRuleMatchTime: "平均规则匹配耗时",
          avgLlmMatchTime: "平均 LLM 匹配耗时",
          avgPromptInjection: "平均注入字符数",
        },
        charts: {
          responseType: "Response Type 分布",
          avgConfidence: "平均置信度对比",
          methodPerformance: "分类方法性能",
          count: "数量",
          latency: "延迟 (ms)",
          // Skill charts
          skillMatchLayer: "Skill 匹配层分布",
          topMatchedSkills: "Top 匹配 Skill",
        },
        recentClassifications: "最近分类记录",
        noData: "暂无数据",
        responseType: "Response Type",
        method: "分类方法",
        latencyMs: "延迟 (ms)",
      },

      export: {
        button: "导出数据",
        csv: "导出 CSV",
        json: "导出 JSON",
      },
      refresh: "刷新",
      loading: "加载中...",
      error: "加载失败",
      quick: "Quick",
      deep: "Deep",
    },

    // Users Management
    users: {
      pageTitle: "用户管理",
      pageDescription: "管理用户和 Beta 测试人员",
      refresh: "刷新",
      loading: "正在加载用户...",
      error: "加载用户失败",

      // Stats
      stats: {
        totalUsers: "总用户数",
        admins: "管理员",
        betaTesters: "Beta 测试人员",
        roleDistribution: "角色分布",
      },

      // Filters
      filters: {
        searchPlaceholder: "搜索用户名、昵称、邮箱或真实姓名...",
        allUsers: "全部用户",
        betaTesters: "Beta 测试人员",
        nonBeta: "非 Beta",
      },

      // Batch Actions
      batch: {
        selected: "已选择 {count} 个用户",
        enableBeta: "启用 Beta",
        disableBeta: "禁用 Beta",
        clearSelection: "清除选择",
      },

      // Table
      table: {
        user: "用户",
        contact: "联系方式",
        role: "角色",
        betaTester: "Beta 测试",
        created: "创建时间",
        actions: "操作",
      },

      // Roles
      roles: {
        free: "免费",
        vip: "VIP",
        premium: "高级",
        admin: "管理员",
      },

      // Actions
      actions: {
        makeBeta: "设为 Beta",
        removeBeta: "移除 Beta",
        makeAdmin: "设为管理员",
        revokeAdmin: "撤销管理员",
        resources: "资源",
        viewResources: "查看资源",
      },

      // Status
      status: {
        beta: "Beta",
        notBeta: "否",
      },

      // Pagination
      pagination: {
        showing: "显示 {start} 至 {end}，共 {total} 个用户",
        page: "第 {current} 页，共 {total} 页",
      },
    },

    // System Settings
    systemSettings: {
      pageTitle: "系统设置",
      pageDescription: "配置系统级设置和集成",

      tabs: {
        mcpServers: "MCP 服务",
        mem0Config: "记忆配置",
        firstMessage: "首消息注入",
        artifactTypes: "产物类型",
        toolEnhancement: "工具展示",
        toolCombo: "工具组合",
        promptTemplates: "提示词模版",
        outputSchemas: "输出格式",
        exportImport: "导入导出",
        auditLogs: "变更记录",
      },

      audit: {
        viewLogsFor: "查看审计日志：",
        mcpServersLog: "MCP 服务器审计日志",
        mem0ConfigLog: "Mem0 配置审计日志",
        firstMessageLog: "首条消息配置审计日志",
        artifactTypesLog: "产物类型配置审计日志",
        outputSchemasLog: "输出格式配置审计日志",
        toolEnhancementLog: "工具增强配置审计日志",
        toolComboLog: "工具组合配置审计日志",
        promptTemplatesLog: "提示词模版配置审计日志",
      },

      // MCP Servers Panel
      mcp: {
        title: "MCP 服务器",
        description: "管理用于工具集成的 Model Context Protocol 服务器",
        warningBanner:
          '警告：对 MCP 服务器配置的更改将影响所有用户。保存后，点击"重载管理器"立即应用更改。',
        editJson: "编辑 JSON",
        reloadManager: "重载管理器",
        addServer: "添加服务器",
        noServers: "暂无 MCP 服务器配置",
        addFirstServer: "添加您的第一个服务器",
        aboutMcp: "关于 MCP 服务器",
        aboutMcpItems: [
          "STDIO 服务器作为本地进程运行（例如 Python 脚本）",
          "HTTP 服务器连接到远程端点",
          "测试连接以验证服务器可用性",
          "更改后需要重载管理器才能生效",
        ],
        version: "版本",
        lastUpdatedBy: "最后更新者",
        jsonEditorTitle: "编辑 MCP 配置 (JSON)",
        jsonEditorHint:
          "直接编辑 JSON。每个服务器需要：command/url、transport 类型、status。",
        cancel: "取消",
        saving: "保存中...",
        saveConfiguration: "保存配置",
        loading: "正在加载 MCP 服务器...",
        loadFailed: "加载 MCP 服务器失败",
        saveFailed: "保存 MCP 服务器失败",
        deleteFailed: "删除 MCP 服务器失败",
        reloadSuccess: "MCP 管理器重载成功",
        reloadFailed: "重载 MCP 管理器失败",
        jsonSaveSuccess: "MCP 配置保存成功",
        jsonMustHaveMcpServers: 'JSON 必须包含 "mcpServers" 对象',
      },

      // Mem0 Config Panel
      mem0: {
        title: "Mem0 系统默认提示词",
        description:
          "配置用于记忆提取和管理的默认提示词（Agent 可以覆盖这些设置）",
        warningBanner:
          "警告：对系统默认提示词的更改将影响未启用自定义提示词的 Agent。已启用自定义提示词的 Agent 将继续使用自己的配置。",
        helpTitle: "Mem0 双记忆架构",
        helpDescription:
          "配置用户和 Agent 记忆提取的系统级默认提示词和类别。每个 Agent 可以选择使用这些默认值或用自定义值覆盖。",
        helpItems: [
          "用户记忆提取：从用户消息中提取事实",
          "Agent 记忆提取：从助手消息中提取事实",
          "记忆更新：将新事实与现有记忆合并",
          "类别：将记忆分类到结构化类别",
        ],
        documentation: "Mem0 文档",
        reloadFromDatabase: "从数据库重新加载",
        saving: "保存中...",
        saveSystemDefaults: "保存系统默认值",
        userMemoryPrompt: "用户记忆提取提示词",
        agentMemoryPrompt: "Agent 记忆提取提示词",
        memoryUpdatePrompt: "记忆更新提示词",
        userMemoryCategories: "用户记忆类别 (JSON)",
        agentMemoryCategories: "Agent 记忆类别 (JSON)",
        resetToHardcoded: "重置为硬编码默认值",
        promptMinMax:
          "用于从消息中提取记忆的提示词。最少 50 字符，最多 8000 字符。",
        promptUpdateMinMax:
          "用于更新/合并记忆的提示词。最少 50 字符，最多 4000 字符。",
        categoriesHint:
          "用于分类记忆的类别。每个类别需要：description、temporal。",
        loading: "正在加载 Mem0 配置...",
        loadFailed: "加载 Mem0 配置失败",
        saveSuccess: "Mem0 系统默认值保存成功",
        saveFailed: "保存配置失败",
        resetSuccess: "已重置为硬编码默认值",
        resetFailed: "重置失败",
        noDefaultPrompt: "API 未返回默认提示词",
        noDefaultCategories: "API 未返回默认类别",
        version: "版本",
        lastUpdatedBy: "最后更新者",
        validation: {
          userPromptMin: "用户记忆提取提示词必须至少 50 字符",
          agentPromptMin: "Agent 记忆提取提示词必须至少 50 字符",
          updatePromptMin: "记忆更新提示词必须至少 50 字符",
          userPromptMax: "用户记忆提取提示词不能超过 8000 字符",
          agentPromptMax: "Agent 记忆提取提示词不能超过 8000 字符",
          updatePromptMax: "记忆更新提示词不能超过 4000 字符",
        },
      },

      // First Message Panel
      firstMessage: {
        title: "首条消息系统默认值",
        description: "配置在新话题首条消息时注入的默认指令",
        warningBanner:
          '警告：对系统默认指令的更改将影响使用"系统"模式的 Agent。已设置自定义首条消息指令的 Agent 不受影响。',
        reloadFromDatabase: "从数据库重新加载",
        saving: "保存中...",
        saveSystemDefault: "保存系统默认值",
        instructionTitle: "系统默认指令",
        instructionPlaceholder: "输入系统默认首条消息指令...",
        instructionLength: "指令长度",
        instructionMinMax: "最少：10，最多：5000 字符",
        howItWorksTitle: "工作原理",
        howItWorksItems: [
          "此指令在新话题的首条消息时注入到系统提示词中",
          "Agent 可以选择使用此系统默认值或定义自己的自定义指令",
          "Agent 级别设置：enable_first_message_instruction 控制此功能是否启用",
          'Agent 级别设置：first_message_mode 控制"系统"与"自定义"模式',
        ],
        loading: "正在加载首条消息配置...",
        loadFailed: "加载首条消息配置失败",
        saveSuccess: "首条消息指令保存成功",
        saveFailed: "保存配置失败",
        version: "版本",
        lastUpdatedBy: "最后更新者",
        validation: {
          minLength: "首条消息指令必须至少 10 字符",
          maxLength: "首条消息指令不能超过 5000 字符",
        },
      },

      // Artifact Types Panel
      artifactTypes: {
        title: "产物类型",
        description: "配置用户生成内容的产物类型（幸运符、播客、报告等）",
        addType: "添加类型",
        noTypes: "暂无产物类型配置",
        addFirstType: "添加您的第一个产物类型",
        editType: "编辑产物类型",
        addTypeTitle: "添加产物类型",
        typeId: "类型 ID",
        typeIdHint: "小写字母、数字、下划线。必须以字母开头。",
        icon: "图标",
        iconOptions: {
          package: "包裹",
          sparkles: "闪光",
          audio: "音频",
          document: "文档",
          image: "图像",
          calendar: "日历",
        },
        nameEnglish: "名称（英文）",
        nameChinese: "名称（中文）",
        descriptionLabel: "描述",
        descriptionHint: "简短描述此产物类型",
        contentTypes: "内容类型",
        contentTypesHint: "image/png, image/jpeg, audio/mpeg",
        rotationPeriod: "轮换周期",
        rotationOptions: {
          none: "无（永久）",
          daily: "每日",
          weekly: "每周",
          monthly: "每月",
        },
        maxStored: "最大存储数",
        maxStoredHint: "例如：7（留空表示无限制）",
        cancel: "取消",
        saving: "保存中...",
        save: "保存",
        confirmDelete: "删除产物类型",
        loading: "正在加载产物类型...",
        loadFailed: "加载产物类型失败",
        saveFailed: "保存产物类型失败",
        deleteFailed: "删除产物类型失败",
        version: "版本",
        lastUpdatedBy: "最后更新者",
        aboutTitle: "关于产物类型",
        aboutItems: [
          "产物类型定义用户生成内容的类别（幸运符、播客、报告）",
          '轮换周期决定产物何时过期以及新产物何时变为"当前"',
          "最大存储数限制每个用户保留的产物数量（最旧的将被标记为过期）",
          "产物是用户级别的，每个用户都有自己的产物",
        ],
        apiAccessTitle: "API 访问（应用集成）",
        workflowTitle: "工作流集成（创建产物）",
        fieldRefTitle: "字段参考",
        adminOpsTitle: "管理员操作（清理和管理）",
      },

      // Audit Log Viewer
      auditLog: {
        loading: "正在加载审计日志...",
        loadFailed: "加载审计日志失败",
        noLogs: "暂无审计日志",
        noLogsHint: "对此配置的更改将记录在这里",
        showing: "显示 {current} / {total} 条记录",
        version: "版本",
        reason: "原因",
        ip: "IP",
      },

      // Export/Import Panel
      exportImport: {
        exportTitle: "导出配置",
        exportDescription:
          "下载所有 Agent 配置为 JSON 文件。包括每个 Agent 的所有设置、提示词和工具配置。",
        exporting: "导出中...",
        exportAll: "导出所有配置",
        exportSuccess: "Agent 配置导出成功",
        exportFailed: "导出失败",
        importTitle: "导入配置",
        importDescription:
          "从 JSON 文件导入 Agent 配置。您可以选择如何处理现有配置。",
        selectFile: "选择文件",
        importMode: "导入模式",
        importModeOptions: {
          createOnly: "仅创建 - 跳过已存在的 Agent",
          overwrite: "覆盖 - 完全替换已存在的 Agent",
          merge: "合并 - 更新已存在的 Agent，保留未指定的字段",
        },
        importModeHints: {
          createOnly: "仅创建新 Agent。已存在的 Agent 将被跳过。",
          overwrite: "用导入的数据完全替换已存在的 Agent。",
          merge: "更新指定的字段，未指定的字段保留现有值。",
        },
        skipErrors: "跳过错误并继续导入其余配置",
        skipErrorsHint: "如果不勾选，导入将在第一个错误处停止。",
        importPreview: "导入预览",
        foundConfigs: "找到 {count} 个 Agent 配置：",
        importResults: "导入结果",
        imported: "已导入",
        skipped: "已跳过",
        errors: "错误",
        importing: "导入中...",
        importConfigurations: "导入配置",
        aboutTitle: "关于导入/导出",
        aboutItems: [
          "导出创建包含所有 Agent 配置的 JSON 文件",
          "导入文件必须包含 configurations 数组",
          "每个配置至少需要一个 agent_type",
          "用于备份、迁移或在环境间共享配置",
        ],
        noFileSelected: "未选择文件",
        invalidJson: "无效的 JSON 文件",
        invalidFormat: "无效格式：缺少 configurations 数组",
        importCompleted: "导入完成",
        importFailed: "导入失败",
      },

      // Tool Enhancement Panel
      toolEnhancement: {
        title: "工具增强",
        description: "配置工具的显示名称和描述",
        loading: "加载中...",
        loadFailed: "加载失败",
        saveSuccess: "保存成功",
        saveFailed: "保存失败",
        save: "保存",
        saving: "保存中...",
        reload: "重新加载",
        searchPlaceholder: "搜索工具...",
        noToolSelected: "从左侧面板选择一个工具进行配置",
        configured: "已配置",
        notConfigured: "未配置",
        displayNameCn: "显示名称（中文）",
        displayNameEn: "显示名称（英文）",
        descriptionCn: "描述（中文）",
        descriptionEn: "描述（英文）",
        icon: "图标",
        displayOrder: "显示顺序",
        enabled: "启用",
        clearConfig: "清除配置",
        toolInfo: "工具信息",
        technicalName: "技术名称",
        originalDescription: "原始描述",
        allCategories: "所有分类",
        orphanConfigs: "孤立配置",
        orphanConfigsDesc: "已不存在的工具的配置",
        cleanupOrphans: "清理",
        orphanWarning: "此工具已不存在于系统中",
        category: "分类",
        categoryOverride: "分类覆盖",
        categoryOverrideHint: "留空则使用默认分类",
        originalCategory: "默认分类",
        useDefaultCategory: "使用默认",
        toolKey: "工具键",
        // Category translations
        categories: {
          search: "搜索",
          math: "数学",
          development: "开发",
          filesystem: "文件系统",
          reasoning: "推理",
          astrology: "占星",
          fortune: "运势",
          memory: "记忆",
          utility: "工具",
          general: "通用",
        } as Record<string, string>,
        // AI Generation
        aiGenerate: "AI 生成描述",
        aiGenerateDesc: "分析原始描述并自动生成用户友好的显示名称和描述。",
        aiGenerateButton: "生成",
        aiGenerating: "生成中...",
        aiSelectModel: "选择模型",
        aiSelectModelPlaceholder: "选择一个模型...",
        aiExplanation: "AI 分析",
        aiNoDescription: "没有可用于 AI 生成的原始描述",
        // Reload Enhancer
        reloadEnhancer: "应用到 LLM",
        reloadEnhancerDesc: "将保存的配置应用到 LLM 工具描述",
        reloadEnhancerSuccess: "工具描述已重新加载",
        reloadEnhancerFailed: "重新加载失败",
        // Tool Testing
        testTool: "测试工具",
        testToolDesc: "使用参数直接调用工具进行调试",
        testParameters: "测试参数",
        testParametersPlaceholder: "输入 JSON 格式的参数...",
        runTest: "执行测试",
        running: "执行中...",
        testResult: "测试结果",
        testSuccess: "执行成功",
        testFailed: "执行失败",
        executionTime: "执行时间",
        errorType: "错误类型",
        closeModal: "关闭",
        noParameters: "此工具无需参数",
        invalidJson: "无效的 JSON 格式",
        requiredFields: "必填字段",
        optionalFields: "可选字段",
        paramType: "类型",
        paramDefault: "默认值",
        requiresUserContext: "需要用户上下文",
        requiresUserContextDesc:
          "此工具需要用户上下文，将使用当前管理员用户ID进行测试",
        formMode: "表单模式",
        jsonMode: "JSON 模式",
        useDefaultValue: "使用默认值",
      },

      // Prompt Templates Panel
      promptTemplates: {
        title: "提示词模版",
        description: "配置用于图像生成、推送通知、工作流等功能的系统提示词模版",
        addTemplate: "添加模版",
        noTemplates: "暂无提示词模版配置",
        addFirstTemplate: "添加您的第一个提示词模版",
        editTemplate: "编辑模版",
        addTemplateTitle: "添加新模版",
        templateKey: "模版键",
        templateKeyHint: "格式: domain.operation (例如: image.generate)",
        nameEnglish: "英文名称",
        nameChinese: "中文名称",
        descriptionEn: "英文描述",
        descriptionZh: "中文描述",
        systemPromptZh: "中文系统提示词",
        systemPromptEn: "英文系统提示词",
        displayOrder: "显示顺序",
        category: "分类",
        categoryOptions: {
          image: "图像",
          push: "推送",
          workflow: "工作流",
        },
        isDefault: "默认模版",
        resetToDefault: "重置为默认",
        resetSuccess: "已重置为默认值",
        cancel: "取消",
        saving: "保存中...",
        save: "保存",
        confirmDelete: "确定要删除这个模版吗？",
        cannotDeleteDefault: "无法删除默认模版。请使用重置功能恢复默认值。",
        loading: "加载中...",
        loadFailed: "加载模版失败",
        saveFailed: "保存模版失败",
        deleteFailed: "删除模版失败",
        version: "版本",
        lastUpdatedBy: "最后更新者",
        searchPlaceholder: "搜索模版...",
        allCategories: "全部分类",
        previewPrompt: "预览提示词",
        chinese: "中文",
        english: "英文",
      },

      // Output Schemas Panel
      outputSchemas: {
        title: "输出格式",
        description: "管理集中式 JSON 输出格式定义，用于 Agent 响应",
        addSchema: "添加格式",
        noSchemas: "暂无输出格式配置",
        noSchemasMatch: "没有匹配的输出格式",
        addFirstSchema: "添加您的第一个输出格式",
        editSchema: "编辑输出格式",
        searchPlaceholder: "搜索格式...",
        scopeAll: "全部范围",
        scopeGlobal: "仅全局",
        scopeAgent: "仅特定 Agent",
        statusAll: "全部状态",
        statusEnabled: "已启用",
        statusDisabled: "已禁用",
        schemaKey: "格式标识",
        schemaKeyHint: "小写字母、数字、下划线。必须以字母开头。",
        nameEnglish: "名称（英文）",
        nameChinese: "名称（中文）",
        descriptionEn: "描述（英文）",
        descriptionZh: "描述（中文）",
        scope: "适用范围",
        agentType: "Agent 类型",
        jsonSchema: "JSON Schema",
        exampleOutput: "示例输出",
        formatInstruction: "格式说明",
        formatInstructionHint: "使用 {schema} 作为 JSON Schema 的占位符",
        displayOrder: "显示顺序",
        enabled: "启用",
        loading: "正在加载输出格式...",
        loadFailed: "加载输出格式失败",
        saveFailed: "保存输出格式失败",
        deleteFailed: "删除输出格式失败",
        toggleFailed: "切换状态失败",
        totalSchemas: "总格式数",
        showing: "显示",
        aboutTitle: "关于输出格式",
        aboutItem1: "全局格式对所有 Agent 可用",
        aboutItem2: "特定 Agent 格式仅对指定的 Agent 类型可用",
        aboutItem3:
          "在 format_instruction 中使用 {schema} 占位符注入 JSON Schema",
        aboutItem4: "禁用的格式在 Agent 选择下拉框中隐藏",
      },
    },

    // Agent Config
    agentConfig: {
      // List Page
      listTitle: "Agent 配置",
      listDescription: "管理 AI Agent 配置和设置",
      createAgent: "创建 Agent",
      searchPlaceholder: "搜索 Agent...",
      showInactive: "显示未激活的 Agent",
      noAgentsMatch: "没有匹配的 Agent",
      noAgentsMatchHint: "尝试调整搜索条件",
      noAgentsYet: "暂无 Agent 配置",
      noAgentsYetHint: "创建您的第一个 Agent 开始使用",
      showingAgents: "显示 {current} / {total} 个 Agent",
      activeCount: "{count} 个已激活",
      inactive: "未激活",
      version: "版本",
      updated: "更新时间",

      // Detail Page
      loadingConfig: "加载 Agent 配置...",
      backToList: "返回列表",
      agentNotFound: "未找到 Agent",
      copy: "复制",
      unsavedChanges: "有未保存的更改",
      saving: "保存中...",
      saveSuccess: "配置已保存成功！",
      saveFailed: "保存失败",
      active: "已激活",
      updatedViaAdmin: "通过管理界面更新",

      // Tabs
      tabSettings: "设置",
      tabHooks: "功能扩展",
      tabTools: "工具",
      tabPrompt: "提示词",
      tabSchemas: "Schemas",
      tabSkills: "技能",
      tabJson: "JSON",
      tabVersions: "版本历史",
      // New tabs
      tabBasic: "基础",
      tabMemory: "记忆",
      tabKnowledge: "知识库",
      tabAstrology: "占星增强",
      tabEmotion: "情绪感知",
      tabClassifier: "分类器",
      tabTeam: "团队",
      tabAdvanced: "高级",

      // Actions
      actions: {
        browseTools: "浏览工具",
      },

      // Output Format Section
      outputFormat: {
        title: "输出格式",
        createSchema: "创建 Schema",
        manageSchemas: "管理 Schemas",
        noSchemasWarning:
          "尚未定义输出 Schema。创建一个 Schema 以启用结构化 JSON 输出。",
        createFirstSchema: "创建第一个 Schema",
        noDefault: "无格式约束 (自由输出)",
        defaultSchemaHelp:
          "未在 API 请求中指定 output_schema_key 时使用的默认 Schema。",
        availableSchemas: "可用的 Schema Keys (用于 API)",
        apiHint:
          "在 API 请求中传递 output_schema_key 参数来使用特定的 Schema。",
      },

      // Create/Copy Agent Dialog
      createAgentDialog: {
        copyAgent: "复制 Agent",
        createNewAgent: "创建新 Agent",
        agentType: "Agent 类型",
        agentTypePlaceholder: "例如：my_agent",
        agentTypeHint: "唯一标识符。仅支持小写字母、数字、下划线和连字符。",
        name: "名称",
        namePlaceholder: "例如：我的 Agent",
        description: "描述",
        descriptionPlaceholder: "描述这个 Agent 的用途...",
        copyingFrom: "复制配置自",
        creating: "创建中...",
        agentTypeRequired: "Agent 类型为必填项",
        nameRequired: "名称为必填项",
        agentTypeInvalid:
          "Agent 类型必须以字母开头，仅包含小写字母、数字、下划线和连字符",
      },

      // Version History
      versionHistory: {
        title: "版本历史",
        loading: "加载版本历史...",
        current: "当前",
        restore: "恢复",
        noHistory: "暂无版本历史",
        createdBy: "创建者",
        versionConfig: "版本 {version} 配置",
        confirmRestore: "确定要恢复到版本 {version} 吗？这将替换当前配置。",
        restoreReason: "通过管理界面恢复",
        editing: "编辑中",
        clickToShowJson: "点击眼睛图标展开JSON配置",
        viewConfig: "查看配置",
      },

      // JSON Preview
      jsonPreview: {
        title: "JSON 配置",
        copy: "复制",
        copied: "已复制",
        download: "下载",
      },

      // Tools Browser Modal
      toolsBrowser: {
        customTools: "自定义工具",
        builtinToolkits: "内置工具包",
        customToolsDesc: "从可用分类中选择单个工具",
        builtinToolkitsDesc: "选择内置工具模块",
        filterByCategory: "按分类筛选：",
        allCategories: "全部分类",
        toolsSelected: "已选择 {count} 个工具",
        loadingTools: "加载工具中...",
        availableTools: "可用工具",
        selectedTools: "已选工具",
        previewConfig: "预览配置",
        cancel: "取消",
        apply: "应用",
        noBuiltinToolkits: "没有可用的内置工具包",
        noCustomTools: "没有可用的自定义工具",
        noToolsSelected: "未选择任何工具",
        toolsCount: "{count} 个工具",
        builtinToolsBrowser: "内置工具浏览器",
        customToolsBrowser: "自定义工具浏览器",
        format: "格式化",
        noItemsConfigured: "未配置任何项目",
        toolkits: "工具包",
        enabledTools: "已启用工具",
        servers: "服务器",
        modules: "模块",
        allToolsEnabled: "(启用全部工具)",
        toolNotFound: "工具不存在，请移除",
        serverNotFound: "服务器不存在，请移除",
        willMigrateOnSave: "保存时将自动迁移",
        toolNotFoundRemove: "工具不存在，请移除",
        invalidToolsWarning: "部分已选工具已不存在，请移除后再应用。",
        removeInvalidToolsFirst: "请先移除无效工具",
      },

      // MCP Tools Browser Modal
      mcpBrowser: {
        title: "MCP 工具浏览器",
        refresh: "刷新",
        systemServers: "系统服务器",
        userServers: "用户服务器",
        serversSelected: "{count} 个服务器已选择",
        toolsSelected: "{count} 个工具已选择",
        loadingServers: "加载 MCP 服务器中...",
        availableServers: "可用服务器",
        selectedItems: "已选项目",
        servers: "服务器",
        individualTools: "单个工具",
        noServersSelected: "未选择服务器",
        noServersAvailable: "没有可用的 MCP 服务器",
        connected: "已连接",
        disconnected: "已断开",
        failedToConnect: "连接此服务器失败",
        previewConfig: "预览配置",
        cancel: "取消",
        apply: "应用",
        toolsCount: "{count} 个工具",
        invalidItemsWarning: "部分已选服务器/工具已不存在，请移除后再应用。",
        removeInvalidItemsFirst: "请先移除无效项目",
      },

      // Prompt Template Preview
      promptPreview: {
        title: "系统提示词预览",
        buttonLabel: "预览提示词",
        copy: "复制",
        copied: "已复制！",
        loading: "加载中...",
        refresh: "刷新",
        generatePreview: "生成预览",
        usingTemplate: "使用模板：",
        default: "(默认)",
        enabledSections: "{count} 个已启用的部分",
        useRealData: "使用真实用户数据",
        livePreview: "实时预览",
        userId: "用户 ID",
        enterUserId: "输入用户 ID...",
        useCurrent: "使用当前用户",
        usingCurrentUser: "使用当前登录用户",
        sampleMessage: "示例用户消息（触发记忆搜索）",
        enterSampleMessage: "输入示例用户消息...",
        realDataHint:
          "使用真实数据运行所有 Hooks：user_facts、birth_chart、memory_context",
        sampleDataHint: "预览使用生成的测试用户和示例数据。",
        previewFailed: "预览失败",
        template: "模板：",
        systemChars: "系统：{count} 字符",
        userChars: "用户：{count} 字符",
        user: "用户：",
        hooksExecuted: "已执行 Hooks ({count})",
        systemStage: "SYSTEM 阶段：",
        userStage: "USER 阶段：",
        noHooksExecuted: "没有执行的 Hooks",
        dataSources: "数据源 ({count})",
        dataSourceHint: "real = 从数据库加载 | empty = 未找到数据",
        systemPrompt: "系统提示词：",
        enhancedUserMessage: "增强的用户消息 ({count} 字符)",
        contextItems: "上下文项 ({count})：",
        emptyStateTitle: '点击"生成预览"查看系统提示词的渲染效果',
        emptyStateDesc:
          "实时预览会运行所有 Hooks 使用真实数据，构建发送给 LLM 的完整提示词",
      },

      // Output Schemas Tab
      outputSchemas: {
        title: "Schemas",
        add: "添加",
        noSchemas: "暂无 Schema 定义",
        noSchemasHint: '点击"添加"创建您的第一个输出 Schema',
        default: "默认",
        properties: "{count} 个属性",
        noSelection: "未选择 Schema",
        noSelectionHint: "从列表中选择一个 Schema 或创建新的",
      },

      // Skills Tab
      skills: {
        title: "技能",
        add: "添加技能",
        noSkills: "暂无技能配置",
        noSkillsHint: "添加技能以启用动态指令注入",
        noSelection: "未选择技能",
        noSelectionHint: "从列表中选择一个技能或添加新的",
        enabled: "已启用",
        disabled: "已禁用",
        priority: "优先级",
        exclusionGroup: "互斥组",
        disabledWarning:
          '技能功能已禁用。请在分类器设置中启用"skills_enabled"以使用技能功能。',
      },

      // Schema Editor
      schemaEditor: {
        aiGenerate: "AI 智能生成 Schema",
        aiGenerateDesc:
          "输入期望的 JSON 输出示例，AI 会自动分析并生成对应的 JSON Schema",
        selectModel: "选择模型",
        selectModelPlaceholder: "-- 请选择模型 --",
        inputExample: "输入 JSON 示例",
        generating: "生成中...",
        generate: "生成 Schema",
        aiExplanation: "AI 设计说明",
        key: "Key",
        keyValidHint:
          "用于 API 调用的唯一标识符。仅支持小写字母、数字和下划线。",
        keyInvalidHint: "必须以字母开头，且仅包含小写字母、数字和下划线。",
        name: "名称",
        namePlaceholder: "Schema 名称",
        description: "描述",
        descriptionPlaceholder: "简要描述此 Schema",
        jsonSchema: "JSON Schema",
        validJson: "JSON 有效",
        jsonSchemaHint: "使用 JSON Schema 格式定义期望的输出结构",
        exampleOutput: "示例输出（可选）",
        exampleOutputHint: "提供一个示例输出供参考",
        formatInstruction: "格式化指令",
        formatInstructionHint: "使用 {schema} 占位符在运行时注入 JSON Schema",
        defaultSchema: "默认 Schema",
        setAsDefault: "设为默认",
        deleteSchema: "删除 Schema",
        confirmDelete: '确定要删除 "{name}" 吗？',
        alertExampleRequired: "请提供 JSON 示例",
        alertModelRequired: "请先选择模型",
        alertGenerateFailed: "AI 生成失败：",
      },

      // Template Selector
      templateSelector: {
        title: "Prompt 模板",
        description: "选择一个模板来定义此 Agent 的系统提示词结构",
        useDefault: "使用默认",
        default: "默认",
        recommended: "推荐",
        systemDefault: "系统默认",
        sections: "模板分段",
        sectionsCount: "{count} 个分段",
        updated: "更新于",
        noTemplates: "暂无可用模板",
        loadingTemplates: "加载模板中...",
        previewTemplate: "预览模板",
      },
    },

    // Hooks (功能扩展)
    hooks: {
      pipelineTitle: "扩展管道",
      loading: "加载功能扩展中...",
      errorLoading: "加载扩展出错",
      noData: "暂无扩展数据",
      enabledCount: "{enabled} / {total} 已启用",
      showHidden: "显示已关闭 ({count})",
      hideHidden: "隐藏已关闭",

      stages: {
        system: "系统阶段",
        user: "用户阶段",
        final: "结束阶段",
      },
      hooksCount: "{count} 个扩展",

      selectHook: "选择一个扩展查看详情",
      description: "描述",
      noDescription: "暂无描述",
      stage: "阶段",
      category: "分类",
      scope: "作用域",
      sortOrder: "排序",
      dragToReorder: "拖拽以重新排序",
      configInAgentConfig: "扩展参数配置在 Agent 设置对应的扩展设置中管理",

      hookDefinitions: {
        // ═══ SYSTEM 阶段 ═══
        user_facts: {
          displayName: "用户事实",
          description: "加载用户的永久偏好、信息和信念（如：喜欢简洁回答、是程序员）",
          requires: "记忆系统 (memory.enabled)",
          note: "注入到 System Prompt，位于最前端以优化 KV Cache",
        },
        datetime_context: {
          displayName: "日期时间",
          description: "注入当前日期时间上下文，让 Agent 知道今天是几月几号星期几",
        },
        astrology_plus: {
          displayName: "占星增强",
          description: "加载用户出生星盘数据和当前行运信息",
          note: "需要用户已设置出生信息",
        },
        first_message: {
          displayName: "首条消息",
          description: "检测是否为新会话的首条消息，用于触发欢迎语或特殊处理",
        },
        classifier: {
          displayName: "响应分类",
          description: "预分类用户意图，决定使用快速响应还是深度响应模式",
          note: "影响后续模型选择和处理流程",
        },
        skill_dedup: {
          displayName: "技能去重",
          description: "检查当前对话是否重复调用相同技能，避免重复执行",
          requires: "依赖 classifier 的分类结果",
        },

        // ═══ USER 阶段 ═══
        emotion_enhance: {
          displayName: "情绪感知",
          description: "分析用户消息的情绪状态和意图，用于调整回复风格",
        },
        memory_context: {
          displayName: "记忆检索",
          description: "根据用户消息搜索相关的用户事件和 Agent 交互记忆",
          requires: "记忆系统 (memory.enabled)",
          note: "检索结果注入到 System Prompt 供 Agent 参考",
        },
        knowledge_context: {
          displayName: "知识检索",
          description: "从知识库中检索与用户问题相关的文档片段",
          requires: "知识库 (knowledge_enabled)",
        },
        memory_reinforce: {
          displayName: "记忆强化",
          description: "基于艾宾浩斯遗忘曲线，强化被访问的记忆以延缓遗忘",
          requires: "依赖 memory_context 的检索结果",
          note: "被检索到的记忆会增加强度，长期不访问的记忆会逐渐衰减",
        },

        // ═══ FINAL 阶段 ═══
        memory_save: {
          displayName: "记忆保存",
          description: "对话结束后，从对话中提取并保存用户记忆和 Agent 交互记忆",
          requires: "记忆系统 (memory.enabled)",
          note: "双提取模式：分别提取用户事实和 Agent 承诺/建议",
        },
        skill_tracking: {
          displayName: "技能追踪",
          description: "记录本次对话中执行的技能，用于统计和去重",
        },
      },
    },
    imageWorkflows: {
      executionPanel: {
        title: "执行",
        execution: "执行",
        progress: "进度",
        nodes: "节点",
        failed: "失败",
        duration: "耗时",
        error: "错误",
        clearAndClose: "清除并关闭",
        nodeDetail: "节点详情",
      },
      executionHistory: {
        title: "执行历史",
        tab: "历史",
        executionLabel: "执行",
        nodes: "节点",
        saveFirst: "请先保存工作流以查看执行历史",
        noHistory: "暂无执行历史",
        runFirst: "运行工作流后可查看执行历史",
      },
      nodeDetail: {
        selectNode: "点击节点查看详情",
        noStepData: "该节点无执行数据",
        status: "状态",
        duration: "耗时",
        order: "执行顺序",
        startedAt: "开始时间",
        completedAt: "完成时间",
      },
      stepResult: {
        error: "错误",
        variables: "变量",
        noVariables: "无变量",
        mode: "模式",
        generatedPrompt: "生成的提示词",
        showLess: "收起",
        showMore: "展开",
        noPrompt: "未生成提示词",
        model: "模型",
        output: "输出",
        imageLoadError: "图片加载失败",
        noImage: "未生成图片",
        storage: "存储",
        saved: "已保存到图库",
        temporary: "临时",
        outputPending: "输出待处理",
        noOutput: "无输出数据",
      },
      outputNode: {
        noArtifact: "不保存为产物 (临时文件)",
      },
    },

    // Resources
    resources: {
      pageTitle: "用户资源",
      tabs: {
        artifacts: "产物",
        library: "图库",
        temp: "临时文件",
      },
      types: {
        artifact: "产物",
        library: "图库",
        temp: "临时文件",
      },
      stats: {
        totalFiles: "文件总数",
        totalSize: "总大小",
        uniqueUsers: "用户数",
        byType: "按类型分布",
        bySubtype: "按类型",
        files: "个文件",
        users: "个用户",
        subtypes: "子类型",
        categories: "分类",
        avgPerUser: "人均占用",
        largestUsers: "存储占用最多的用户",
        autoCleanup: "7天后自动清理",
      },
      summary: {
        totalFiles: "总文件数",
        totalSize: "总大小",
      },
      modal: {
        title: "用户资源",
        summary: "资源摘要",
        noResources: "暂无资源",
        loading: "加载中...",
        error: "加载失败",
        deleteWarning: "删除操作不可撤销",
      },
      status: {
        expired: "已过期",
        current: "当前",
      },
      actions: {
        viewResources: "查看资源",
        delete: "删除",
        open: "打开",
        download: "下载",
        confirmDelete: "确认删除",
        cancel: "取消",
        browseTools: "浏览工具",
      },
      groups: {
        uncategorized: "未分类",
        expandAll: "展开全部",
        collapseAll: "折叠全部",
      },
      deleteAll: {
        type: "删除所有{type}",
        all: "删除全部资源",
        confirmType: "确定要删除该用户的所有{type}吗？",
        confirmAll: "确定要删除该用户的所有资源吗？此操作不可撤销。",
      },
      deleting: "删除中...",
      table: {
        userId: "用户ID",
        totalFiles: "文件数",
        totalSize: "大小",
      },
      cleanup: {
        title: "清理操作",
        tempTitle: "清理临时文件",
        tempDesc: "删除超过指定天数的临时文件",
        tempButton: "清理临时文件",
        artifactsTitle: "清理过期产物",
        artifactsDesc: "删除已过期的产物文件及数据库记录",
        artifactsButton: "清理过期产物",
        daysOld: "文件天数",
        days: "天",
        confirm: "确认清理",
        cleaning: "清理中...",
        error: "清理失败",
        successTemp: "已删除 {count} 个临时文件，释放 {size} 空间",
        successArtifacts: "已删除 {count} 个过期产物，释放 {size} 空间",
      },
      lastUpdated: "最后更新",
      errors: {
        loadFailed: "加载失败",
      },
    },

    // Shared: User Selector
    userSelector: {
      searchPlaceholder: "搜索用户名或ID...",
      selectedCount: "已选择 {count} 个用户",
      selectAll: "全选",
      deselectAll: "取消全选",
      clearAll: "清除",
      noUsers: "未找到用户",
      devices: "个设备",
      showingCount: "显示 {from}-{to} 条，共 {total} 条",
    },

    // Shared: Workflow Target Selector
    workflowTarget: {
      currentUser: "当前用户",
      currentUserDesc: "使用创建者/执行者的身份运行",
      specificUsers: "指定用户",
      specificUsersDesc: "选择特定用户作为执行目标",
      willRunAs: "将以此用户身份运行",
    },
  },

  // ========== English ==========
  en: {
    common: {
      appTitle: "Agent Admin",
      save: "Save",
      saving: "Saving...",
      cancel: "Cancel",
      delete: "Delete",
      reset: "Reset",
      loading: "Loading...",
      success: "Success",
      error: "Error",
      warning: "Warning",
      required: "Required",
      previous: "Previous",
      next: "Next",
      refresh: "Refresh",
      close: "Close",
      description: "Description",
      retry: "Retry",
      clear: "Clear",
      admin: "Admin",
    },
    actions: {
      saveChanges: "Save Changes",
      resetToDefault: "Reset to Default",
      resetToSystemDefault: "Reset to System Default",
      browseTools: "Browse Tools",
    },
    navigation: {
      appTitle: "Dora Admin",
      agentConfigs: "Agent Configs",
      systemSettings: "System Settings",
      promptTemplates: "Prompt Templates",
      skills: "Skills",
      monitoring: "Monitoring",
      reports: "Reports",
      classificationAnalytics: "Classification Analytics",
      messageFeedback: "Message Feedback",
      users: "Users",
      pushTest: "Push Test",
      evaluation: "Evaluation",
      playground: "Playground",
      imageWorkflows: "Image Workflows",
      workflowSchedules: "Schedules",
      annotation: "Annotation",
      logout: "Logout",
      expandSidebar: "Expand sidebar",
      collapseSidebar: "Collapse sidebar",
    },
    sections: {
      basic: "Basic Information",
      models: "Model Configuration",
      llm_parameters: "LLM Sampling Parameters",
      output_format: "Output Format",
      features: "Features",
      message_processing: "Message Processing",
      advanced: "Advanced Settings",
      memory: "Memory Configuration",
      memory_sampling: "Memory Model Sampling",
      memory_prompts: "Memory Custom Prompts",
      memory_user: "User Memory",
      memory_agent: "Agent Memory",
      memory_query: "Memory Query Settings",
      memory_ebbinghaus: "Memory Decay (Ebbinghaus)",
      session_summary: "Session Summary",
      custom_tools: "Custom Tools",
      builtin_tools: "Builtin Tools",
      mcp_tools: "MCP Servers",
      team: "Team Configuration",
      prompts: "System Prompts",
      first_message: "First Message Hook",
      response_classifier: "Response Classifier Hook",
      response_classifier_prompts: "Response Type Instructions",
    },
    fields: {
      // Basic Information
      agent_type: {
        label: "Agent Type",
        help: "Unique identifier for the agent",
      },
      name: {
        label: "Name",
        help: "Display name for the agent",
        placeholder: "Enter agent name",
      },
      description: {
        label: "Description",
        help: "Brief description of this agent",
        placeholder: "Enter description",
      },

      // Model Configuration
      chat_model_id: {
        label: "Chat Model",
        help: "LLM model used for conversation",
        invalidMessage:
          'Warning: Model ID "{value}" is not in the available list',
      },
      memory_model_id: {
        label: "Memory Model",
        help: "Model used for memory extraction",
        invalidMessage:
          'Warning: Model ID "{value}" is not in the available list',
      },
      embedding_model_id: {
        label: "Embedding Model",
        help: "Model used for text embeddings",
        invalidMessage:
          'Warning: Model ID "{value}" is not in the available list',
      },

      // LLM Sampling Parameters
      temperature: {
        label: "Temperature",
        help: "Sampling temperature (0=deterministic, higher=random). Range: 0.0-2.0",
      },
      top_p: {
        label: "Top P",
        help: "Nucleus sampling probability (0.0-1.0). Lower uses fewer tokens",
      },
      max_tokens: {
        label: "Max Tokens",
        help: "Maximum tokens to generate (leave empty for unlimited). Range: 1-128000",
        placeholder: "Leave empty for unlimited",
      },
      presence_penalty: {
        label: "Presence Penalty",
        help: "Token presence penalty (-2.0 to 2.0). Positive encourages new topics",
      },
      frequency_penalty: {
        label: "Frequency Penalty",
        help: "Token frequency penalty (-2.0 to 2.0). Positive reduces repetition",
      },

      // Features
      output_format: {
        label: "Output Format",
        help: "Response output format: default (plain text), markdown, or JSON",
        options: {
          default: "Default",
          markdown: "Markdown",
          json: "JSON",
        },
      },
      debug_mode: {
        label: "Debug Mode",
        help: "Enable verbose logging",
      },
      storage_enabled: {
        label: "Storage Enabled",
        help: "Enable session storage",
      },
      knowledge_enabled: {
        label: "Knowledge Base",
        help: "Enable knowledge base retrieval",
      },
      enable_birth_chart_context: {
        label: "Birth Chart Context",
        help: "Automatically inject user's birth chart data (XML format) into agent context. Requires user to have added their birth chart.",
      },
      enable_web_search: {
        label: "Web Search",
        help: "Enable web search functionality",
      },

      // Message Processing
      add_datetime_to_instructions: {
        label: "Add DateTime",
        help: "Include current date and time in system prompt",
      },
      add_history_to_context: {
        label: "Add History",
        help: "Include conversation history in agent context (Agno: add_history_to_context)",
      },
      num_history_runs: {
        label: "Max History Runs",
        help: "Maximum conversation turns to include (0-50). Prevents context overflow (Agno: num_history_runs)",
      },
      read_chat_history: {
        label: "Read Chat History",
        help: "Allow agent to query full chat history via tools (Agno: read_chat_history)",
      },

      // Memory Configuration
      "memory.enabled": {
        label: "Enable Memory",
        help: "Enable long-term memory storage",
      },
      "memory.provider": {
        label: "Memory Provider",
        help: "Choose memory storage backend",
        options: {
          MEM0: "MEM0",
          POSTGRES: "PostgreSQL",
          NONE: "None",
        },
        invalidMessage:
          'Warning: Provider "{value}" is not valid. Expected: MEM0, POSTGRES, or NONE',
      },
      "memory.model_id": {
        label: "Memory Model",
        help: "Model used for memory extraction and operations",
        invalidMessage:
          'Warning: Model ID "{value}" is not in the available memory models list',
      },
      "memory.enable_agentic_memory": {
        label: "Agentic Memory",
        help: "Enable agentic memory management - adds memory control tools to the agent. For MEM0: adds search/add/update/delete memory tools. For PostgreSQL: adds memory search/save tools. When enabled, the agent can actively manage its own memories.",
      },
      "memory.operation_mode": {
        label: "Mem0 Operation Mode",
        help: "Select memory operation mode",
        options: {
          SYNC: "Sync (Default - Wait for confirmation)",
          FIRE_FORGET: "Fire & Forget (Fast - No wait)",
          BATCH: "Batch (Queue for bulk processing)",
        },
      },
      "memory.loading_strategy": {
        label: "Memory Loading Strategy",
        help: "Select memory loading strategy",
        options: {
          balanced: "Balanced (Default - 3 pref, 3 facts, 2 plans, 2 recent)",
          preference_first:
            "Preference-First (5 pref, 2 critical facts, 1 recent)",
          context_heavy: "Context-Heavy (2 key pref, 3 critical, 5 episodic)",
          minimal: "Minimal (3 critical only, 2 episodic)",
          time_aware: "Time-Aware (3 plans, 2 pref, 3 recent facts)",
        },
      },

      // Memory Sampling
      "memory.model_sampling.temperature": {
        label: "Memory Temperature",
        help: "Memory model temperature (lower=more factual). Default: 0.3 (more deterministic than chat)",
      },
      "memory.model_sampling.top_p": {
        label: "Memory Top P",
        help: "Memory model nucleus sampling (default: 0.9 for precision)",
      },
      "memory.model_sampling.max_tokens": {
        label: "Memory Max Tokens",
        help: "Max tokens for memory operations (default: 2000)",
        placeholder: "2000",
      },

      // Memory Custom Prompts
      "memory.prompts.enable_custom_prompts": {
        label: "Enable Custom Prompts",
        help: "Enable Mem0 custom prompts feature for this agent",
      },
      "memory.prompts.user_memory_extraction_mode": {
        label: "User Memory Extraction Mode",
        help: "Choose to use system default or custom prompt",
        options: {
          system: "Use System Default",
          custom: "Use Custom Prompt",
        },
      },
      "memory.prompts.user_memory_extraction_prompt": {
        label: "User Memory Extraction Prompt",
        help: 'Custom prompt for extracting memories from USER messages. Click "Reset to System Default" to load the default value.',
        placeholder:
          "Enter custom user memory extraction prompt (50-4000 characters)...",
      },
      "memory.prompts.agent_memory_extraction_mode": {
        label: "Agent Memory Extraction Mode",
        help: "Choose to use system default or custom prompt",
        options: {
          system: "Use System Default",
          custom: "Use Custom Prompt",
        },
      },
      "memory.prompts.agent_memory_extraction_prompt": {
        label: "Agent Memory Extraction Prompt",
        help: 'Custom prompt for extracting memories from AGENT messages. Click "Reset to System Default" to load the default value.',
        placeholder:
          "Enter custom agent memory extraction prompt (50-4000 characters)...",
      },
      "memory.prompts.memory_update_mode": {
        label: "Memory Update Mode",
        help: "Choose to use system default or custom prompt",
        options: {
          system: "Use System Default",
          custom: "Use Custom Prompt",
        },
      },
      "memory.prompts.memory_update_prompt": {
        label: "Memory Update Prompt",
        help: 'Custom prompt for updating and merging memories. Click "Reset to System Default" to load the default value.',
        placeholder:
          "Enter custom memory update prompt (50-4000 characters)...",
      },

      // Memory Categories
      "memory.categories.enable_user_custom_categories": {
        label: "Enable User Custom Categories",
        help: "Enable custom categories for user memories",
      },
      "memory.categories.user_categories_mode": {
        label: "User Categories Mode",
        help: "Choose to use system default or custom categories",
        options: {
          system: "Use System Default",
          custom: "Use Custom Categories",
        },
      },
      "memory.categories.user_custom_categories": {
        label: "User Custom Categories",
        help: 'Define custom USER memory categories in JSON format. Each category must have: description (string), examples (array), temporal (boolean). Click "Reset to System Default" to load the default categories.',
        placeholder:
          '{"preference": {"description": "User preferences", "examples": ["Likes pizza"], "temporal": false}}',
      },
      "memory.categories.enable_agent_custom_categories": {
        label: "Enable Agent Custom Categories",
        help: "Enable custom categories for agent memories",
      },
      "memory.categories.agent_categories_mode": {
        label: "Agent Categories Mode",
        help: "Choose to use system default or custom categories",
        options: {
          system: "Use System Default",
          custom: "Use Custom Categories",
        },
      },
      "memory.categories.agent_custom_categories": {
        label: "Agent Custom Categories",
        help: 'Define custom AGENT memory categories in JSON format. Each category must have: description (string), examples (array), temporal (boolean). Click "Reset to System Default" to load the default categories.',
        placeholder:
          '{"recommendation": {"description": "Recommendations", "examples": ["Exercise daily"], "temporal": false}}',
      },

      // Memory Query Settings
      "memory.query.rerank_enabled": {
        label: "Enable Reranking",
        help: "Enable reranking of memory query results",
      },
      "memory.query.top_k": {
        label: "Top K",
        help: "Maximum number of memories to return",
      },
      "memory.query.score_threshold": {
        label: "Score Threshold",
        help: "Minimum relevance score threshold for memories (0.0-1.0)",
      },

      // Memory Decay (Ebbinghaus)
      "memory.ebbinghaus.enabled": {
        label: "Enable Memory Decay",
        help: "Enable Ebbinghaus forgetting curve for temporal memories. Memories decay over time based on access frequency.",
      },
      "memory.ebbinghaus.decay_factor": {
        label: "Decay Factor",
        help: "Controls decay speed (0.1=fast forget, 1.0=slow forget). Higher values mean slower memory decay.",
      },
      "memory.ebbinghaus.min_retention": {
        label: "Min Retention",
        help: "Minimum retention floor (0.0-1.0). Memories never decay below this value. Default 0.1 means memories retain at least 10% relevance.",
      },
      "memory.ebbinghaus.initial_strength": {
        label: "Initial Strength",
        help: "Starting memory strength for new memories (0.5-2.0). Higher values make new memories more resistant to decay.",
      },
      "memory.ebbinghaus.reinforcement_boost": {
        label: "Reinforcement Boost",
        help: "Strength increase when memory is accessed (0.1-1.0). Higher values strengthen memories more on each access.",
      },
      "memory.ebbinghaus.max_strength": {
        label: "Max Strength",
        help: "Maximum memory strength cap (1.0-5.0). Limits how strong a memory can become through reinforcement.",
      },

      // Session Summary Configuration
      "session_summary.enabled": {
        label: "Enable Session Summary",
        help: "Automatically generate summaries for long conversations. Summaries compress early conversation content and inject into system prompts, helping agents maintain awareness of the full conversation.",
      },
      "session_summary.model_id": {
        label: "Summary Model",
        help: "Model used for summary generation. Recommend using a fast, cost-effective model (e.g., gpt-5-mini) since summary generation is a background task.",
      },
      "session_summary.summary_frequency": {
        label: "Generation Timing",
        help: "When to generate summaries:\n• On Request: Only via API manual trigger\n• After N Messages: Auto-generate after threshold (recommended)\n• End of Session: Generate when session ends",
        options: {
          on_request: "On Request",
          after_n_messages: "After N Messages (Recommended)",
          end_of_session: "End of Session",
        },
      },
      "session_summary.message_threshold": {
        label: "First Generation Threshold",
        help: "Number of messages before generating the first summary.\n\n💡 Recommended: Set equal to 'num_history_runs'.\n\nExample: history_runs=5, threshold=5\n• Messages 1-5: Covered by history (no summary needed)\n• Messages 6+: Summary covers [1, N-5], history covers [N-4, N]\n\nThis ensures seamless coverage with no information gaps.",
      },
      "session_summary.regenerate_interval": {
        label: "Regenerate Interval",
        help: "After first generation, regenerate summary every N new messages.\n• 0: Never regenerate (use initial summary forever)\n• N > 0: Update summary every N new messages\n\n💡 Recommended: Set equal to 'num_history_runs' for rolling coverage.",
      },
      "session_summary.inject_into_prompt": {
        label: "Inject into Prompt",
        help: "Whether to inject the summary as context into system prompts. When enabled, agents can perceive key points from earlier conversation when responding.",
      },
      "session_summary.custom_prompt": {
        label: "Custom Summary Prompt",
        help: "Custom prompt template for summary generation. Leave empty to use Agno framework's default prompt.\n\nAvailable variables: {messages} (conversation content)",
      },

      // Advanced Settings
      session_timeout_minutes: {
        label: "Session Timeout (minutes)",
        help: "Automatic session timeout duration",
      },
      max_cached_resources: {
        label: "Max Cached Resources",
        help: "Maximum number of cached resources",
      },
      resource_ttl_minutes: {
        label: "Resource TTL (minutes)",
        help: "Cache validity time for resources",
      },

      // Tools
      custom_tools: {
        label: "Custom Tools",
        help: "Custom tool configuration - can be a module name (string), list of modules (array), or detailed config (object)",
        placeholder:
          '{"modules": ["tools.astrology.astro_tools"], "enabled_tools": ["calculate_natal_chart"]}',
      },
      builtin_tools: {
        label: "Builtin Tools",
        help: "List of enabled builtin tools",
      },
      mcp_servers: {
        label: "MCP Servers",
        help: "MCP server configuration - can be a server ID (string), list of IDs (array), or detailed config (object)",
        placeholder:
          '{"servers": ["astro-tool"], "enabled_tools": ["mcp__astro_tool__get_birth_chart"]}',
      },

      // System Prompts
      instructions: {
        label: "System Instructions",
        help: "Define the agent's role, behavior, and capabilities",
        placeholder: "Enter system instructions...",
      },
      additional_context: {
        label: "Additional Context",
        help: "Extra context, examples, or constraints",
        placeholder: "Enter additional context...",
      },
      enable_first_message_instruction: {
        label: "Enable First Message Instruction",
        help: "Enable special instruction for the first message in a new topic/session",
      },
      first_message_mode: {
        label: "Instruction Mode",
        help: "Choose to use system default or custom instruction",
        options: {
          system: "Use System Default",
          custom: "Use Custom Instruction",
        },
      },
      first_message_custom_instruction: {
        label: "Custom First Message Instruction",
        help: 'Custom instruction for first message. Click "Reset to System Default" to load default value.',
        placeholder: "Enter custom instruction...",
      },

      // Response Classifier
      "response_classifier.enabled": {
        label: "Enable Response Classifier",
        help: "Classify user messages to determine response type (quick vs deep). Enables dynamic prompt selection based on message complexity.",
      },
      "response_classifier.model_id": {
        label: "Fallback Classification Model",
        help: "Model used for LLM-based classification when rule confidence is below threshold. Use a fast, cost-effective model.",
      },
      "response_classifier.rule_confidence_threshold": {
        label: "Rule Confidence Threshold",
        help: "Confidence threshold for rule-based classification. Below this value, LLM classification is used. Range: 0.0-1.0, default: 0.8",
      },
      "response_classifier.quick_response_max_length": {
        label: "Quick Response Max Length",
        help: "Maximum message length (characters) for quick response classification. Longer messages tend toward deep responses.",
      },
      "response_classifier.llm_timeout": {
        label: "LLM Timeout (seconds)",
        help: "Timeout for LLM classification in seconds. If exceeded, defaults to deep response.",
      },

      // Response Type Instructions
      "response_classifier.quick_response_instructions": {
        label: "Quick Response Instructions",
        help: "Instructions used for quick responses (greetings, confirmations, casual chat). If empty, default instructions will be used instead.",
        placeholder:
          "You are a friendly assistant. Please respond to greetings or simple questions in a concise, natural way. Keep the conversation light and pleasant.",
      },
      "response_classifier.deep_response_instructions": {
        label: "Deep Response Instructions",
        help: "Instructions used for deep responses (analysis, consultation, emotional support). If empty, default instructions will be used instead.",
        placeholder:
          "You are a professional consultant. Please carefully analyze the user's question, providing in-depth, professional insights and suggestions. Consider multiple angles and provide valuable analysis.",
      },

      // ========== Hook Configuration Fields ==========

      // Memory Query Settings (hooks.user_facts, hooks.memory_context - unified)
      "hooks.user_facts.facts_limit": {
        label: "Facts Query Limit",
        help: "Maximum number of permanent user facts to retrieve (1-100)",
      },
      "hooks.memory_context.events_limit": {
        label: "Events Query Limit",
        help: "Maximum number of temporal event memories to retrieve (1-50)",
      },
      "hooks.memory_context.events_threshold": {
        label: "Events Search Threshold",
        help: "Similarity threshold for event search (0.0-1.0). Lower values return more results.",
      },
      "hooks.memory_context.agent_memories_limit": {
        label: "Agent Memories Limit",
        help: "Maximum number of agent-specific memories to retrieve (1-20)",
      },
      "hooks.memory_context.agent_memories_threshold": {
        label: "Agent Memories Threshold",
        help: "Similarity threshold for agent memory search (0.0-1.0). Lower values return more results.",
      },
      "hooks.memory_context.query_max_length": {
        label: "Query Max Length",
        help: "Maximum total length of memory search query in characters (100-1000)",
      },
      "hooks.memory_context.user_query_length": {
        label: "User Query Length",
        help: "Max length of current user question in search query (50-500 chars)",
      },
      "hooks.memory_context.assistant_context_length": {
        label: "Assistant Context Length",
        help: "Max length of last assistant response in search query (0-500 chars). Set to 0 to disable assistant context.",
      },

      // Memory Decay / Ebbinghaus (hooks.memory_reinforce)
      "hooks.memory_reinforce.enabled": {
        label: "Enable Memory Decay",
        help: "Enable Ebbinghaus forgetting curve for temporal memories. Memories decay over time based on access frequency.",
      },
      "hooks.memory_reinforce.decay_factor": {
        label: "Decay Factor",
        help: "Controls decay speed (1.0=fast forget, 30.0=slow forget). Higher values mean slower memory decay.",
      },
      "hooks.memory_reinforce.min_retention": {
        label: "Min Retention",
        help: "Minimum retention floor (0.0-0.5). Memories never decay below this value. Default 0.1 means memories retain at least 10% relevance.",
      },
      "hooks.memory_reinforce.initial_strength": {
        label: "Initial Strength",
        help: "Starting memory strength for new memories (0.5-2.0). Higher values make new memories more resistant to decay.",
      },
      "hooks.memory_reinforce.reinforcement_boost": {
        label: "Reinforcement Boost",
        help: "Strength increase when memory is accessed (0.1-1.0). Higher values strengthen memories more on each access.",
      },
      "hooks.memory_reinforce.max_strength": {
        label: "Max Strength",
        help: "Maximum memory strength cap (1.0-5.0). Limits how strong a memory can become through reinforcement.",
      },

      // First Message Hook (hooks.first_message)
      "hooks.first_message.enabled": {
        label: "Enable First Message Hook",
        help: "Add special instruction for first message in new sessions",
      },
      "hooks.first_message.mode": {
        label: "First Message Mode",
        help: "Use system default or custom instruction",
        options: {
          system: "Use System Default",
          custom: "Use Custom Instruction",
        },
      },
      "hooks.first_message.custom_instruction": {
        label: "Custom First Message Instruction",
        help: "Custom instruction shown only in first message of new sessions",
        placeholder: "Enter custom instruction for first message...",
      },

      // Response Classifier Hook (hooks.classifier)
      "hooks.classifier.enabled": {
        label: "Enable Response Classifier",
        help: "Classify user messages to determine response type (quick vs deep). Enables dynamic prompt selection based on message complexity.",
      },
      "hooks.classifier.model_id": {
        label: "Fallback Classification Model",
        help: "Model used for LLM-based classification when rule confidence is below threshold. Use a fast, cost-effective model.",
      },
      "hooks.classifier.rule_confidence_threshold": {
        label: "Rule Confidence Threshold",
        help: "Confidence threshold for rule-based classification. Below this value, LLM classification is used. Range: 0.0-1.0, default: 0.8",
      },
      "hooks.classifier.quick_response_max_length": {
        label: "Quick Response Max Length",
        help: "Maximum message length (characters) for quick response classification. Longer messages tend toward deep responses.",
      },
      "hooks.classifier.llm_timeout": {
        label: "LLM Timeout (seconds)",
        help: "Timeout for LLM classification in seconds. If exceeded, defaults to deep response.",
      },
      "hooks.classifier.quick_response_instructions": {
        label: "Quick Response Instructions",
        help: "Instructions used for quick responses (greetings, confirmations, casual chat). If empty, default instructions will be used.",
        placeholder:
          "You are a friendly assistant. Please respond briefly and naturally to user greetings or simple questions. Maintain a light and pleasant conversation atmosphere.",
      },
      "hooks.classifier.deep_response_instructions": {
        label: "Deep Response Instructions",
        help: "Instructions used for deep responses (analysis, consultation, emotional support). If empty, default instructions will be used.",
        placeholder:
          "You are a professional consultant. Please carefully analyze the user's question, providing in-depth, professional insights and suggestions. Consider multiple angles and provide valuable analysis.",
      },
    },
    pushNotifications: {
      // Page
      pageTitle: "Push Notifications",

      // Stats
      stats: {
        totalTokens: "Total Tokens",
        activeTokens: "Active Tokens",
        pushSent: "Pushes Sent",
        successRate: "Success Rate",
        platformBreakdown: "Platform Breakdown",
        ios: "iOS",
        android: "Android",
      },

      // Send Notification Form
      sendForm: {
        title: "Send Push Notification",
        description:
          "Send a push notification to test devices or specific users",
        notificationTitle: "Notification Title",
        notificationTitlePlaceholder: "Enter notification title",
        body: "Notification Body",
        bodyPlaceholder: "Enter notification content",
        target: "Target",
        targetAll: "All Users",
        targetAllWarning:
          "This will send notifications to all registered users.",
        targetTest: "Test Devices",
        targetTestDesc: "Send to devices with test tokens",
        targetSpecificUsers: "Specific Users",
        targetSpecificUsersDesc: "Send to specific user IDs",
        userIds: "User IDs",
        userIdsPlaceholder: "Enter user IDs, one per line",
        userIdsHint: "Enter one user ID per line",
        sound: "Sound",
        soundPlaceholder: "default",
        soundHint: "Sound played when notification arrives",
        soundDefault: "Default Sound",
        soundNone: "Silent",
        badgeCount: "Badge Count",
        badgeCountPlaceholder: "",
        badgeCountHint: "App icon badge number",
        badgeCountHintFull: "0 = Clear badge, empty = No change",
        customData: "Custom Data (JSON)",
        customDataPlaceholder: '{"type": "action", "action": "profile"}',
        customDataHint: "Action triggered when notification is tapped",
        dataTemplates: "Templates",
        dataTemplateLabels: {
          navigation: "Home",
          profile: "Profile",
          feedback: "Feedback",
          birthChart: "Birth Chart",
          fortune: "Fortune",
        },
        clearButton: "Clear",
        sendButton: "Send Notification",
        sending: "Sending...",
        successMessage: "Notification sent successfully",
        sent: "Sent",
        failed: "Failed",
        errors: "Errors",
      },

      // Device Tokens
      tokens: {
        tabTitle: "Device Tokens",
        title: "Registered Devices",
        allPlatforms: "All Platforms",
        refresh: "Refresh",
        loading: "Loading...",
        noTokens: "No device tokens found",
        user: "User",
        registered: "Registered",
        active: "Active",
        inactive: "Inactive",
        page: "Page",
        of: "of",
        total: "total",
        previous: "Previous",
        next: "Next",
      },

      // Notification Logs
      logs: {
        tabTitle: "Push History",
        title: "Recent Pushes",
        allStatuses: "All Statuses",
        statusSent: "Sent",
        statusFailed: "Failed",
        refresh: "Refresh",
        loading: "Loading...",
        noLogs: "No push history found",
        error: "Error",
        page: "Page",
        of: "of",
        total: "total",
        previous: "Previous",
        next: "Next",
        // Detail view fields
        user: "User",
        userId: "User ID",
        displayName: "Display Name",
        platform: "Platform",
        deviceInfo: "Device Info",
        sentAt: "Sent At",
        pushToken: "Push Token",
        fullTitle: "Full Title",
        fullBody: "Full Body",
        dataPayload: "Custom Data",
        actionNavigation: "Navigation",
        actionType: "Action",
        actionFortune: "Fortune",
        // Grouped view fields
        recipients: "recipient(s)",
        batches: "batches",
        totalRecipients: "Total Recipients",
        recipientList: "Recipient List",
      },

      // Time formatting
      time: {
        justNow: "Just now",
        minutesAgo: "{n} minutes ago",
        hoursAgo: "{n} hours ago",
        daysAgo: "{n} days ago",
      },

      // Validation errors
      validation: {
        titleRequired: "Title is required",
        bodyRequired: "Body is required",
        userIdsRequired: "At least one user ID is required",
        invalidJson: "Invalid JSON format",
      },

      // User Selector
      userSelector: {
        searchPlaceholder: "Search by name or ID...",
        selectedCount: "{count} users selected",
        selectAll: "Select All",
        deselectAll: "Deselect All",
        clearAll: "Clear",
        noUsers: "No users found",
        devices: "device(s)",
        showingCount: "Showing {from}-{to} of {total}",
      },

      // Content Assist (AI-powered content generation)
      contentAssist: {
        sectionTitle: "Push Content",
        inputModeLabel: "Content Input Mode",
        agentMode: "Agent",
        llmMode: "LLM",
        manualMode: "Manual",
        selectAgent: "Select Agent",
        loading: "Loading...",
        noAgents: "No agents available",
        descriptionLabel: "Describe push content",
        descriptionPlaceholder: "e.g., Remind users to check daily horoscope",
        generateButton: "Generate Content",
        generating: "Generating...",
        selectAgentFirst: "Please select an agent first",
        generateFailed: "Content generation failed",
        // LLM mode specific
        selectLLMModel: "Select LLM Model",
        noModels: "No models available",
        llmModeDescription:
          "Use raw LLM API with custom system prompt for full control",
        temperature: "Temperature",
        precise: "Precise (0)",
        balanced: "Balanced (1)",
        creative: "Creative (2)",
        selectModelFirst: "Please select an LLM model first",
      },
    },
    evalScenarios: {
      // Page
      pageTitle: "Evaluation Scenarios",
      pageDescription:
        "Manage test scenarios and run agent evaluations to ensure quality",

      // Actions
      createScenario: "Create Scenario",
      promoteChat: "Promote Chat",
      refresh: "Refresh",
      runAll: "Run All",
      runSingle: "Run Test",
      running: "Running...",
      edit: "Edit",
      delete: "Delete",
      duplicate: "Duplicate",
      duplicateSuccess: "Scenario duplicated successfully",
      expandDetails: "Show details",
      collapseDetails: "Collapse",
      removeTag: "Remove tag",

      // Filters
      filters: "Filters",
      searchPlaceholder: "Search scenarios...",
      allAgentTypes: "All Agent Types",
      allCategories: "All Categories",
      allSources: "All Sources",
      allStatus: "All Status",
      sourceManual: "Manual",
      sourceChatPromoted: "Chat Promoted",
      statusActive: "Active",
      statusInactive: "Inactive",

      // Stats
      totalScenarios: "Total Scenarios",
      active: "Active",
      manual: "Manual",
      chatPromoted: "Chat Promoted",
      neverRun: "Never run",

      // Form Steps
      step1Title: "Basic Information",
      step1Description:
        "Set scenario name, select target agent, and categorize",
      step2Title: "Test Conversation",
      step2Description:
        "Define the conversation to test (user messages and expected flow)",
      step3Title: "Evaluation Config",
      step3Description: "Choose LLM judge model and evaluation metrics",
      stepOf: "Step {current} of {total}",
      next: "Next",
      previous: "Previous",

      // Form Fields - Step 1
      scenarioName: "Scenario Name",
      scenarioNamePlaceholder: "e.g., sagittarius_career_advice",
      scenarioNameHint: "Use lowercase with underscores (snake_case)",
      description: "Description",
      descriptionPlaceholder:
        "What does this scenario test? e.g., Test if agent provides helpful career advice for Sagittarius users",
      targetAgent: "Target Agent",
      targetAgentHint: "Select the agent to run this test against",
      category: "Category",
      categoryPlaceholder: "e.g., astrology, career",
      priority: "Priority",
      priorityHint: "Higher = run first",
      tags: "Tags",
      addTagPlaceholder: "Add tag and press Enter...",

      // Form Fields - Step 2
      testConversation: "Test Conversation",
      conversationHint:
        "Define the test conversation. At minimum, add a user message to send to the agent. You can add multiple turns to simulate a multi-turn conversation.",
      addTurn: "Add Turn",
      userRole: "User",
      assistantRole: "Assistant",
      userMessagePlaceholder: "User message to test...",
      assistantMessagePlaceholder:
        "Expected assistant response (for context)...",
      expectedResponse: "Expected Response",
      expectedResponsePlaceholder:
        "Write an ideal Assistant response example, e.g.:\n\nSure! Considering you prefer quiet places, here are some hidden gems:\n1. Qingcheng Back Mountain - peaceful, ¥60 entrance\n2. Jiezi Ancient Town - free, ~2 hours drive\n\nWhich type do you prefer?",
      expectedResponseHint:
        "Write an ideal response text, not evaluation rules",
      expectedResponseDetailTitle: "What is Expected Response?",
      expectedResponseDetail1:
        '✅ Should write: A specific ideal response example ("How Agent should respond")',
      expectedResponseDetail2:
        '❌ Do NOT write: Evaluation rules (that\'s what "Criteria" is for)',
      expectedResponseDetail3:
        "Purpose: Used for AnswerRelevancy, Correctness metrics for semantic similarity comparison",
      expectedResponseExample:
        "If using GEval custom metrics, this field can be left empty - evaluation relies mainly on Criteria",

      // Form Fields - Step 3
      llmJudgeModel: "LLM Judge Model",
      llmJudgeModelHint:
        "Recommend: gpt-4o-mini for cost-effective evaluation, gpt-4o for higher accuracy",
      llmJudgeModelWarning:
        "⚠️ This model may not be supported by DeepEval. Please use OpenAI GPT models (e.g., gpt-4o-mini, gpt-4o) to ensure evaluations run correctly.",
      quickAddMetrics: "Quick Add Metrics",
      evaluationCriteria: "Evaluation Criteria",
      customMetric: "Custom Metric",
      metricNamePlaceholder: "Metric name",
      threshold: "Threshold",
      noMetricsSelected:
        'No metrics selected. Click "Quick Add" buttons above or add custom metrics.',
      minOverallScore: "Minimum Overall Score to Pass",
      minOverallScoreHint:
        "Scenario passes if weighted average of enabled metrics exceeds this threshold",
      activeCheckbox: "Active (scenario will be included in batch evaluations)",

      // Metrics
      metrics: {
        hallucination: "Hallucination Detection",
        hallucinationDesc: "Detect factual errors",
        hallucinationHelp:
          "Detects if AI fabricates non-existent information. Lower is better (0=no hallucination, 1=severe). Threshold 0.3 means score must be below 30% to pass.",
        relevancy: "Response Relevancy",
        relevancyDesc: "Check if response is relevant",
        relevancyHelp:
          "Evaluates how relevant the response is to the user question. Higher is better (0=irrelevant, 1=highly relevant). Threshold 0.7 means 70%+ relevancy required.",
        faithfulness: "Faithfulness",
        faithfulnessDesc: "Check fidelity to source",
        faithfulnessHelp:
          'Checks if response stays faithful to provided context without adding extra information. Higher is better. Requires "Expected Response" as reference.',
        answer_relevancy: "Answer Relevancy",
        answer_relevancyDesc: "Check answer quality",
        answer_relevancyHelp:
          "Comprehensive evaluation of answer quality: directness, completeness, and clarity. Higher is better.",
        contextual_relevancy: "Contextual Relevancy",
        contextual_relevancyDesc: "Check context usage",
        contextual_relevancyHelp:
          "Evaluates if AI correctly uses conversation history and context. Best for multi-turn conversations.",
        bias: "Bias Detection",
        biasDesc: "Detect biased content",
        biasHelp:
          "Detects gender, racial, age or other biases in the response. Lower is better (0=no bias).",
        toxicity: "Toxicity Detection",
        toxicityDesc: "Detect toxic content",
        toxicityHelp:
          "Detects offensive, insulting or harmful content. Lower is better (0=safe). Threshold 0.1 means toxicity must be below 10%.",
        astro_accuracy: "Astrological Accuracy",
        astro_accuracyDesc: "Check astrology terminology accuracy",
        astro_accuracyHelp:
          "Evaluates accuracy of astrological content: zodiac identification, planet positions, house interpretation, aspect analysis.",
        interpretation_quality: "Interpretation Quality",
        interpretation_qualityDesc: "Evaluate depth and insight",
        interpretation_qualityHelp:
          "Evaluates astrology reading quality: depth and insight, personal relevance, professional vs accessible balance, actionable guidance.",
        helpfulness: "Helpfulness",
        helpfulnessDesc: "Evaluate how helpful the response is",
        helpfulnessHelp:
          "Evaluates practical helpfulness: does it solve user problem, is info practical and actionable, is tone appropriate.",
      },
      metricsGuide: "Metrics Guide",
      metricsGuideContent:
        "Metrics fall into two categories: 1) Lower is better (hallucination, bias, toxicity) - threshold is max acceptable score; 2) Higher is better (relevancy, faithfulness, etc.) - threshold is minimum required. All scores range 0-1.",
      lowerIsBetter: "↓ Lower is better",
      higherIsBetter: "↑ Higher is better",

      // Dialogs
      createDialogTitle: "Create Evaluation Scenario",
      editDialogTitle: "Edit Evaluation Scenario",
      createButton: "Create Scenario",
      saveChanges: "Save Changes",

      // Promote Chat Dialog
      promoteChatTitle: "Promote Chat to Scenario",
      promoteChatDescription:
        "Convert a real chat conversation into a test scenario. The conversation's messages will be extracted and used as the test conversation.",
      chatTopicId: "Chat/Topic ID",
      chatTopicIdPlaceholder: "Enter chat or topic ID",
      chatTopicIdHint: "Find this in the chat history or database",
      promoteButton: "Promote to Scenario",

      // Run Evaluation Dialog
      runEvaluationTitle: "Run Evaluation",
      runningEvaluation: "Running evaluation...",
      runningHint:
        "You can close this dialog, the evaluation will continue in the background",
      runningHintResult:
        "Results will appear in the evaluation history when complete",
      evaluationStarted: "Evaluation Started",
      evaluationStartedHint:
        "Evaluation is running in the background, you can close this dialog",
      checkRunsPanel: 'Open "Run Records" to view real-time progress',
      evaluationComplete: "Evaluation Complete",
      overallScore: "Overall Score",
      passed: "Passed",
      failed: "Failed",
      response: "Response",
      responseTime: "Response Time",
      metricResults: "Metric Results",
      reason: "Reason",
      scenario: "Scenario",
      agentType: "Agent Type",
      judgerModel: "Judger Model",
      criteriaLabel: "Criteria",
      batchSummary: "Batch Summary",
      total: "Total",
      close: "Close",
      runEvaluationButton: "Run Evaluation",
      batchInfoPrefix: "This will run evaluations for",
      batchInfoSuffix: "active scenario(s).",

      // Results
      lastResult: "Last Result",
      noResults: "No results yet",

      // Confirmations
      deleteConfirm: "Are you sure you want to delete this scenario?",

      // Success/Error messages
      createSuccess: "Scenario created successfully",
      updateSuccess: "Scenario updated successfully",
      deleteSuccess: "Scenario deleted successfully",
      promoteSuccess: "Chat promoted to scenario successfully",
      runSuccess: "Evaluation completed",
      error: "Operation failed",

      // History Panel
      historyPanel: {
        title: "Evaluation History",
        scoreTrend: "Score Trend",
        recentRuns: "Recent {n} runs",
        noHistory: "No evaluation records",
        checkRunsPanelHint:
          'If you just started an evaluation, click the "Run Records" button at the top to check progress',
        expandDetails: "Expand details",
        collapseDetails: "Collapse details",
        agentResponse: "Agent Response",
        responseTime: "Response Time",
        executedAt: "Executed At",
        executedBy: "Executed By",
        metrics: "Metrics",
        resultsList: "Evaluation Records",
        running: "Running...",
        failed: "Execution Failed",
      },

      // Result Detail Dialog
      resultDetail: {
        title: "Evaluation Result Details",
        viewDetails: "View Details",
        overallScore: "Overall Score",
        metricsTitle: "Metric Details",
        judgmentReason: "LLM Judgment Reason",
        executedAt: "Executed At",
        model: "Model",
        responseTime: "Response Time",
        executedBy: "Executed By",
        runningHint:
          "Evaluation is in progress, please refresh later to see results",
      },

      // Runs Panel
      runsPanel: {
        title: "Evaluation Runs",
        runId: "Run ID",
        totalScenarios: "scenarios",
        passed: "Passed",
        failed: "Failed",
        skipped: "Skipped",
        averageScore: "Average",
        duration: "Duration",
        viewDetails: "View Details",
        noRuns: "No run records",
        noResults: "No results available",
        autoRefreshing: "Auto-refreshing...",
        statusRunning: "Running",
        statusCompleted: "Completed",
        statusFailed: "Failed",
      },

      // Results Panel
      resultsPanel: {
        title: "Evaluation Results",
        loading: "Loading results...",
        noResults: "No results yet",
        noResultsHint: "Run an evaluation to see results here",
        averageScore: "Average Score",
        totalRuns: "Total Runs",
        scenarioName: "Scenario Name",
        description: "Description",
        testInput: "Test Input",
        noInput: "No input",
        history: "Evaluation History",
        detailedMetrics: "Detailed Metrics",
        agentResponse: "Agent Response",
        evaluationNotes: "Evaluation Notes",
      },

      // Common texts
      optional: "Optional",
      noDescription: "No description",
      createFirstScenario: "Create your first scenario to get started",
      input: "Input",

      // Time formatting
      time: {
        justNow: "Just now",
        minutesAgo: "{n} minutes ago",
        hoursAgo: "{n} hours ago",
        daysAgo: "{n} days ago",
      },

      // Metric Definitions
      metricDefinitions: {
        pageTitle: "Metric Definitions",
        pageDescription:
          "Manage evaluation metric definitions, view built-in prompts, and customize GEval metrics",
        createMetric: "Create Custom Metric",
        builtinMetrics: "Built-in Metrics",
        customMetrics: "Custom Metrics (GEval)",
        viewPrompt: "View",
        editMetric: "Edit",
        deleteMetric: "Delete",
        metricName: "Metric Name",
        displayName: "Display Name",
        metricType: "Metric Type",
        defaultThreshold: "Default Threshold",
        thresholdDirection: "Threshold Direction",
        higherBetter: "Higher is better (≥)",
        lowerBetter: "Lower is better (≤)",
        applicableAgents: "Applicable Agents",
        allAgents: "All",
        systemMetric: "System Metric",
        customGeval: "Custom GEval",
        criteria: "Evaluation Criteria",
        criteriaPlaceholder: "Describe how to evaluate response quality...",
        criteriaHint:
          "Criteria is passed to the LLM model to judge response quality. Be specific for consistent results. Use bullet points.",
        evaluationSteps: "Evaluation Steps (Advanced)",
        evaluationStepsHint:
          "Define specific evaluation steps for more precise control. Overrides Criteria when set.",
        addStep: "Add Step",
        removeStep: "Remove",
        noMetrics: "No custom metrics yet",
        builtinPromptNote:
          "This is a DeepEval built-in metric. The prompt is maintained by the framework and cannot be modified. To customize evaluation logic, create a new GEval custom metric.",
        promptViewer: {
          title: "View Metric Prompt",
          type: "Type",
          source: "Source",
          threshold: "Default Threshold",
          prompt: "Evaluation Prompt",
          readOnlyNote: "This prompt is read-only and cannot be edited",
        },
        editor: {
          createTitle: "Create Custom Metric",
          editTitle: "Edit Metric",
          nameHint: "Unique identifier, use lowercase letters and underscores",
          displayNameHint: "Name displayed in the UI",
          descriptionHint: "Describe what this metric evaluates",
          // Evaluation Steps
          evaluationStepsTitle: "Evaluation Steps (Advanced)",
          optional: "Optional",
          stepsVsCriteriaTitle: "Evaluation Steps vs Criteria",
          stepsExplanation1:
            "Criteria: Descriptive evaluation requirements, suitable for most scenarios",
          stepsExplanation2:
            "Steps: Step-by-step instructions, LLM will execute each step in order",
          stepsExplanation3:
            "If steps are set, they will override the criteria",
          exampleTitle: "Example - Astrological Accuracy Steps:",
          exampleStep1:
            "Check if zodiac signs and planet positions match user birth chart data",
          exampleStep2:
            "Verify aspect interpretations follow astrological principles",
          exampleStep3:
            "Evaluate the professionalism and accuracy of terminology",
          exampleStep4:
            "Give a comprehensive score of 1-5 based on the above checks",
          yourSteps: "Your Evaluation Steps",
          noStepsYet: "No evaluation steps yet, click the button below to add",
          stepPlaceholder: "Describe what to check or evaluate in this step...",
          addStep: "Add Step",
          removeStep: "Remove",
          // AI Generation
          aiAssistantTitle: "AI Smart Design",
          aiAssistantDescription:
            "Describe what you want to evaluate, AI will generate metric configuration based on DeepEval best practices",
          aiDescriptionPlaceholder:
            "e.g., Evaluate the accuracy of astrological interpretations, including zodiac positions, planetary aspects, house interpretations...",
          generating: "Generating...",
          generateMetric: "Generate Metric",
          minChars: "At least 10 characters",
          chars: "characters",
          aiExplanation: "AI Design Explanation",
          aiDescriptionTooShort:
            "Please provide a more detailed description (at least 10 characters)",
          aiGenerationFailed: "AI generation failed. Please try again.",
          selectModel: "Select Model",
          selectModelPlaceholder: "-- Select a model --",
          selectModelFirst: "Please select a model first",
        },
        deleteConfirm:
          "Are you sure you want to delete this metric? This action cannot be undone.",
        deleteSystemError: "System metrics cannot be deleted",
        createSuccess: "Metric created successfully",
        updateSuccess: "Metric updated successfully",
        deleteSuccess: "Metric deleted successfully",
      },

      // Test Suites
      testSuites: {
        // Selection
        selectForSuite: "Select for Suite",
        exitSelect: "Exit Selection",
        createSuite: "Create Suite",
        selectAll: "Select all",
        deselectAll: "Deselect all",
        selected: "selected",

        // Dialog
        createTitle: "Create Test Suite",
        editTitle: "Edit Test Suite",
        name: "Name",
        namePlaceholder: "Enter suite name",
        description: "Description",
        descriptionPlaceholder: "Enter description (optional)",
        tags: "Tags",
        tagsPlaceholder: "Type and press Enter to add",
        priority: "Priority",
        scenarioCount: "Scenarios included",
        scenarios: "scenarios",
        cancel: "Cancel",
        create: "Create",
        save: "Save",

        // Run
        selectSuite: "Select Test Suite",
        runAllActive: "Run all active scenarios",
        runSuite: "Run Test Suite",
        runSuiteInfo: "Run",
      },
    },
    evaluation: {
      pageTitle: "Evaluation Center",
      pageDescription:
        "Manage evaluation scenarios, metric definitions, and test suites",
      tabs: {
        scenarios: "Scenarios",
        metrics: "Metrics",
        suites: "Test Suites",
      },
    },
    monitoring: {
      // Page
      pageTitle: "Service Monitoring",
      pageDescription: "Real-time health and performance metrics",
      lastUpdated: "Last updated",

      // Tabs
      tabs: {
        services: "Services",
        cache: "Cache",
        storage: "Storage",
        queue: "Extraction Queue",
      },

      // Queue
      queue: {
        title: "Extraction Queue",
        status: "Queue Status",
        running: "Running",
        stopped: "Stopped",
        queueSize: "Queue Size",
        processed: "Processed",
        success: "Success",
        failed: "Failed",
        merged: "Merged",
        savedCalls: "Saved Calls",
        mergeRate: "Merge Rate",
        costSaving: "Cost Saving",
        deadLetter: "Dead Letter Queue",
        deadLetterDesc:
          "Failed tasks after retries are moved to the dead letter queue for manual retry or cleanup",
        retryAll: "Retry All",
        clearAll: "Clear Queue",
        retry: "Retry",
        noTasks: "No failed tasks",
        taskId: "Task ID",
        userId: "User ID",
        memoryType: "Type",
        error: "Error",
        failedAt: "Failed At",
        retryCount: "Retries",
        confirmClear:
          "Are you sure you want to clear the dead letter queue? This action cannot be undone.",
        confirmRetryAll: "Are you sure you want to retry all failed tasks?",
        clearSuccess: "Cleared {count} tasks",
        retrySuccess: "Retried {count} tasks",
        resetStats: "Reset Stats",
        confirmReset: "Are you sure you want to reset queue statistics?",
        resetSuccess: "Statistics have been reset",
      },

      // Service Health
      serviceHealth: {
        title: "Service Health Status",
        services: "Services",
        healthy: "Healthy",
        unhealthy: "Unhealthy",
        error: "Error",
        unknown: "Unknown",
        allOk: "All OK",
        issues: "Issues",
        overallStatus: "Overall Status",
        latency: "Latency",
        openService: "Open Service",
      },

      // Cache Performance
      cache: {
        title: "Cache Performance",
        hitRate: "Hit Rate",
        hits: "Hits",
        misses: "Misses",
        avgLatency: "Avg Latency",
        totalRequests: "total requests",
        successfulRetrievals: "Successful cache retrievals",
        fetchFailures: "Cache fetch failures",
        avgResponseTime: "Average response time",
        unavailable: "Cache data unavailable",
      },

      // Charts
      charts: {
        cacheHitRate: "Cache Hit Rate",
        hitRateLabel: "hit rate",
        avgLatency: "Average Latency",
        total: "Total",
        cacheLatency: "Cache",
        backend: "Backend",
        requestDistribution: "Request Distribution",
        noData: "No data",
      },

      // Cache Details
      cacheDetails: {
        title: "Cache Performance",
        totalRequests: "Total Requests",
        cachedKeys: "Cached Keys",
        cacheSize: "Cache Size",
        errors: "Errors",
        redisConnection: "Redis Connection",
        teiBackend: "TEI Backend",
        connected: "Connected",
        disconnected: "Disconnected",
        healthy: "Healthy",
        unhealthy: "Unhealthy",
        clear: "Clear",
        clearConfirm:
          "Clear all cached embeddings? This action cannot be undone.",
        clearSuccess: "Cleared {count} cached embeddings",
        clearError: "Failed to clear cache",
      },

      // Refresh Control
      refresh: {
        autoRefresh: "Auto-refresh",
        interval: "Interval",
        manual: "Manual",
        refreshButton: "Refresh",
        refreshing: "Refreshing...",
      },

      // Errors
      errors: {
        loadFailed: "Failed to load monitoring data",
        tryAgain: "Try again",
      },
    },

    // Classification Analytics
    classificationAnalytics: {
      pageTitle: "Classification Analytics",
      pageDescription:
        "Skill matching and Response Type classification performance analysis",
      lastUpdated: "Last updated",

      tabs: {
        skillExecution: "Skill Execution",
        classification: "Response Classification",
      },

      timeRange: {
        label: "Time Range",
        last24h: "Last 24 Hours",
        last7d: "Last 7 Days",
        last30d: "Last 30 Days",
      },

      skillExecution: {
        title: "Skill Execution Analytics",
        stats: {
          totalExecutions: "Total Executions",
          activeSkills: "Active Skills",
          autoMatchRate: "Auto Match Rate",
          ruleMatchRate: "Rule Match Rate",
        },
        charts: {
          matchLayer: "Match Layer Distribution",
          matchSource: "Match Source Distribution",
          topSkills: "Top 10 Skills",
        },
        recentExecutions: "Recent Executions",
        noData: "No data available",
        skillId: "Skill ID",
        query: "User Query",
        matchSource: "Match Source",
        matchedRule: "Matched Rule",
        confidence: "Confidence",
        time: "Time",
      },

      classification: {
        title: "Response Classification Analytics",
        stats: {
          totalClassifications: "Total Classifications",
          quickResponseRate: "Quick Response Rate",
          avgQueryLength: "Avg Query Length",
          avgLatency: "Avg Latency",
          // Skill metrics
          ruleMatchRate: "Rule Match Rate",
          avgRuleMatchTime: "Avg Rule Match Time",
          avgLlmMatchTime: "Avg LLM Match Time",
          avgPromptInjection: "Avg Prompt Injection",
        },
        charts: {
          responseType: "Response Type Distribution",
          avgConfidence: "Average Confidence Comparison",
          methodPerformance: "Classification Method Performance",
          count: "Count",
          latency: "Latency (ms)",
          // Skill charts
          skillMatchLayer: "Skill Match Layer Distribution",
          topMatchedSkills: "Top Matched Skills",
        },
        recentClassifications: "Recent Classifications",
        noData: "No data available",
        responseType: "Response Type",
        method: "Classification Method",
        latencyMs: "Latency (ms)",
      },

      export: {
        button: "Export Data",
        csv: "Export CSV",
        json: "Export JSON",
      },
      refresh: "Refresh",
      loading: "Loading...",
      error: "Failed to load",
      quick: "Quick",
      deep: "Deep",
    },

    // Users Management
    users: {
      pageTitle: "User Management",
      pageDescription: "Manage users and beta tester access",
      refresh: "Refresh",
      loading: "Loading users...",
      error: "Failed to load users",

      // Stats
      stats: {
        totalUsers: "Total Users",
        admins: "Admins",
        betaTesters: "Beta Testers",
        roleDistribution: "Role Distribution",
      },

      // Filters
      filters: {
        searchPlaceholder:
          "Search by username, nickname, email, or real name...",
        allUsers: "All Users",
        betaTesters: "Beta Testers",
        nonBeta: "Non-Beta",
      },

      // Batch Actions
      batch: {
        selected: "{count} user(s) selected",
        enableBeta: "Enable Beta",
        disableBeta: "Disable Beta",
        clearSelection: "Clear Selection",
      },

      // Table
      table: {
        user: "User",
        contact: "Contact",
        role: "Role",
        betaTester: "Beta Tester",
        created: "Created",
        actions: "Actions",
      },

      // Roles
      roles: {
        free: "Free",
        vip: "VIP",
        premium: "Premium",
        admin: "Admin",
      },

      // Actions
      actions: {
        makeBeta: "Make Beta",
        removeBeta: "Remove Beta",
        makeAdmin: "Make Admin",
        revokeAdmin: "Revoke Admin",
        resources: "Resources",
        viewResources: "View Resources",
      },

      // Status
      status: {
        beta: "Beta",
        notBeta: "No",
      },

      // Pagination
      pagination: {
        showing: "Showing {start} to {end} of {total} users",
        page: "Page {current} of {total}",
      },
    },

    // System Settings
    systemSettings: {
      pageTitle: "System Settings",
      pageDescription: "Configure system-wide settings and integrations",

      tabs: {
        mcpServers: "MCP Services",
        mem0Config: "Memory",
        firstMessage: "First Message",
        artifactTypes: "Artifacts",
        toolEnhancement: "Tool Display",
        toolCombo: "Tool Combos",
        promptTemplates: "Prompts",
        outputSchemas: "Output Schemas",
        exportImport: "Import/Export",
        auditLogs: "Change Logs",
      },

      audit: {
        viewLogsFor: "View audit logs for:",
        mcpServersLog: "MCP Servers Audit Log",
        mem0ConfigLog: "Mem0 Configuration Audit Log",
        firstMessageLog: "First Message Configuration Audit Log",
        artifactTypesLog: "Artifact Types Configuration Audit Log",
        toolEnhancementLog: "Tool Enhancement Configuration Audit Log",
        toolComboLog: "Tool Combos Configuration Audit Log",
        promptTemplatesLog: "Prompt Templates Configuration Audit Log",
        outputSchemasLog: "Output Schemas Configuration Audit Log",
      },

      // MCP Servers Panel
      mcp: {
        title: "MCP Servers",
        description:
          "Manage Model Context Protocol servers for tool integration",
        warningBanner:
          'Warning: Changes to MCP server configuration affect ALL users. After saving, click "Reload Manager" to apply changes immediately.',
        editJson: "Edit JSON",
        reloadManager: "Reload Manager",
        addServer: "Add Server",
        noServers: "No MCP servers configured",
        addFirstServer: "Add your first server",
        aboutMcp: "About MCP Servers",
        aboutMcpItems: [
          "STDIO servers run as local processes (e.g., Python scripts)",
          "HTTP servers connect to remote endpoints",
          "Test connections to verify server availability",
          "Reload manager after making changes to apply them",
        ],
        version: "Version",
        lastUpdatedBy: "Last updated by",
        jsonEditorTitle: "Edit MCP Configuration (JSON)",
        jsonEditorHint:
          "Edit the JSON directly. Each server needs: command/url, transport type, status.",
        cancel: "Cancel",
        saving: "Saving...",
        saveConfiguration: "Save Configuration",
        loading: "Loading MCP servers...",
        loadFailed: "Failed to load MCP servers",
        saveFailed: "Failed to save MCP server",
        deleteFailed: "Failed to delete MCP server",
        reloadSuccess: "MCP manager reloaded successfully",
        reloadFailed: "Failed to reload MCP manager",
        jsonSaveSuccess: "MCP configuration saved successfully",
        jsonMustHaveMcpServers: 'JSON must have a "mcpServers" object',
      },

      // Mem0 Config Panel
      mem0: {
        title: "Mem0 System Default Prompts",
        description:
          "Configure default prompts used for memory extraction and management (agents can override these)",
        warningBanner:
          "Warning: Changes to system default prompts affect agents that do NOT have custom prompts enabled. Agents with custom prompts will continue using their own.",
        helpTitle: "Mem0 Dual Memory Architecture",
        helpDescription:
          "Configure system-wide default prompts and categories for both User and Agent memory extraction. Each agent can choose to use these defaults or override with custom values.",
        helpItems: [
          "User Memory Extraction: Extracts facts from user messages",
          "Agent Memory Extraction: Extracts facts from assistant messages",
          "Memory Update: Merges new facts with existing memories",
          "Categories: Classifies memories into structured categories",
        ],
        documentation: "Mem0 Documentation",
        reloadFromDatabase: "Reload from Database",
        saving: "Saving...",
        saveSystemDefaults: "Save System Defaults",
        userMemoryPrompt: "User Memory Extraction Prompt",
        agentMemoryPrompt: "Agent Memory Extraction Prompt",
        memoryUpdatePrompt: "Memory Update Prompt",
        userMemoryCategories: "User Memory Categories (JSON)",
        agentMemoryCategories: "Agent Memory Categories (JSON)",
        resetToHardcoded: "Reset to Hardcoded Default",
        promptMinMax:
          "Prompt for extracting memories from messages. Minimum 50 characters, maximum 8000 characters.",
        promptUpdateMinMax:
          "Prompt for updating/merging memories. Minimum 50 characters, maximum 4000 characters.",
        categoriesHint:
          "Categories for classifying memories. Each category needs: description, temporal.",
        loading: "Loading Mem0 configuration...",
        loadFailed: "Failed to load Mem0 configuration",
        saveSuccess: "Mem0 system defaults saved successfully",
        saveFailed: "Failed to save configuration",
        resetSuccess: "Reset to hardcoded default",
        resetFailed: "Failed to reset",
        noDefaultPrompt: "No default prompt returned from API",
        noDefaultCategories: "No default categories returned from API",
        version: "Version",
        lastUpdatedBy: "Last updated by",
        validation: {
          userPromptMin:
            "User memory extraction prompt must be at least 50 characters",
          agentPromptMin:
            "Agent memory extraction prompt must be at least 50 characters",
          updatePromptMin:
            "Memory update prompt must be at least 50 characters",
          userPromptMax:
            "User memory extraction prompt cannot exceed 8000 characters",
          agentPromptMax:
            "Agent memory extraction prompt cannot exceed 8000 characters",
          updatePromptMax: "Memory update prompt cannot exceed 4000 characters",
        },
      },

      // First Message Panel
      firstMessage: {
        title: "First Message System Default",
        description:
          "Configure the default instruction injected on first message in a new topic",
        warningBanner:
          'Warning: Changes to the system default instruction affect agents using "system" mode. Agents with custom first message instructions will not be affected.',
        reloadFromDatabase: "Reload from Database",
        saving: "Saving...",
        saveSystemDefault: "Save System Default",
        instructionTitle: "System Default Instruction",
        instructionPlaceholder:
          "Enter the system default first message instruction...",
        instructionLength: "Instruction Length",
        instructionMinMax: "Minimum: 10, Maximum: 5000 characters",
        howItWorksTitle: "How It Works",
        howItWorksItems: [
          "This instruction is injected into the system prompt on the first message of a new topic",
          "Agents can choose to use this system default or define their own custom instruction",
          "Agent-level setting: enable_first_message_instruction controls if this feature is active",
          'Agent-level setting: first_message_mode controls "system" vs "custom"',
        ],
        loading: "Loading first message configuration...",
        loadFailed: "Failed to load first message configuration",
        saveSuccess: "First message instruction saved successfully",
        saveFailed: "Failed to save configuration",
        version: "Version",
        lastUpdatedBy: "Last updated by",
        validation: {
          minLength: "First message instruction must be at least 10 characters",
          maxLength: "First message instruction cannot exceed 5000 characters",
        },
      },

      // Artifact Types Panel
      artifactTypes: {
        title: "Artifact Types",
        description:
          "Configure artifact types for user-generated content (lucky charms, podcasts, reports)",
        addType: "Add Type",
        noTypes: "No artifact types configured",
        addFirstType: "Add your first artifact type",
        editType: "Edit Artifact Type",
        addTypeTitle: "Add Artifact Type",
        typeId: "Type ID",
        typeIdHint:
          "Lowercase letters, numbers, underscores. Must start with a letter.",
        icon: "Icon",
        iconOptions: {
          package: "Package",
          sparkles: "Sparkles",
          audio: "Audio",
          document: "Document",
          image: "Image",
          calendar: "Calendar",
        },
        nameEnglish: "Name (English)",
        nameChinese: "Name (Chinese)",
        descriptionLabel: "Description",
        descriptionHint: "Brief description of this artifact type",
        contentTypes: "Content Types",
        contentTypesHint: "image/png, image/jpeg, audio/mpeg",
        rotationPeriod: "Rotation Period",
        rotationOptions: {
          none: "None (Permanent)",
          daily: "Daily",
          weekly: "Weekly",
          monthly: "Monthly",
        },
        maxStored: "Max Stored",
        maxStoredHint: "e.g., 7 (leave empty for unlimited)",
        cancel: "Cancel",
        saving: "Saving...",
        save: "Save",
        confirmDelete: "Delete artifact type",
        loading: "Loading artifact types...",
        loadFailed: "Failed to load artifact types",
        saveFailed: "Failed to save artifact type",
        deleteFailed: "Failed to delete artifact type",
        version: "Version",
        lastUpdatedBy: "Last updated by",
        aboutTitle: "About Artifact Types",
        aboutItems: [
          "Artifact types define categories of user-generated content (lucky charms, podcasts, reports)",
          'Rotation period determines when artifacts expire and new ones become "current"',
          "Max stored limits how many artifacts to keep per user (oldest are marked expired)",
          "Artifacts are user-level, each user has their own artifacts",
        ],
        apiAccessTitle: "API Access (App Integration)",
        workflowTitle: "Workflow Integration (Creating Artifacts)",
        fieldRefTitle: "Field Reference",
        adminOpsTitle: "Admin Operations (Cleanup & Management)",
      },

      // Audit Log Viewer
      auditLog: {
        loading: "Loading audit logs...",
        loadFailed: "Failed to load audit logs",
        noLogs: "No audit logs available",
        noLogsHint: "Changes to this configuration will be logged here",
        showing: "Showing {current} of {total} log entries",
        version: "Version",
        reason: "Reason",
        ip: "IP",
      },

      // Export/Import Panel
      exportImport: {
        exportTitle: "Export Configurations",
        exportDescription:
          "Download all agent configurations as a JSON file. This includes all settings, prompts, and tool configurations for each agent.",
        exporting: "Exporting...",
        exportAll: "Export All Configurations",
        exportSuccess: "Agent configurations exported successfully",
        exportFailed: "Export failed",
        importTitle: "Import Configurations",
        importDescription:
          "Import agent configurations from a JSON file. You can choose how to handle existing configurations.",
        selectFile: "Select File",
        importMode: "Import Mode",
        importModeOptions: {
          createOnly: "Create Only - Skip existing agents",
          overwrite: "Overwrite - Replace existing agents completely",
          merge: "Merge - Update existing agents, keep unspecified fields",
        },
        importModeHints: {
          createOnly: "Only creates new agents. Existing agents are skipped.",
          overwrite: "Completely replaces existing agents with imported data.",
          merge:
            "Updates specified fields, keeps existing values for unspecified fields.",
        },
        skipErrors: "Skip errors and continue with remaining imports",
        skipErrorsHint: "If unchecked, import will stop on first error.",
        importPreview: "Import Preview",
        foundConfigs: "Found {count} agent configurations:",
        importResults: "Import Results",
        imported: "Imported",
        skipped: "Skipped",
        errors: "Errors",
        importing: "Importing...",
        importConfigurations: "Import Configurations",
        aboutTitle: "About Import/Export",
        aboutItems: [
          "Export creates a JSON file with all agent configurations",
          "Import file must have a configurations array",
          "Each configuration needs at least an agent_type",
          "Use this for backup, migration, or sharing configurations between environments",
        ],
        noFileSelected: "No file selected",
        invalidJson: "Invalid JSON file",
        invalidFormat: "Invalid format: missing configurations array",
        importCompleted: "Import completed",
        importFailed: "Import failed",
      },

      // Tool Enhancement Panel
      toolEnhancement: {
        title: "Tool Enhancement",
        description: "Configure display names and descriptions for tools",
        loading: "Loading...",
        loadFailed: "Failed to load",
        saveSuccess: "Saved successfully",
        saveFailed: "Failed to save",
        save: "Save",
        saving: "Saving...",
        reload: "Reload",
        searchPlaceholder: "Search tools...",
        noToolSelected: "Select a tool from the left panel to configure",
        configured: "Configured",
        notConfigured: "Not configured",
        displayNameCn: "Display Name (Chinese)",
        displayNameEn: "Display Name (English)",
        descriptionCn: "Description (Chinese)",
        descriptionEn: "Description (English)",
        icon: "Icon",
        displayOrder: "Display Order",
        enabled: "Enabled",
        clearConfig: "Clear Config",
        toolInfo: "Tool Info",
        technicalName: "Technical Name",
        originalDescription: "Original Description",
        allCategories: "All Categories",
        orphanConfigs: "Orphan Configs",
        orphanConfigsDesc: "Configurations for tools that no longer exist",
        cleanupOrphans: "Clean Up",
        orphanWarning: "This tool no longer exists in the system",
        category: "Category",
        categoryOverride: "Category Override",
        categoryOverrideHint: "Leave empty to use default category",
        originalCategory: "Default Category",
        useDefaultCategory: "Use Default",
        toolKey: "Tool Key",
        // Category translations
        categories: {
          search: "Search",
          math: "Math",
          development: "Development",
          filesystem: "File System",
          reasoning: "Reasoning",
          astrology: "Astrology",
          fortune: "Fortune",
          memory: "Memory",
          utility: "Utility",
          general: "General",
        } as Record<string, string>,
        // AI Generation
        aiGenerate: "AI Generate Descriptions",
        aiGenerateDesc:
          "Analyze the original description and automatically generate user-friendly display names and descriptions.",
        aiGenerateButton: "Generate",
        aiGenerating: "Generating...",
        aiSelectModel: "Select Model",
        aiSelectModelPlaceholder: "Select a model...",
        aiExplanation: "AI Analysis",
        aiNoDescription: "No original description available for AI generation",
        // Reload Enhancer
        reloadEnhancer: "Apply to LLM",
        reloadEnhancerDesc:
          "Apply saved configurations to LLM tool descriptions",
        reloadEnhancerSuccess: "Tool descriptions reloaded",
        reloadEnhancerFailed: "Failed to reload",
        // Tool Testing
        testTool: "Test Tool",
        testToolDesc: "Directly invoke the tool with parameters for debugging",
        testParameters: "Test Parameters",
        testParametersPlaceholder: "Enter parameters in JSON format...",
        runTest: "Run Test",
        running: "Running...",
        testResult: "Test Result",
        testSuccess: "Execution Successful",
        testFailed: "Execution Failed",
        executionTime: "Execution Time",
        errorType: "Error Type",
        closeModal: "Close",
        noParameters: "This tool requires no parameters",
        invalidJson: "Invalid JSON format",
        requiredFields: "Required Fields",
        optionalFields: "Optional Fields",
        paramType: "Type",
        paramDefault: "Default",
        requiresUserContext: "Requires User Context",
        requiresUserContextDesc:
          "This tool requires user context. Your admin user ID will be used for testing.",
        formMode: "Form Mode",
        jsonMode: "JSON Mode",
        useDefaultValue: "Use Default",
      },

      // Prompt Templates Panel
      promptTemplates: {
        title: "Prompt Templates",
        description:
          "Configure system prompt templates for image generation, push notifications, workflows, and more",
        addTemplate: "Add Template",
        noTemplates: "No prompt templates configured",
        addFirstTemplate: "Add your first prompt template",
        editTemplate: "Edit Template",
        addTemplateTitle: "Add New Template",
        templateKey: "Template Key",
        templateKeyHint: "Format: domain.operation (e.g., image.generate)",
        nameEnglish: "English Name",
        nameChinese: "Chinese Name",
        descriptionEn: "English Description",
        descriptionZh: "Chinese Description",
        systemPromptZh: "Chinese System Prompt",
        systemPromptEn: "English System Prompt",
        displayOrder: "Display Order",
        category: "Category",
        categoryOptions: {
          image: "Image",
          push: "Push",
          workflow: "Workflow",
        },
        isDefault: "Default Template",
        resetToDefault: "Reset to Default",
        resetSuccess: "Reset to default value",
        cancel: "Cancel",
        saving: "Saving...",
        save: "Save",
        confirmDelete: "Are you sure you want to delete this template?",
        cannotDeleteDefault:
          "Cannot delete default templates. Use reset to restore default values.",
        loading: "Loading...",
        loadFailed: "Failed to load templates",
        saveFailed: "Failed to save template",
        deleteFailed: "Failed to delete template",
        version: "Version",
        lastUpdatedBy: "Last Updated By",
        searchPlaceholder: "Search templates...",
        allCategories: "All Categories",
        previewPrompt: "Preview Prompt",
        chinese: "Chinese",
        english: "English",
      },

      // Output Schemas Panel
      outputSchemas: {
        title: "Output Schemas",
        description:
          "Manage centralized JSON output schema definitions for agent responses",
        addSchema: "Add Schema",
        noSchemas: "No output schemas configured",
        noSchemasMatch: "No schemas match your search",
        addFirstSchema: "Add your first schema",
        editSchema: "Edit Output Schema",
        searchPlaceholder: "Search schemas...",
        scopeAll: "All Scopes",
        scopeGlobal: "Global Only",
        scopeAgent: "Agent-Specific",
        statusAll: "All Status",
        statusEnabled: "Enabled",
        statusDisabled: "Disabled",
        schemaKey: "Schema Key",
        schemaKeyHint:
          "Lowercase letters, numbers, underscores. Must start with a letter.",
        nameEnglish: "Name (English)",
        nameChinese: "Name (Chinese)",
        descriptionEn: "Description (English)",
        descriptionZh: "Description (Chinese)",
        scope: "Scope",
        agentType: "Agent Type",
        jsonSchema: "JSON Schema",
        exampleOutput: "Example Output",
        formatInstruction: "Format Instruction",
        formatInstructionHint:
          "Use {schema} as a placeholder for the JSON schema",
        displayOrder: "Display Order",
        enabled: "Enabled",
        loading: "Loading output schemas...",
        loadFailed: "Failed to load output schemas",
        saveFailed: "Failed to save output schema",
        deleteFailed: "Failed to delete output schema",
        toggleFailed: "Failed to toggle status",
        totalSchemas: "Total schemas",
        showing: "Showing",
        aboutTitle: "About Output Schemas",
        aboutItem1: "Global schemas are available to all agents",
        aboutItem2:
          "Agent-specific schemas are only available to the specified agent type",
        aboutItem3:
          "Use {schema} placeholder in format_instruction to inject the JSON schema",
        aboutItem4:
          "Disabled schemas are hidden from agent selection dropdowns",
      },
    },

    // Agent Config
    agentConfig: {
      // List Page
      listTitle: "Agent Configurations",
      listDescription: "Manage AI agent configurations and settings",
      createAgent: "Create Agent",
      searchPlaceholder: "Search agents...",
      showInactive: "Show inactive agents",
      noAgentsMatch: "No agents match your search",
      noAgentsMatchHint: "Try adjusting your search terms",
      noAgentsYet: "No agents configured yet",
      noAgentsYetHint: "Create your first agent to get started",
      showingAgents: "Showing {current} of {total} agents",
      activeCount: "{count} active",
      inactive: "Inactive",
      version: "Version",
      updated: "Updated",

      // Detail Page
      loadingConfig: "Loading agent configuration...",
      backToList: "Back to List",
      agentNotFound: "Agent not found",
      copy: "Copy",
      unsavedChanges: "Unsaved changes",
      saving: "Saving...",
      saveSuccess: "Configuration saved successfully!",
      saveFailed: "Failed to save",
      active: "Active",
      updatedViaAdmin: "Updated via admin interface",

      // Tabs
      tabSettings: "Settings",
      tabHooks: "Hooks",
      tabTools: "Tools",
      tabPrompt: "Prompt",
      tabSchemas: "Schemas",
      tabSkills: "Skills",
      tabJson: "JSON",
      tabVersions: "Versions",
      // New tabs
      tabBasic: "Basic",
      tabMemory: "Memory",
      tabKnowledge: "Knowledge",
      tabAstrology: "Astrology+",
      tabEmotion: "Emotion",
      tabClassifier: "Classifier",
      tabTeam: "Team",
      tabAdvanced: "Advanced",

      // Actions
      actions: {
        browseTools: "Browse Tools",
      },

      // Output Format Section
      outputFormat: {
        title: "Output Format",
        createSchema: "Create Schema",
        manageSchemas: "Manage Schemas",
        noSchemasWarning:
          "No output schemas defined. Create a schema to enable structured JSON output.",
        createFirstSchema: "Create First Schema",
        noDefault: "No format constraint (free output)",
        defaultSchemaHelp:
          "Default schema used when no output_schema_key is specified in API request.",
        availableSchemas: "Available Schema Keys (for API)",
        apiHint:
          "Pass output_schema_key in API request to use a specific schema.",
      },

      // Create/Copy Agent Dialog
      createAgentDialog: {
        copyAgent: "Copy Agent",
        createNewAgent: "Create New Agent",
        agentType: "Agent Type",
        agentTypePlaceholder: "e.g., my_agent",
        agentTypeHint:
          "Unique identifier. Lowercase letters, numbers, underscores, and hyphens only.",
        name: "Name",
        namePlaceholder: "e.g., My Agent",
        description: "Description",
        descriptionPlaceholder: "Describe what this agent does...",
        copyingFrom: "Copying configuration from",
        creating: "Creating...",
        agentTypeRequired: "Agent type is required",
        nameRequired: "Name is required",
        agentTypeInvalid:
          "Agent type must start with a letter and contain only lowercase letters, numbers, underscores, and hyphens",
      },

      // Version History
      versionHistory: {
        title: "Version History",
        loading: "Loading version history...",
        current: "Current",
        restore: "Restore",
        noHistory: "No version history available",
        createdBy: "Created by",
        versionConfig: "Version {version} Configuration",
        confirmRestore:
          "Activate version {version}? This will replace the current configuration.",
        restoreReason: "Activated via admin interface",
        editing: "Editing",
        clickToShowJson: "Click the eye icon to show JSON configuration",
        viewConfig: "View Config",
      },

      // JSON Preview
      jsonPreview: {
        title: "JSON Configuration",
        copy: "Copy",
        copied: "Copied",
        download: "Download",
      },

      // Tools Browser Modal
      toolsBrowser: {
        customTools: "Custom Tools",
        builtinToolkits: "Builtin Toolkits",
        customToolsDesc: "Select individual tools from available categories",
        builtinToolkitsDesc: "Select builtin tool modules",
        filterByCategory: "Filter by category:",
        allCategories: "All categories",
        toolsSelected: "{count} tool(s) selected",
        loadingTools: "Loading tools...",
        availableTools: "Available Tools",
        selectedTools: "Selected Tools",
        previewConfig: "Preview Configuration",
        cancel: "Cancel",
        apply: "Apply",
        noBuiltinToolkits: "No builtin toolkits available",
        noCustomTools: "No custom tools available",
        noToolsSelected: "No tools selected",
        toolsCount: "{count} tools",
        builtinToolsBrowser: "Builtin Tools Browser",
        customToolsBrowser: "Custom Tools Browser",
        format: "Format",
        noItemsConfigured: "No items configured",
        toolkits: "Toolkits",
        enabledTools: "Enabled Tools",
        servers: "Servers",
        modules: "Modules",
        allToolsEnabled: "(all tools enabled)",
        toolNotFound: "Tool not found - please remove",
        serverNotFound: "Server not found - please remove",
        willMigrateOnSave: "will migrate on save",
        toolNotFoundRemove: "Tool not found - please remove",
        invalidToolsWarning:
          "Some selected tools no longer exist. Please remove them before applying.",
        removeInvalidToolsFirst: "Remove invalid tools first",
      },

      // MCP Tools Browser Modal
      mcpBrowser: {
        title: "MCP Tools Browser",
        refresh: "Refresh",
        systemServers: "System Servers",
        userServers: "User Servers",
        serversSelected: "{count} server(s) selected",
        toolsSelected: "{count} tool(s) selected",
        loadingServers: "Loading MCP servers...",
        availableServers: "Available Servers",
        selectedItems: "Selected Items",
        servers: "Servers",
        individualTools: "Individual Tools",
        noServersSelected: "No servers selected",
        noServersAvailable: "No MCP servers available",
        connected: "Connected",
        disconnected: "Disconnected",
        failedToConnect: "Failed to connect to this server",
        previewConfig: "Preview Configuration",
        cancel: "Cancel",
        apply: "Apply",
        toolsCount: "{count} tools",
        invalidItemsWarning:
          "Some selected servers/tools no longer exist. Please remove them before applying.",
        removeInvalidItemsFirst: "Remove invalid items first",
      },

      // Prompt Template Preview
      promptPreview: {
        title: "System Prompt Preview",
        buttonLabel: "Preview Prompt",
        copy: "Copy",
        copied: "Copied!",
        loading: "Loading...",
        refresh: "Refresh",
        generatePreview: "Generate Preview",
        usingTemplate: "Using template:",
        default: "(Default)",
        enabledSections: "{count} enabled sections",
        useRealData: "Use real user data",
        livePreview: "Live Preview",
        userId: "User ID",
        enterUserId: "Enter User ID...",
        useCurrent: "Use Current",
        usingCurrentUser: "Using current logged-in user",
        sampleMessage: "Sample User Message (triggers memory search)",
        enterSampleMessage: "Enter a sample user message...",
        realDataHint:
          "Runs all hooks with real data: user_facts, birth_chart, memory_context",
        sampleDataHint: "Preview uses generated test user with sample data.",
        previewFailed: "Preview Failed",
        template: "Template:",
        systemChars: "System: {count} chars",
        userChars: "User: {count} chars",
        user: "User:",
        hooksExecuted: "Hooks Executed ({count} total)",
        systemStage: "SYSTEM Stage:",
        userStage: "USER Stage:",
        noHooksExecuted: "No hooks executed",
        dataSources: "Data Sources ({count})",
        dataSourceHint: "real = loaded from database | empty = no data found",
        systemPrompt: "System Prompt:",
        enhancedUserMessage: "Enhanced User Message ({count} chars)",
        contextItems: "Context Items ({count}):",
        emptyStateTitle:
          'Click "Generate Preview" to see how the system prompt will be rendered',
        emptyStateDesc:
          "Live preview runs all hooks with real data and builds the exact prompt sent to LLM",
      },

      // Output Schemas Tab
      outputSchemas: {
        title: "Schemas",
        add: "Add",
        noSchemas: "No schemas defined",
        noSchemasHint: 'Click "Add" to create your first output schema',
        default: "Default",
        properties: "{count} properties",
        noSelection: "No schema selected",
        noSelectionHint: "Select a schema from the list or create a new one",
      },

      // Skills Tab
      skills: {
        title: "Skills",
        add: "Add Skill",
        noSkills: "No skills configured",
        noSkillsHint: "Add skills to enable dynamic instruction injection",
        noSelection: "No skill selected",
        noSelectionHint: "Select a skill from the list or add a new one",
        enabled: "Enabled",
        disabled: "Disabled",
        priority: "Priority",
        exclusionGroup: "Group",
        disabledWarning:
          'Skills are disabled. Enable "skills_enabled" in classifier settings to use skills.',
      },

      // Schema Editor
      schemaEditor: {
        aiGenerate: "AI Generate Schema",
        aiGenerateDesc:
          "Input your expected JSON output example, AI will analyze and generate the corresponding JSON Schema",
        selectModel: "Select Model",
        selectModelPlaceholder: "-- Select a model --",
        inputExample: "Input JSON Example",
        generating: "Generating...",
        generate: "Generate Schema",
        aiExplanation: "AI Design Explanation",
        key: "Key",
        keyValidHint:
          "Unique identifier for API usage. Lowercase letters, numbers, and underscores only.",
        keyInvalidHint:
          "Must start with a letter and contain only lowercase letters, numbers, and underscores.",
        name: "Name",
        namePlaceholder: "Schema name",
        description: "Description",
        descriptionPlaceholder: "Brief description of this schema",
        jsonSchema: "JSON Schema",
        validJson: "Valid JSON",
        jsonSchemaHint:
          "Define the expected output structure using JSON Schema format",
        exampleOutput: "Example Output (Optional)",
        exampleOutputHint: "Provide a sample output for reference",
        formatInstruction: "Format Instruction",
        formatInstructionHint:
          "Use {schema} placeholder to inject JSON Schema at runtime",
        defaultSchema: "Default Schema",
        setAsDefault: "Set as Default",
        deleteSchema: "Delete Schema",
        confirmDelete: 'Are you sure you want to delete "{name}"?',
        alertExampleRequired: "Please provide an example JSON",
        alertModelRequired: "Please select a model first",
        alertGenerateFailed: "AI generation failed:",
      },

      // Template Selector
      templateSelector: {
        title: "Prompt Template",
        description:
          "Select a template to define how the system prompt is structured for this agent.",
        useDefault: "Use Default",
        default: "Default",
        recommended: "Recommended",
        systemDefault: "System Default",
        sections: "Template Sections",
        sectionsCount: "{count} sections",
        updated: "Updated",
        noTemplates: "No templates available",
        loadingTemplates: "Loading templates...",
        previewTemplate: "Preview Template",
      },
    },

    // Hooks
    hooks: {
      pipelineTitle: "Hook Pipeline",
      loading: "Loading hooks pipeline...",
      errorLoading: "Error Loading Pipeline",
      noData: "No pipeline data available",
      enabledCount: "{enabled} / {total} enabled",
      showHidden: "Show hidden ({count})",
      hideHidden: "Hide hidden",

      stages: {
        system: "System Stage",
        user: "User Stage",
        final: "Final Stage",
      },
      hooksCount: "{count} hooks",

      selectHook: "Select a hook to view details",
      description: "Description",
      noDescription: "No description",
      stage: "Stage",
      category: "Category",
      scope: "Scope",
      sortOrder: "Sort Order",
      dragToReorder: "Drag and drop to reorder hooks",
      configInAgentConfig:
        "Hook parameters are configured in the Agent Config section under the corresponding hook settings.",

      hookDefinitions: {
        // ═══ SYSTEM Stage ═══
        user_facts: {
          displayName: "User Facts",
          description: "Load permanent user preferences, info, and beliefs (e.g., prefers concise answers, is a programmer)",
          requires: "Memory System (memory.enabled)",
          note: "Injected into System Prompt at the front for KV Cache optimization",
        },
        datetime_context: {
          displayName: "Date & Time",
          description: "Inject current date/time context so the agent knows today's date and day of week",
        },
        astrology_plus: {
          displayName: "Astrology+",
          description: "Load user's birth chart data and current transit information",
          note: "Requires user to have set birth information",
        },
        first_message: {
          displayName: "First Message",
          description: "Detect if this is the first message of a new session for welcome or special handling",
        },
        classifier: {
          displayName: "Response Classifier",
          description: "Pre-classify user intent to decide between quick response or deep response mode",
          note: "Affects subsequent model selection and processing flow",
        },
        skill_dedup: {
          displayName: "Skill Dedup",
          description: "Check if the same skill is being called repeatedly in the conversation to avoid duplicate execution",
          requires: "Depends on classifier results",
        },

        // ═══ USER Stage ═══
        emotion_enhance: {
          displayName: "Emotion Sensing",
          description: "Analyze user message emotion and intent to adjust response style",
        },
        memory_context: {
          displayName: "Memory Retrieval",
          description: "Search for relevant user events and agent interaction memories based on user message",
          requires: "Memory System (memory.enabled)",
          note: "Retrieved results are injected into System Prompt for agent reference",
        },
        knowledge_context: {
          displayName: "Knowledge Retrieval",
          description: "Retrieve relevant document snippets from knowledge base for user's question",
          requires: "Knowledge Base (knowledge_enabled)",
        },
        memory_reinforce: {
          displayName: "Memory Reinforce",
          description: "Based on Ebbinghaus forgetting curve, reinforce accessed memories to delay forgetting",
          requires: "Depends on memory_context retrieval results",
          note: "Retrieved memories gain strength; memories not accessed for long periods gradually decay",
        },

        // ═══ FINAL Stage ═══
        memory_save: {
          displayName: "Memory Save",
          description: "After conversation ends, extract and save user memories and agent interaction memories",
          requires: "Memory System (memory.enabled)",
          note: "Dual extraction mode: separately extracts user facts and agent commitments/suggestions",
        },
        skill_tracking: {
          displayName: "Skill Tracking",
          description: "Record skills executed in this conversation for statistics and deduplication",
        },
      },
    },
    imageWorkflows: {
      executionPanel: {
        title: "Execution",
        execution: "Execution",
        progress: "Progress",
        nodes: "nodes",
        failed: "failed",
        duration: "Duration",
        error: "Error",
        clearAndClose: "Clear & Close",
        nodeDetail: "Node Detail",
      },
      executionHistory: {
        title: "Execution History",
        tab: "History",
        executionLabel: "Execution",
        nodes: "nodes",
        saveFirst: "Save the workflow to view execution history",
        noHistory: "No execution history",
        runFirst: "Run the workflow to see execution history",
      },
      nodeDetail: {
        selectNode: "Click a node on the canvas to view details",
        noStepData: "No execution data for this node",
        status: "Status",
        duration: "Duration",
        order: "Step Order",
        startedAt: "Started",
        completedAt: "Completed",
      },
      stepResult: {
        error: "Error",
        variables: "Variables",
        noVariables: "No variables",
        mode: "Mode",
        generatedPrompt: "Generated Prompt",
        showLess: "Show less",
        showMore: "Show more",
        noPrompt: "No prompt generated",
        model: "Model",
        output: "Output",
        imageLoadError: "Failed to load image",
        noImage: "No image generated",
        storage: "Storage",
        saved: "Saved to Library",
        temporary: "Temporary",
        outputPending: "Output pending",
        noOutput: "No output data",
      },
      outputNode: {
        noArtifact: "No artifact (temporary file)",
      },
    },

    // Resources
    resources: {
      pageTitle: "User Resources",
      tabs: {
        artifacts: "Artifacts",
        library: "Library",
        temp: "Temp Files",
      },
      types: {
        artifact: "Artifacts",
        library: "Library",
        temp: "Temp Files",
      },
      stats: {
        totalFiles: "Total Files",
        totalSize: "Total Size",
        uniqueUsers: "Users",
        byType: "By Type",
        bySubtype: "By Subtype",
        files: "files",
        users: "users",
        subtypes: "Subtypes",
        categories: "Categories",
        avgPerUser: "Avg per User",
        largestUsers: "Top Users by Storage",
        autoCleanup: "Auto cleanup in 7 days",
      },
      summary: {
        totalFiles: "Total Files",
        totalSize: "Total Size",
      },
      modal: {
        title: "User Resources",
        summary: "Resource Summary",
        noResources: "No resources",
        loading: "Loading...",
        error: "Failed to load",
        deleteWarning: "Delete operations cannot be undone",
      },
      status: {
        expired: "Expired",
        current: "Current",
      },
      actions: {
        viewResources: "View Resources",
        delete: "Delete",
        open: "Open",
        download: "Download",
        confirmDelete: "Confirm Delete",
        cancel: "Cancel",
        browseTools: "Browse Tools",
      },
      groups: {
        uncategorized: "Uncategorized",
        expandAll: "Expand All",
        collapseAll: "Collapse All",
      },
      deleteAll: {
        type: "Delete All {type}",
        all: "Delete All Resources",
        confirmType: "Delete all {type} for this user?",
        confirmAll:
          "Delete all resources for this user? This cannot be undone.",
      },
      deleting: "Deleting...",
      table: {
        userId: "User ID",
        totalFiles: "Files",
        totalSize: "Size",
      },
      cleanup: {
        title: "Cleanup Operations",
        tempTitle: "Cleanup Temp Files",
        tempDesc: "Delete temp files older than specified days",
        tempButton: "Cleanup Temp Files",
        artifactsTitle: "Cleanup Expired Artifacts",
        artifactsDesc: "Delete expired artifact files and database records",
        artifactsButton: "Cleanup Expired",
        daysOld: "Days old",
        days: "days",
        confirm: "Confirm Cleanup",
        cleaning: "Cleaning...",
        error: "Cleanup failed",
        successTemp: "Deleted {count} temp files, freed {size}",
        successArtifacts: "Deleted {count} expired artifacts, freed {size}",
      },
      lastUpdated: "Last updated",
      errors: {
        loadFailed: "Failed to load",
      },
    },

    // Shared: User Selector
    userSelector: {
      searchPlaceholder: "Search by name or ID...",
      selectedCount: "{count} selected",
      selectAll: "Select All",
      deselectAll: "Deselect All",
      clearAll: "Clear",
      noUsers: "No users found",
      devices: "device(s)",
      showingCount: "Showing {from}-{to} of {total}",
    },

    // Shared: Workflow Target Selector
    workflowTarget: {
      currentUser: "Current User",
      currentUserDesc: "Run as the creator/executor",
      specificUsers: "Specific Users",
      specificUsersDesc: "Select specific users as targets",
      willRunAs: "Will run as",
    },
  },
};

export default messages;
